// Shared UI label translations for SEO pages and Footer (DE/EN/ES)

export interface SeoPageTranslation {
  h1: string;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
  intro: string;
  contentSections?: {
    eyebrow?: string;
    title: string;
    paragraphs?: string[];
    bullets?: string[];
    note?: string;
  }[];
  servicesLabel?: string;
  servicesTitle?: string;
  services: { title: string; desc: string }[];
  serviceAreaText: string;
  faq: { q: string; a: string }[];
  internalLinks: { label: string }[];
  breadcrumbs: { label: string }[];
  contactTitle?: string;
  contactText?: string;
}

export const seoUiLabels: Record<string, {
  home: string;
  servicesLabel: string;
  servicesTitle: string;
  serviceArea: string;
  moreTopicsLabel: string;
  moreTopicsTitle: string;
  faqLabel: string;
  faqTitle: string;
  contactLabel: string;
  contactTitle: string;
  contactText: string;
  ctaHero: string;
  whatsappBtn: string;
  backBtn: string;
  externalLabel: string;
  externalTitle: string;
  typeService: string;
  typeLocation: string;
  typeInfo: string;
  typeGuide: string;
  typeRatgeber: string;
  footerServiceHeading: string;
  footerLocationHeading: string;
  footerGuideHeading: string;
  footerRatgeberHeading: string;
}> = {
  de: {
    home: 'Startseite',
    servicesLabel: 'Unsere Leistungen',
    servicesTitle: 'Was wir für Sie tun',
    serviceArea: 'Einsatzgebiet auf Mallorca',
    moreTopicsLabel: 'Weitere Themen',
    moreTopicsTitle: 'Das könnte Sie auch interessieren',
    faqLabel: 'Häufige Fragen',
    faqTitle: 'Ihre Fragen – unsere Antworten',
    contactLabel: 'Kontakt',
    contactTitle: 'Lassen Sie uns sprechen',
    contactText: 'Wir melden uns zeitnah und diskret. Rufen Sie uns an oder schreiben Sie per WhatsApp.',
    ctaHero: 'Erstgespräch anfragen',
    whatsappBtn: 'WhatsApp schreiben',
    backBtn: 'Zurück zur Startseite',
    externalLabel: 'Referenz & Orientierung',
    externalTitle: 'Wichtige Einrichtungen & Links',
    typeService: 'Pflegeleistung',
    typeLocation: 'Standort',
    typeInfo: 'Information',
    typeGuide: 'Guide',
    typeRatgeber: 'Ratgeber',
    footerServiceHeading: 'Pflege & Gesundheit',
    footerLocationHeading: 'Standorte auf Mallorca',
    footerGuideHeading: 'Guide – Mallorca erleben',
    footerRatgeberHeading: 'Medizinischer Ratgeber',
  },
  en: {
    home: 'Home',
    servicesLabel: 'Our Services',
    servicesTitle: 'What We Do for You',
    serviceArea: 'Service Area in Mallorca',
    moreTopicsLabel: 'Related Topics',
    moreTopicsTitle: 'You Might Also Be Interested In',
    faqLabel: 'Frequently Asked Questions',
    faqTitle: 'Your Questions – Our Answers',
    contactLabel: 'Contact',
    contactTitle: 'Let\'s Talk',
    contactText: 'We will get back to you promptly and discreetly. Call us or send a WhatsApp message.',
    ctaHero: 'Request Consultation',
    whatsappBtn: 'Send WhatsApp',
    backBtn: 'Back to Homepage',
    externalLabel: 'Reference & Orientation',
    externalTitle: 'Important Facilities & Links',
    typeService: 'Care Service',
    typeLocation: 'Location',
    typeInfo: 'Information',
    typeGuide: 'Guide',
    typeRatgeber: 'Medical Guide',
    footerServiceHeading: 'Care & Health',
    footerLocationHeading: 'Locations in Mallorca',
    footerGuideHeading: 'Guide – Discover Mallorca',
    footerRatgeberHeading: 'Medical Guide',
  },
  es: {
    home: 'Inicio',
    servicesLabel: 'Nuestros servicios',
    servicesTitle: 'Lo que hacemos por usted',
    serviceArea: 'Zona de servicio en Mallorca',
    moreTopicsLabel: 'Temas relacionados',
    moreTopicsTitle: 'También podría interesarle',
    faqLabel: 'Preguntas frecuentes',
    faqTitle: 'Sus preguntas – nuestras respuestas',
    contactLabel: 'Contacto',
    contactTitle: 'Hablemos',
    contactText: 'Nos pondremos en contacto con usted de forma rápida y discreta. Llámenos o escríbanos por WhatsApp.',
    ctaHero: 'Solicitar consulta',
    whatsappBtn: 'Escribir por WhatsApp',
    backBtn: 'Volver a la página principal',
    externalLabel: 'Referencia y orientación',
    externalTitle: 'Centros y enlaces importantes',
    typeService: 'Servicio de cuidado',
    typeLocation: 'Ubicación',
    typeInfo: 'Información',
    typeGuide: 'Guía',
    typeRatgeber: 'Guía médica',
    footerServiceHeading: 'Cuidado y Salud',
    footerLocationHeading: 'Ubicaciones en Mallorca',
    footerGuideHeading: 'Guía – Descubrir Mallorca',
    footerRatgeberHeading: 'Guía médica',
  },
};

export let seoPageTranslations: Record<string, { en?: Partial<SeoPageTranslation>; es?: Partial<SeoPageTranslation> }> = {};
const seoTranslationOverrides: Record<string, { en?: Partial<SeoPageTranslation>; es?: Partial<SeoPageTranslation> }> = {};

const mergeSeoTranslation = (
  slug: string,
  language: 'en' | 'es',
  translation: Partial<SeoPageTranslation>,
) => {
  const current = seoTranslationOverrides[slug] || {};
  seoTranslationOverrides[slug] = {
    ...current,
    [language]: {
      ...(current[language] || {}),
      ...translation,
    },
  };
};

// Fully revised translations for the eleven SEO pages added or substantially
// expanded in July 2026. Existing shorter translations above remain available
// for the other legacy SEO routes.
mergeSeoTranslation('pflege-mallorca', 'en', {
  h1: 'German-speaking care throughout Mallorca',
  intro: 'Geis Care supports older people and people in need of care so they can live safely and as independently as possible in Mallorca. Founder Tutku Geis is a qualified nurse with more than 15 years of intensive-care and operating-theatre experience. We clarify personally which support is professionally appropriate and can be organised safely.',
  contentSections: [
    {
      eyebrow: 'Who it is for',
      title: 'Support when everyday life becomes more difficult',
      paragraphs: [
        'The service is intended for people who need temporary or regular support, for example after a hospital stay, when care needs increase or when relatives cannot be in Mallorca all the time.',
        'Long-stay visitors and winter residents can also request support. The decisive factor is the individual situation at home, not age or diagnosis alone.',
      ],
      bullets: [
        'Older people who wish to remain in familiar surroundings',
        'People who need help with personal care, mobility or daily structure',
        'Families in Mallorca or abroad who need reliable coordination',
      ],
    },
    {
      eyebrow: 'Process',
      title: 'From the first consultation to the start of care',
      paragraphs: [
        'During the free initial consultation of approximately 20 to 30 minutes, you explain the situation. A personal needs assessment at home can then follow. Services, schedules and communication channels are agreed on this basis.',
        'Once care begins, the agreed measures are documented. Authorised relatives can therefore follow completed tasks and upcoming appointments within the agreed framework.',
      ],
    },
    {
      eyebrow: 'Scope and safety',
      title: 'What must be clarified before care begins',
      paragraphs: [
        'Not every situation can be resolved through outpatient support at home. Medical measures require professional assessment and, where necessary, a doctor’s order. For an acute or life-threatening emergency, call 112.',
        'Geis Care also offers night care and around-the-clock models. Staffing, start date and exact scope are planned individually and recorded in the agreement.',
      ],
    },
  ],
  servicesLabel: 'Possible support',
  servicesTitle: 'Services based on a personal needs assessment',
  services: [
    { title: 'Personal care', desc: 'Respectful support with washing, showering, bathing and dressing.' },
    { title: 'Mobility and activation', desc: 'Support with safe everyday movement and suitable activities.' },
    { title: 'Medication administration and organisation', desc: 'Professional support based on the agreed medication plan.' },
    { title: 'Wound care', desc: 'Professional care within the medically clarified and agreed scope.' },
    { title: 'Medical and therapy appointments', desc: 'Organisation, accompaniment and clear communication of relevant information.' },
    { title: 'Dementia care', desc: 'Compassionate support based on many years of practical care experience.' },
    { title: 'Intensive care', desc: 'Individually planned support for increased care and monitoring needs.' },
    { title: 'Digital documentation', desc: 'Transparent documentation of agreed measures for clients and authorised relatives.' },
  ],
  serviceAreaText: 'Geis Care provides care in every region of Mallorca, including Manacor, Porto Cristo, Cala Millor, Artà, Capdepera, Santanyí, Campos, Son Vida, Port d’Andratx and Palma. Start date, scope and staffing are agreed for the individual situation.',
  faq: [
    { q: 'What does care in Mallorca cost?', a: 'Costs depend on the needs, duration, location and scope. After the free consultation, Geis Care prepares an individual quote that also states travel, changes and cancellation terms.' },
    { q: 'Is the care provided in German?', a: 'Geis Care is specifically aimed at German-speaking clients and relatives. Any additional language requirements are clarified during the enquiry.' },
    { q: 'How quickly can care begin?', a: 'This depends on the needs, location and current availability. Urgency is discussed during the consultation; a firm start date is confirmed only after assessment.' },
    { q: 'Is there an on-call service?', a: 'Yes. Geis Care offers an on-call service with individually agreed contact and response procedures. For acute or life-threatening emergencies, always call 112 immediately.' },
  ],
  internalLinks: [
    { label: 'Intensive Care Mallorca' },
    { label: 'Senior Care Mallorca' },
    { label: 'Home Care Mallorca' },
    { label: 'Dementia Care Mallorca' },
    { label: 'Support after hospital discharge' },
    { label: 'How the first consultation works' },
  ],
  contactTitle: 'What support fits your situation?',
  contactText: 'Tell us briefly who needs support, where in Mallorca they live and what help is required. Geis Care will discuss the next steps personally.',
});

mergeSeoTranslation('pflege-mallorca', 'es', {
  h1: 'Cuidados en alemán en toda Mallorca',
  intro: 'Geis Care ayuda a personas mayores y dependientes a vivir en Mallorca con seguridad y la mayor autonomía posible. La fundadora Tutku Geis es enfermera titulada y cuenta con más de 15 años de experiencia en cuidados intensivos y quirófano. Aclaramos personalmente qué apoyo es adecuado y puede organizarse de forma segura.',
  contentSections: [
    {
      eyebrow: 'Para quién',
      title: 'Apoyo cuando la vida cotidiana se vuelve más difícil',
      paragraphs: [
        'El servicio se dirige a personas que necesitan ayuda temporal o periódica, por ejemplo después de una hospitalización, cuando aumenta la necesidad de cuidados o cuando la familia no puede estar siempre en Mallorca.',
        'También pueden solicitar apoyo quienes pasan largas temporadas o el invierno en la isla. Lo decisivo es la situación concreta en casa, no solo la edad o el diagnóstico.',
      ],
      bullets: [
        'Personas mayores que desean permanecer en su entorno habitual',
        'Personas que necesitan ayuda con la higiene, la movilidad o la estructura diaria',
        'Familias en Mallorca o en el extranjero que necesitan una coordinación fiable',
      ],
    },
    {
      eyebrow: 'Proceso',
      title: 'Desde la primera consulta hasta el inicio de los cuidados',
      paragraphs: [
        'En la consulta inicial gratuita, de unos 20 a 30 minutos, usted explica la situación. Después puede realizarse una valoración personal en el domicilio. Sobre esta base se acuerdan servicios, horarios y vías de comunicación.',
        'Cuando comienza la atención, se documentan las medidas acordadas. Los familiares autorizados pueden consultar las tareas realizadas y las próximas citas dentro del marco convenido.',
      ],
    },
    {
      eyebrow: 'Alcance y seguridad',
      title: 'Qué debe aclararse antes de empezar',
      paragraphs: [
        'No todas las situaciones pueden resolverse mediante atención ambulatoria en casa. Las medidas médicas requieren una valoración profesional y, cuando corresponda, indicación médica. En una urgencia grave o con peligro vital, llame al 112.',
        'Geis Care también ofrece atención nocturna y modelos de asistencia las 24 horas. El personal, la fecha de inicio y el alcance exacto se planifican individualmente y se recogen en el acuerdo.',
      ],
    },
  ],
  servicesLabel: 'Posibles apoyos',
  servicesTitle: 'Servicios según una valoración personal',
  services: [
    { title: 'Higiene personal', desc: 'Apoyo respetuoso para lavarse, ducharse, bañarse y vestirse.' },
    { title: 'Movilidad y activación', desc: 'Ayuda para moverse con seguridad y realizar actividades adecuadas.' },
    { title: 'Administración y organización de medicamentos', desc: 'Apoyo profesional según el plan de medicación acordado.' },
    { title: 'Cuidado de heridas', desc: 'Atención profesional dentro del marco médico aclarado y acordado.' },
    { title: 'Acompañamiento médico y terapéutico', desc: 'Organización, acompañamiento y transmisión clara de información relevante.' },
    { title: 'Atención a personas con demencia', desc: 'Acompañamiento cercano basado en muchos años de experiencia práctica.' },
    { title: 'Cuidados intensivos', desc: 'Apoyo individual para necesidades elevadas de cuidado y supervisión.' },
    { title: 'Documentación digital', desc: 'Registro transparente de las medidas acordadas para clientes y familiares autorizados.' },
  ],
  serviceAreaText: 'Geis Care ofrece cuidados en todas las regiones de Mallorca, entre ellas Manacor, Porto Cristo, Cala Millor, Artà, Capdepera, Santanyí, Campos, Son Vida, Port d’Andratx y Palma. El inicio, el alcance y el personal se acuerdan para cada situación.',
  faq: [
    { q: '¿Cuánto cuestan los cuidados en Mallorca?', a: 'El precio depende de las necesidades, la duración, el lugar y el alcance. Tras la consulta gratuita, Geis Care prepara un presupuesto individual que también regula desplazamientos, cambios y cancelaciones.' },
    { q: '¿La atención se presta en alemán?', a: 'Geis Care se dirige especialmente a clientes y familiares germanoparlantes. Cualquier necesidad lingüística adicional se aclara durante la solicitud.' },
    { q: '¿Cuándo puede comenzar la atención?', a: 'Depende de las necesidades, el lugar y la disponibilidad. La urgencia se trata en la primera consulta y la fecha definitiva se confirma tras la valoración.' },
    { q: '¿Existe un servicio de guardia?', a: 'Sí. Geis Care ofrece un servicio de guardia con vías de contacto y respuesta acordadas individualmente. En una urgencia grave o con peligro vital, llame siempre al 112.' },
  ],
  internalLinks: [
    { label: 'Cuidados intensivos Mallorca' },
    { label: 'Atención a mayores Mallorca' },
    { label: 'Cuidados a domicilio Mallorca' },
    { label: 'Atención a la demencia Mallorca' },
    { label: 'Apoyo después del hospital' },
    { label: 'Cómo funciona la primera consulta' },
  ],
  contactTitle: '¿Qué apoyo se adapta a su situación?',
  contactText: 'Indíquenos brevemente quién necesita ayuda, en qué lugar de Mallorca y para qué tareas. Geis Care hablará personalmente con usted sobre los próximos pasos.',
});

mergeSeoTranslation('intensivpflege-mallorca', 'en', {
  h1: 'Planning intensive care in Mallorca with care',
  intro: 'People with increased care or monitoring needs require a particularly careful assessment. Tutku Geis is a qualified nurse with more than 15 years of intensive-care and operating-theatre experience. Before any commitment, we assess what can be provided safely at home, in coordination with doctors and with suitable staffing.',
  contentSections: [
    {
      eyebrow: 'Who it is for',
      title: 'When ordinary everyday assistance is not enough',
      paragraphs: [
        'An enquiry is appropriate after surgery, a longer hospital stay or when complex care needs require closer observation and professional support.',
        'Intensive care covers very different situations. Planning therefore starts with current findings, medical instructions, the medication plan, available equipment and the home environment rather than a standard package.',
      ],
    },
    {
      eyebrow: 'Preparation',
      title: 'Information that supports the assessment',
      bullets: ['Current medical or discharge report', 'Medication plan and known risks', 'Medically prescribed nursing measures', 'Available equipment and the home environment', 'Requested times and reachable relatives or professionals'],
      note: 'Please send sensitive health information only through a secure channel agreed with Geis Care.',
    },
    {
      eyebrow: 'Safety',
      title: 'Clear boundaries protect patients',
      paragraphs: [
        'Geis Care does not replace an intensive care unit or an ambulance service. Ventilation, permanent monitoring or other highly complex requirements cannot be accepted solely on the basis of a website enquiry.',
        'Which measures can be provided depends on professional competence, medical instructions, equipment and staffing. In an acute deterioration or life-threatening emergency, call 112 immediately.',
      ],
    },
  ],
  servicesLabel: 'Possible components',
  servicesTitle: 'Coordinate care individually and professionally',
  services: [
    { title: 'Assess care needs', desc: 'Review available documents, medical instructions and the everyday situation.' },
    { title: 'Post-operative support', desc: 'Aftercare within the professionally agreed scope following hospital discharge.' },
    { title: 'Observation and documentation', desc: 'Document agreed observations and communicate relevant changes.' },
    { title: 'Nursing measures', desc: 'Provided only when professionally accepted and, where required, medically prescribed.' },
    { title: 'Medical coordination', desc: 'Coordination with treating practices, hospitals and other professionals.' },
    { title: 'Involve relatives', desc: 'Agree responsibilities, communication channels and reachable contacts.' },
  ],
  serviceAreaText: 'Geis Care offers intensive care throughout Mallorca. Location, travel, medical requirements and safe staffing are planned individually before care begins.',
  faq: [
    { q: 'What intensive-care experience does Geis Care have?', a: 'Founder Tutku Geis is a qualified nurse with more than 15 years of intensive-care and operating-theatre experience.' },
    { q: 'Is around-the-clock care available?', a: 'Yes. Geis Care offers around-the-clock models. Scope, professional requirements and staffing are planned individually and confirmed before the start.' },
    { q: 'Can Geis Care help after a hospital stay?', a: 'Post-operative support and aftercare can be requested. The exact measures are agreed on the basis of discharge documents and medical instructions.' },
    { q: 'Does Geis Care offer an on-call service?', a: 'Yes. An on-call service with clear contact procedures can be arranged for agreed care. For an acute or life-threatening emergency, call 112 immediately.' },
  ],
  internalLinks: [
    { label: 'Care Mallorca' }, { label: 'Home Care Mallorca' }, { label: 'Support after hospital discharge' },
    { label: 'Hospital admission in Mallorca' }, { label: 'Doctors in Mallorca' }, { label: 'Clinics in Mallorca' },
  ],
  contactTitle: 'Request a confidential preliminary assessment',
  contactText: 'Describe the care situation initially without unnecessary sensitive details. Geis Care will then explain which documents are needed for a professional assessment.',
});

mergeSeoTranslation('intensivpflege-mallorca', 'es', {
  h1: 'Planificar cuidadosamente los cuidados intensivos en Mallorca',
  intro: 'Las personas con mayores necesidades de cuidado o supervisión requieren una valoración especialmente precisa. Tutku Geis es enfermera titulada y cuenta con más de 15 años de experiencia en cuidados intensivos y quirófano. Antes de aceptar un caso se comprueba qué atención puede prestarse con seguridad en casa, coordinada con los médicos y con el personal adecuado.',
  contentSections: [
    {
      eyebrow: 'Para quién',
      title: 'Cuando la ayuda cotidiana no es suficiente',
      paragraphs: [
        'La solicitud es adecuada después de una operación, una hospitalización prolongada o cuando una situación compleja requiere observación más estrecha y apoyo profesional.',
        'Los cuidados intensivos abarcan situaciones muy distintas. Por ello, la planificación parte de informes actuales, indicaciones médicas, plan de medicación, equipos disponibles y condiciones del domicilio.',
      ],
    },
    {
      eyebrow: 'Preparación',
      title: 'Información útil para la valoración',
      bullets: ['Informe médico o de alta actual', 'Plan de medicación y riesgos conocidos', 'Medidas de enfermería prescritas', 'Equipos disponibles y condiciones del domicilio', 'Horarios deseados y familiares o profesionales localizables'],
      note: 'Envíe información médica sensible únicamente por una vía segura acordada con Geis Care.',
    },
    {
      eyebrow: 'Seguridad',
      title: 'Los límites claros protegen al paciente',
      paragraphs: [
        'Geis Care no sustituye una unidad de cuidados intensivos ni un servicio de ambulancias. Ventilación, monitorización permanente u otros requisitos muy complejos no pueden aceptarse solo mediante una solicitud web.',
        'Las medidas posibles dependen de la competencia profesional, las indicaciones médicas, el equipo y el personal disponible. Ante un empeoramiento agudo o una urgencia vital, llame inmediatamente al 112.',
      ],
    },
  ],
  servicesLabel: 'Posibles componentes',
  servicesTitle: 'Coordinar la atención de forma individual y profesional',
  services: [
    { title: 'Valorar las necesidades', desc: 'Revisar documentos, indicaciones médicas y situación cotidiana.' },
    { title: 'Apoyo posoperatorio', desc: 'Cuidados posteriores dentro del marco profesional acordado.' },
    { title: 'Observación y documentación', desc: 'Registrar las observaciones acordadas y comunicar cambios relevantes.' },
    { title: 'Medidas de enfermería', desc: 'Solo cuando puedan asumirse profesionalmente y, si corresponde, estén prescritas.' },
    { title: 'Coordinación médica', desc: 'Coordinación con consultas, hospitales y otros profesionales.' },
    { title: 'Participación de la familia', desc: 'Acordar responsabilidades, comunicación y contactos disponibles.' },
  ],
  serviceAreaText: 'Geis Care ofrece cuidados intensivos en toda Mallorca. El lugar, el desplazamiento, los requisitos médicos y el personal seguro se planifican individualmente antes de comenzar.',
  faq: [
    { q: '¿Qué experiencia tiene Geis Care en cuidados intensivos?', a: 'La fundadora Tutku Geis es enfermera titulada y cuenta con más de 15 años de experiencia en cuidados intensivos y quirófano.' },
    { q: '¿Es posible la atención las 24 horas?', a: 'Sí. Geis Care ofrece modelos de atención continua. El alcance, los requisitos profesionales y el personal se planifican y confirman antes del inicio.' },
    { q: '¿Puede ayudar Geis Care después de una hospitalización?', a: 'Puede solicitarse apoyo posoperatorio y seguimiento. Las medidas concretas se acuerdan según el informe de alta y las indicaciones médicas.' },
    { q: '¿Ofrece Geis Care un servicio de guardia?', a: 'Sí. Para la atención acordada puede establecerse un servicio de guardia con vías de contacto claras. En una urgencia grave o vital, llame al 112.' },
  ],
  internalLinks: [
    { label: 'Cuidados Mallorca' }, { label: 'Cuidados a domicilio Mallorca' }, { label: 'Apoyo después del hospital' },
    { label: 'Ingreso hospitalario en Mallorca' }, { label: 'Médicos en Mallorca' }, { label: 'Clínicas en Mallorca' },
  ],
  contactTitle: 'Solicitar una valoración preliminar confidencial',
  contactText: 'Describa inicialmente la situación sin datos sensibles innecesarios. Geis Care le indicará después qué documentos necesita para una valoración profesional.',
});

mergeSeoTranslation('seniorenbetreuung-mallorca', 'en', {
  h1: 'German-speaking senior care in Mallorca',
  intro: 'Geis Care supports older people who wish to continue living as independently as possible in Mallorca. Support can take place at home, during errands or at medical appointments. Relatives on the island or abroad can be included in the coordination if requested.',
  contentSections: [
    {
      eyebrow: 'Suitable for',
      title: 'Everyday support before everything becomes too much',
      paragraphs: [
        'Senior care is useful before comprehensive nursing is needed. Accompaniment, fixed appointments and a reliable contact can already help structure everyday life.',
        'The service can also relieve families who organise care from abroad. Digital documentation can keep agreed measures and appointments transparent.',
      ],
      bullets: ['Help with daily routines and selected household tasks', 'Accompaniment for shopping, walks or medical appointments', 'Company and agreed activities', 'Coordination with relatives in Mallorca or abroad'],
    },
    {
      eyebrow: 'Personal planning',
      title: 'How suitable support is arranged',
      paragraphs: [
        'The free consultation covers wishes, limitations, location and timing. A subsequent assessment at home considers routines, risks and responsibilities in more detail.',
        'Only then is the exact scope agreed. Support should provide as much help as necessary while respecting existing independence.',
      ],
    },
    {
      eyebrow: 'Clear distinction',
      title: 'Differentiate support, nursing and emergency care',
      paragraphs: [
        'Everyday support does not replace medical diagnosis, therapy or emergency care. Nursing measures must be assessed separately and may require a doctor’s order.',
        'Call 112 in an acute emergency. Geis Care offers night, long-term and around-the-clock models; exact availability and staffing are planned individually.',
      ],
    },
  ],
  servicesLabel: 'Everyday life',
  servicesTitle: 'How Geis Care can help',
  services: [
    { title: 'Everyday companionship', desc: 'Support with agreed routines and tasks at home.' },
    { title: 'Medical appointments', desc: 'Planning and accompaniment, including help with communication.' },
    { title: 'Medication organisation', desc: 'Help to keep the medically agreed medication plan organised.' },
    { title: 'Shopping assistance', desc: 'Shared errands and practical support away from home.' },
    { title: 'Company', desc: 'Time for conversation, shared activities and social contact.' },
    { title: 'Walks', desc: 'Accompanied outdoor movement adapted to the individual situation.' },
  ],
  serviceAreaText: 'Geis Care offers senior care throughout Mallorca, including Manacor, Porto Cristo, Cala Millor, Artà, Capdepera, Santanyí, Campos, Son Vida, Port d’Andratx and Palma. Schedules and scope are agreed personally.',
  faq: [
    { q: 'At what age is senior care useful?', a: 'The specific support needed matters more than age. Individual tasks, accompaniment or regular social contact can all be reasons to enquire.' },
    { q: 'Can care be arranged by the hour or by the day?', a: 'Yes. Hourly and daily models are possible. The requested schedule is confirmed during the consultation.' },
    { q: 'Are night and around-the-clock models available?', a: 'Yes. Geis Care offers night care and around-the-clock models. Scope and staffing are agreed for the individual need.' },
    { q: 'Can relatives abroad organise the care?', a: 'Yes. Responsibilities, updates and documentation are agreed with the person receiving care and authorised relatives.' },
    { q: 'Are medical services also available?', a: 'Medical nursing measures are separate from general senior support. Professional feasibility and any required medical order are assessed individually.' },
  ],
  internalLinks: [
    { label: 'Care Mallorca' }, { label: 'Home Care Mallorca' }, { label: 'Dementia Care Mallorca' },
    { label: 'Cost of senior care' }, { label: 'Organising care from abroad' }, { label: 'Holiday and respite care' },
  ],
  contactTitle: 'Discuss your support needs without obligation',
  contactText: 'Tell Geis Care where support is needed and which tasks are involved. Together, you can clarify the most suitable next step.',
});

mergeSeoTranslation('seniorenbetreuung-mallorca', 'es', {
  h1: 'Atención a mayores en alemán en Mallorca',
  intro: 'Geis Care acompaña a personas mayores que desean seguir viviendo en Mallorca con la mayor autonomía posible. El apoyo puede prestarse en casa, durante recados o en citas médicas. Si se desea, los familiares en la isla o en el extranjero participan en la coordinación.',
  contentSections: [
    {
      eyebrow: 'Adecuado para',
      title: 'Apoyo cotidiano antes de que todo resulte demasiado',
      paragraphs: [
        'La atención a mayores puede ser útil antes de necesitar cuidados amplios. El acompañamiento, las citas fijas y un contacto fiable ya ayudan a estructurar el día a día.',
        'El servicio también puede aliviar a familias que organizan la atención desde el extranjero. La documentación digital permite mantener transparentes las medidas y citas acordadas.',
      ],
      bullets: ['Ayuda con rutinas diarias y determinadas tareas domésticas', 'Acompañamiento a compras, paseos o citas médicas', 'Compañía y actividades acordadas', 'Coordinación con familiares en Mallorca o en el extranjero'],
    },
    {
      eyebrow: 'Planificación personal',
      title: 'Cómo se organiza un apoyo adecuado',
      paragraphs: [
        'La consulta gratuita trata deseos, limitaciones, lugar y horarios. Una valoración posterior en casa revisa con más detalle las rutinas, riesgos y responsabilidades.',
        'Solo entonces se acuerda el alcance concreto. El apoyo debe ofrecer toda la ayuda necesaria y respetar al mismo tiempo la autonomía existente.',
      ],
    },
    {
      eyebrow: 'Diferenciación',
      title: 'Distinguir acompañamiento, enfermería y urgencias',
      paragraphs: [
        'El apoyo cotidiano no sustituye el diagnóstico, el tratamiento ni la atención de urgencias. Las medidas de enfermería deben valorarse por separado y pueden requerir prescripción médica.',
        'Ante una urgencia aguda llame al 112. Geis Care ofrece modelos nocturnos, de larga duración y de 24 horas; la disponibilidad y el personal se planifican individualmente.',
      ],
    },
  ],
  servicesLabel: 'Vida cotidiana',
  servicesTitle: 'Cómo puede ayudar Geis Care',
  services: [
    { title: 'Acompañamiento diario', desc: 'Apoyo con rutinas y tareas acordadas en casa.' },
    { title: 'Citas médicas', desc: 'Planificación y acompañamiento, incluida ayuda con la comunicación.' },
    { title: 'Organización de medicamentos', desc: 'Ayuda para mantener organizado el plan de medicación indicado.' },
    { title: 'Acompañamiento a compras', desc: 'Recados conjuntos y apoyo práctico fuera de casa.' },
    { title: 'Compañía', desc: 'Tiempo para conversar, realizar actividades y mantener contactos sociales.' },
    { title: 'Paseos', desc: 'Movimiento acompañado al aire libre adaptado a la situación personal.' },
  ],
  serviceAreaText: 'Geis Care ofrece atención a mayores en toda Mallorca, entre otras zonas en Manacor, Porto Cristo, Cala Millor, Artà, Capdepera, Santanyí, Campos, Son Vida, Port d’Andratx y Palma. Los horarios y el alcance se acuerdan personalmente.',
  faq: [
    { q: '¿A partir de qué edad es útil la atención a mayores?', a: 'Lo decisivo es la necesidad concreta, no la edad. También pueden solicitarse tareas individuales, acompañamiento o contacto social periódico.' },
    { q: '¿Puede contratarse por horas o por días?', a: 'Sí. Son posibles modelos por horas y por días. El horario solicitado se confirma en la consulta.' },
    { q: '¿Hay atención nocturna y modelos de 24 horas?', a: 'Sí. Geis Care ofrece atención nocturna y modelos de 24 horas. El alcance y el personal se acuerdan según la necesidad.' },
    { q: '¿Pueden organizar la atención los familiares desde el extranjero?', a: 'Sí. Las responsabilidades, actualizaciones y documentación se acuerdan con la persona atendida y los familiares autorizados.' },
    { q: '¿También se prestan servicios médicos?', a: 'Las medidas de enfermería médica se diferencian del acompañamiento general. La viabilidad profesional y cualquier prescripción necesaria se valoran individualmente.' },
  ],
  internalLinks: [
    { label: 'Cuidados Mallorca' }, { label: 'Cuidados a domicilio Mallorca' }, { label: 'Atención a la demencia Mallorca' },
    { label: 'Costes de la atención a mayores' }, { label: 'Organizar la atención desde el extranjero' }, { label: 'Atención vacacional y de respiro' },
  ],
  contactTitle: 'Hablar de sus necesidades sin compromiso',
  contactText: 'Indique a Geis Care dónde se necesita apoyo y para qué tareas. Juntos podrán aclarar el siguiente paso más adecuado.',
});

mergeSeoTranslation('hauspflege-mallorca', 'en', {
  h1: 'Home care in familiar surroundings in Mallorca',
  intro: 'Home care combines practical help, personal companionship and, where professionally agreed, nursing support in the client’s own home. Geis Care plans the services together with the person receiving care and the relatives involved. The aim is everyday life with as much independence and safety as possible.',
  contentSections: [
    {
      eyebrow: 'At home',
      title: 'When home care can provide relief',
      paragraphs: [
        'Support at home may be needed temporarily after an illness or may develop into regular assistance. It is particularly suitable when familiar routines should be maintained and help is needed with specific activities.',
        'Before care begins, the home environment, daily rhythm, mobility, available aids and the responsibilities of relatives or other professionals are discussed.',
      ],
    },
    {
      eyebrow: 'Working together',
      title: 'A clear plan for everyone involved',
      bullets: ['Agree tasks, visiting times and contacts in writing', 'Keep medical instructions and medication plans current', 'Document changes and agreed measures', 'Review regularly whether the scope still fits the situation'],
      note: 'Relatives receive information only within the agreed and legally permitted data-protection framework.',
    },
    {
      eyebrow: 'Boundaries',
      title: 'When additional professionals are needed',
      paragraphs: [
        'Home care does not replace medical treatment or an ambulance service. Medical measures require professional assessment and, where necessary, a doctor’s order.',
        'The home itself can also impose limits. Additional aids, therapy, adaptations or another care model may be required. Geis Care can discuss the situation but cannot guarantee that every home is suitable.',
      ],
    },
  ],
  servicesLabel: 'At home',
  servicesTitle: 'Possible components of home care',
  services: [
    { title: 'Support at home', desc: 'Agreed help where everyday life takes place.' },
    { title: 'Personal care', desc: 'Respectful help with hygiene, clothing and everyday mobility.' },
    { title: 'Nursing measures', desc: 'Professionally assessed measures within the agreed and, where required, medically prescribed scope.' },
    { title: 'Everyday life and household', desc: 'Support with selected daily tasks by prior agreement.' },
    { title: 'Appointments and errands', desc: 'Accompaniment to doctors, therapy or necessary errands.' },
    { title: 'Digital documentation', desc: 'Transparent information about agreed measures and appointments.' },
  ],
  serviceAreaText: 'Geis Care offers home care throughout Mallorca. Travel, visiting times, scope and staffing are agreed individually before care begins.',
  faq: [
    { q: 'How often can Geis Care visit the home?', a: 'Frequency and duration are agreed according to the need and availability. A specific schedule is confirmed only after assessment.' },
    { q: 'Can the support be adjusted later?', a: 'Yes. Needs should be reviewed regularly. Changes are possible but must be agreed again and depend on availability.' },
    { q: 'What counts as medical nursing at home?', a: 'This depends on the measure, professional competence and possible medical instructions. Medical services are assessed individually.' },
    { q: 'Is home care the same as household help?', a: 'No. Home care can include nursing and companionship; household help concerns practical household tasks. The exact tasks are expressly agreed.' },
  ],
  internalLinks: [
    { label: 'Care Mallorca' }, { label: 'Senior Care Mallorca' }, { label: 'Intensive Care Mallorca' },
    { label: 'Care at home or a care home?' }, { label: 'Support after hospital discharge' }, { label: 'How the first consultation works' },
  ],
  contactTitle: 'Plan suitable home care',
  contactText: 'Describe the location, everyday situation and support required. A personal consultation will clarify which help at home is realistic and appropriate.',
});

mergeSeoTranslation('hauspflege-mallorca', 'es', {
  h1: 'Cuidados a domicilio en un entorno familiar en Mallorca',
  intro: 'Los cuidados a domicilio combinan ayuda práctica, acompañamiento personal y, cuando se acuerda profesionalmente, apoyo de enfermería en la propia vivienda. Geis Care planifica los servicios con la persona atendida y los familiares implicados. El objetivo es mantener la mayor autonomía y seguridad posibles.',
  contentSections: [
    {
      eyebrow: 'En casa',
      title: 'Cuándo puede aliviar la atención domiciliaria',
      paragraphs: [
        'El apoyo en casa puede ser temporal después de una enfermedad o convertirse en una ayuda periódica. Es especialmente adecuado cuando se desean conservar rutinas conocidas y se necesita ayuda con actividades concretas.',
        'Antes de empezar se revisan el domicilio, el ritmo diario, la movilidad, las ayudas disponibles y las responsabilidades de familiares u otros profesionales.',
      ],
    },
    {
      eyebrow: 'Colaboración',
      title: 'Un plan claro para todas las personas implicadas',
      bullets: ['Acordar por escrito tareas, horarios y contactos', 'Mantener actualizadas las indicaciones médicas y el plan de medicación', 'Documentar cambios y medidas acordadas', 'Revisar periódicamente si el alcance sigue siendo adecuado'],
      note: 'Los familiares reciben información únicamente dentro del marco acordado y permitido por la protección de datos.',
    },
    {
      eyebrow: 'Límites',
      title: 'Cuándo se necesitan otros profesionales',
      paragraphs: [
        'La atención domiciliaria no sustituye un tratamiento médico ni una ambulancia. Las medidas médicas requieren valoración profesional y, cuando corresponda, indicación médica.',
        'La propia vivienda también puede imponer límites. Pueden ser necesarias ayudas, terapias, adaptaciones u otro modelo asistencial. Geis Care puede valorar la situación, pero no garantizar que cualquier domicilio sea adecuado.',
      ],
    },
  ],
  servicesLabel: 'En casa',
  servicesTitle: 'Posibles componentes de la atención domiciliaria',
  services: [
    { title: 'Apoyo en el domicilio', desc: 'Ayuda acordada allí donde transcurre la vida cotidiana.' },
    { title: 'Higiene personal', desc: 'Ayuda respetuosa con la higiene, la ropa y la movilidad diaria.' },
    { title: 'Medidas de enfermería', desc: 'Medidas valoradas profesionalmente y, si corresponde, prescritas por un médico.' },
    { title: 'Vida diaria y hogar', desc: 'Apoyo en tareas seleccionadas según acuerdo previo.' },
    { title: 'Citas y recados', desc: 'Acompañamiento a médicos, terapias o recados necesarios.' },
    { title: 'Documentación digital', desc: 'Información transparente sobre medidas y citas acordadas.' },
  ],
  serviceAreaText: 'Geis Care ofrece atención domiciliaria en toda Mallorca. El desplazamiento, los horarios, el alcance y el personal se acuerdan individualmente antes de comenzar.',
  faq: [
    { q: '¿Con qué frecuencia puede acudir Geis Care al domicilio?', a: 'La frecuencia y duración se acuerdan según la necesidad y la disponibilidad. El horario concreto se confirma tras la valoración.' },
    { q: '¿Puede adaptarse el apoyo más adelante?', a: 'Sí. Las necesidades deben revisarse periódicamente. Los cambios son posibles, pero deben volver a acordarse y dependen de la disponibilidad.' },
    { q: '¿Qué se considera enfermería médica en casa?', a: 'Depende de la medida, la competencia profesional y las posibles indicaciones médicas. Los servicios médicos se valoran individualmente.' },
    { q: '¿La atención domiciliaria es lo mismo que la ayuda doméstica?', a: 'No. Puede incluir enfermería y acompañamiento; la ayuda doméstica se refiere a tareas prácticas. Las tareas exactas se acuerdan expresamente.' },
  ],
  internalLinks: [
    { label: 'Cuidados Mallorca' }, { label: 'Atención a mayores Mallorca' }, { label: 'Cuidados intensivos Mallorca' },
    { label: '¿Cuidados en casa o residencia?' }, { label: 'Apoyo después del hospital' }, { label: 'Cómo funciona la primera consulta' },
  ],
  contactTitle: 'Planificar una atención domiciliaria adecuada',
  contactText: 'Describa el lugar, la situación cotidiana y el apoyo que necesita. En una consulta personal se aclarará qué ayuda en casa es realista y adecuada.',
});

mergeSeoTranslation('demenzbetreuung-mallorca', 'en', {
  h1: 'Compassionate dementia care in Mallorca',
  intro: 'People living with dementia need reliability, respectful communication and a daily routine adapted to their abilities. Geis Care offers German-speaking support and includes relatives in the planning. Support is based on the person, not only on a diagnosis.',
  contentSections: [
    {
      eyebrow: 'Person-centred',
      title: 'Familiar routines provide orientation',
      paragraphs: [
        'Familiar routines, known places and calm communication can provide valuable orientation. Biography, preferences, daily rhythm and possible triggers for anxiety are therefore considered in the plan.',
        'Support can include conversation, walks, simple everyday tasks and other familiar activities. It is adapted to the person’s current capacity and is not presented as therapy.',
      ],
    },
    {
      eyebrow: 'For relatives',
      title: 'Relief with clear coordination',
      paragraphs: [
        'Family carers need reliable breaks and information. Contacts, updates, authorisations and procedures for changes or crises are discussed before care begins.',
        'Digital documentation can make agreed measures and observations transparent. Health information is shared only with authorised people and within the agreed framework.',
      ],
      bullets: ['Record important routines and preferences', 'Keep the current medication plan available', 'Define emergency contacts and the responsible medical practice', 'Review the home environment and visible risks together'],
    },
    {
      eyebrow: 'Boundaries',
      title: 'When medical help or another care model is needed',
      paragraphs: [
        'Dementia care does not replace diagnosis or medical treatment. Sudden severe confusion, new neurological symptoms or an acute deterioration require medical assessment; call 112 in an emergency.',
        'If there is pronounced wandering behaviour, night-time support or a high nursing need, the safety of the home situation and any additional professional services or aids must be assessed.',
      ],
    },
  ],
  servicesLabel: 'Everyday life',
  servicesTitle: 'Possible elements of dementia care',
  services: [
    { title: 'Everyday orientation', desc: 'Familiar routines and simple prompts adapted to the individual situation.' },
    { title: 'Review the home environment', desc: 'Discuss visible everyday risks and clarify responsibilities.' },
    { title: 'Familiar activities', desc: 'Biographical activities, music or creative tasks without a therapeutic claim.' },
    { title: 'Calm companionship', desc: 'Patient, respectful communication and time for the individual.' },
    { title: 'Relief for relatives', desc: 'Planned support times and agreed updates for the family.' },
    { title: 'Coordinate cooperation', desc: 'Coordination with relatives, doctors and other professionals.' },
  ],
  serviceAreaText: 'Geis Care offers dementia care throughout Mallorca. Location, scope, required continuity and staffing are agreed for the individual situation.',
  faq: [
    { q: 'Is a medical diagnosis required before making an enquiry?', a: 'An enquiry for everyday support is possible even when changes have only recently been noticed. Diagnosis and medical assessment remain the responsibility of doctors.' },
    { q: 'What experience does Geis Care have in dementia care?', a: 'Support is based on many years of practical nursing and care experience. A separate certified dementia qualification is not stated.' },
    { q: 'How are restlessness and challenging behaviour handled?', a: 'Geis Care places great emphasis on respectful, compassionate support. Triggers, familiar calming strategies and boundaries are discussed in advance.' },
    { q: 'Are night and around-the-clock care available?', a: 'Yes. Geis Care offers night care and around-the-clock models. Suitability and staffing are planned for the individual situation.' },
    { q: 'How are relatives kept informed?', a: 'Communication channels and permissions are agreed together. Digital documentation can make agreed measures and observations transparent.' },
  ],
  internalLinks: [
    { label: 'Care Mallorca' }, { label: 'Senior Care Mallorca' }, { label: 'Home Care Mallorca' },
    { label: 'Organising care from abroad' }, { label: 'Holiday and respite care' }, { label: 'Doctors in Mallorca' },
  ],
  contactTitle: 'Discuss dementia care personally',
  contactText: 'Tell us what changes you have noticed, which routines matter and where in Mallorca support is needed. Geis Care will discuss the next steps individually.',
});

mergeSeoTranslation('demenzbetreuung-mallorca', 'es', {
  h1: 'Atención cercana a la demencia en Mallorca',
  intro: 'Las personas con demencia necesitan fiabilidad, comunicación respetuosa y una rutina adaptada a sus capacidades. Geis Care ofrece acompañamiento en alemán e incluye a la familia en la planificación. El apoyo se orienta a la persona, no solo al diagnóstico.',
  contentSections: [
    {
      eyebrow: 'Centrado en la persona',
      title: 'Las rutinas conocidas aportan orientación',
      paragraphs: [
        'Las rutinas familiares, los lugares conocidos y una comunicación tranquila pueden aportar orientación. Por ello, se tienen en cuenta la biografía, las preferencias, el ritmo diario y los posibles desencadenantes de inquietud.',
        'El acompañamiento puede incluir conversaciones, paseos, tareas sencillas y otras actividades conocidas. Se adapta a la capacidad actual y no se presenta como terapia.',
      ],
    },
    {
      eyebrow: 'Para la familia',
      title: 'Alivio con una coordinación clara',
      paragraphs: [
        'Los familiares cuidadores necesitan descansos fiables e información. Antes de empezar se acuerdan contactos, actualizaciones, autorizaciones y el procedimiento ante cambios o crisis.',
        'La documentación digital puede hacer transparentes las medidas y observaciones acordadas. Los datos de salud solo se comparten con personas autorizadas y dentro del marco convenido.',
      ],
      bullets: ['Anotar rutinas y preferencias importantes', 'Tener disponible el plan de medicación actual', 'Definir contactos de urgencia y consulta médica responsable', 'Revisar juntos el domicilio y los riesgos visibles'],
    },
    {
      eyebrow: 'Límites',
      title: 'Cuándo se necesita ayuda médica u otro modelo asistencial',
      paragraphs: [
        'La atención a la demencia no sustituye el diagnóstico ni el tratamiento médico. Una confusión intensa repentina, síntomas neurológicos nuevos o un empeoramiento agudo requieren valoración médica; en una urgencia, llame al 112.',
        'Ante deambulación marcada, necesidad nocturna o cuidados elevados, debe valorarse la seguridad del domicilio y la posible participación de otros profesionales o ayudas.',
      ],
    },
  ],
  servicesLabel: 'Vida cotidiana',
  servicesTitle: 'Posibles elementos de la atención a la demencia',
  services: [
    { title: 'Orientación diaria', desc: 'Rutinas conocidas e indicaciones sencillas adaptadas a la situación.' },
    { title: 'Revisar el domicilio', desc: 'Comentar riesgos cotidianos visibles y aclarar responsabilidades.' },
    { title: 'Actividades conocidas', desc: 'Actividades biográficas, música o creatividad sin promesa terapéutica.' },
    { title: 'Acompañamiento tranquilo', desc: 'Comunicación paciente y respetuosa, con tiempo para la persona.' },
    { title: 'Alivio para la familia', desc: 'Horarios planificados y actualizaciones acordadas.' },
    { title: 'Coordinar la colaboración', desc: 'Coordinación con familiares, médicos y otros profesionales.' },
  ],
  serviceAreaText: 'Geis Care ofrece atención a la demencia en toda Mallorca. El lugar, el alcance, la continuidad necesaria y el personal se acuerdan para cada situación.',
  faq: [
    { q: '¿Se necesita un diagnóstico médico para solicitar información?', a: 'Puede solicitarse acompañamiento aunque los cambios se hayan observado recientemente. El diagnóstico y la valoración médica corresponden a los médicos.' },
    { q: '¿Qué experiencia tiene Geis Care en demencia?', a: 'El acompañamiento se basa en muchos años de experiencia práctica en enfermería y cuidados. No se indica una certificación adicional específica en demencia.' },
    { q: '¿Cómo se afrontan la inquietud o conductas difíciles?', a: 'Geis Care da gran importancia a un acompañamiento respetuoso y cercano. Los desencadenantes, estrategias conocidas y límites se comentan previamente.' },
    { q: '¿Hay atención nocturna y de 24 horas?', a: 'Sí. Geis Care ofrece atención nocturna y modelos de 24 horas. La adecuación y el personal se planifican para cada situación.' },
    { q: '¿Cómo se informa a la familia?', a: 'Las vías de comunicación y autorizaciones se acuerdan conjuntamente. La documentación digital puede mostrar medidas y observaciones acordadas.' },
  ],
  internalLinks: [
    { label: 'Cuidados Mallorca' }, { label: 'Atención a mayores Mallorca' }, { label: 'Cuidados a domicilio Mallorca' },
    { label: 'Organizar la atención desde el extranjero' }, { label: 'Atención vacacional y de respiro' }, { label: 'Médicos en Mallorca' },
  ],
  contactTitle: 'Hablar personalmente sobre la atención a la demencia',
  contactText: 'Cuéntenos qué cambios observa, qué rutinas son importantes y dónde se necesita apoyo. Geis Care aclarará los próximos pasos individualmente.',
});

mergeSeoTranslation('kosten-seniorenbetreuung-mallorca', 'en', {
  h1: 'What does senior care cost in Mallorca?',
  metaTitle: 'Cost of senior care in Mallorca | Geis Care',
  metaDescription: 'Understand the factors that determine the cost of senior care in Mallorca and prepare a transparent individual quote.',
  metaKeywords: 'senior care costs Mallorca, care prices Mallorca, home care cost Mallorca',
  intro: 'Reliable cost planning starts with the support actually required rather than a standard hourly rate. Duration, location, tasks, time of day and nursing requirements all affect the effort. Geis Care therefore prepares an individual quote only after a free initial consultation.',
  contentSections: [
    {
      eyebrow: 'Cost factors',
      title: 'What determines the price of care',
      paragraphs: [
        'Two enquiries that sound similar can require very different organisation. A single appointment is different from regular home visits, post-hospital support or long-term relief for a family.',
        'All requested tasks should be stated openly so the quote includes the real scope and avoids unexpected additional work.',
      ],
      bullets: ['Frequency and duration', 'Location and necessary travel in Mallorca', 'Time of day, weekends and continuity', 'Companionship, household help or nursing measures', 'Appointments, transport, documentation and family coordination'],
    },
    {
      eyebrow: 'Review the quote',
      title: 'Points that should be clear in writing',
      bullets: ['Which tasks and times are included?', 'Are travel, accompaniment or extra appointments charged separately?', 'How are changes, cancellations and additional needs handled?', 'Which qualification is planned for the tasks?', 'How is care documented and who receives access?'],
      note: 'Compare the described scope and responsibilities, not only a single price.',
    },
    {
      eyebrow: 'Insurers and care funds',
      title: 'Do not assume reimbursement',
      paragraphs: [
        'Whether an insurer, care fund or another payer reimburses individual services depends on the personal contract and case.',
        'Geis Care does not work directly with insurers or care funds. Clarify any reimbursement yourself before the contract and obtain written confirmation where possible.',
      ],
    },
    {
      eyebrow: 'Next step',
      title: 'Prepare for the first consultation',
      paragraphs: [
        'Note the location, preferred days and times and the tasks for which help is needed. It is also useful to identify current helpers and regular appointments.',
        'The first consultation provides initial orientation. If appropriate, a personal assessment at home follows before a concrete quote is issued.',
      ],
    },
  ],
  servicesLabel: 'For planning',
  servicesTitle: 'Six details for a transparent quote',
  services: [
    { title: 'Location', desc: 'Address or region in Mallorca and accessibility of the home.' },
    { title: 'Time frame', desc: 'Frequency, duration, time of day and desired start date.' },
    { title: 'Tasks', desc: 'Specific support required in daily life, at home or at appointments.' },
    { title: 'Care needs', desc: 'Assess medical and nursing requirements separately.' },
    { title: 'Contacts', desc: 'Name involved relatives and preferred communication channels.' },
    { title: 'Written quote', desc: 'Record the scope and possible additional costs clearly.' },
  ],
  serviceAreaText: 'Geis Care prepares individual quotes for care throughout Mallorca. Price, travel, changes and cancellation terms are agreed for the specific service.',
  faq: [
    { q: 'Why does Geis Care not publish a standard price?', a: 'Because scope, duration, location and professional requirements differ. A meaningful price can be calculated only after the actual needs are known.' },
    { q: 'Is the first consultation free?', a: 'Yes. The initial consultation is free and without obligation. A concrete quote follows the needs assessment.' },
    { q: 'Does Geis Care work with insurers or care funds?', a: 'No. Geis Care does not bill insurers or care funds directly. Please clarify possible reimbursement with your payer yourself.' },
    { q: 'How are travel and cancellation charged?', a: 'Prices, travel, changes and cancellation terms are calculated individually and stated in the quote.' },
    { q: 'Can the price change later?', a: 'If tasks, times or scope change, a new calculation may be necessary. Changes should be discussed and recorded in writing in advance.' },
  ],
  internalLinks: [
    { label: 'Senior Care Mallorca' }, { label: 'Home Care Mallorca' }, { label: 'How the first consultation works' },
    { label: 'Organising care from abroad' }, { label: 'Holiday and respite care' },
  ],
  breadcrumbs: [{ label: 'Home' }, { label: 'Guides' }, { label: 'Cost of senior care' }],
  contactTitle: 'Prepare an individual quote',
  contactText: 'Provide the location, desired time frame and the main tasks. Geis Care will explain which additional details are needed during the free consultation.',
});

mergeSeoTranslation('kosten-seniorenbetreuung-mallorca', 'es', {
  h1: '¿Cuánto cuesta la atención a mayores en Mallorca?',
  metaTitle: 'Costes de la atención a mayores en Mallorca | Geis Care',
  metaDescription: 'Conozca los factores que determinan el coste de la atención a mayores en Mallorca y prepare un presupuesto individual transparente.',
  metaKeywords: 'costes atención mayores Mallorca, precios cuidados Mallorca, coste cuidados domicilio Mallorca',
  intro: 'Una planificación fiable no empieza con una tarifa estándar, sino con el apoyo que realmente se necesita. La duración, el lugar, las tareas, el horario y las exigencias de enfermería influyen en el esfuerzo. Por ello, Geis Care prepara un presupuesto individual después de una consulta inicial gratuita.',
  contentSections: [
    {
      eyebrow: 'Factores de coste',
      title: 'Qué determina el precio de la atención',
      paragraphs: [
        'Dos solicitudes parecidas pueden requerir una organización muy distinta. Una cita puntual no se planifica igual que visitas regulares, apoyo después del hospital o alivio a largo plazo para la familia.',
        'Deben indicarse todas las tareas deseadas para que el presupuesto refleje el alcance real y evite trabajo adicional inesperado.',
      ],
      bullets: ['Frecuencia y duración', 'Lugar y desplazamientos necesarios en Mallorca', 'Horario, fines de semana y continuidad', 'Acompañamiento, ayuda doméstica o enfermería', 'Citas, transporte, documentación y coordinación familiar'],
    },
    {
      eyebrow: 'Revisar el presupuesto',
      title: 'Puntos que deben quedar claros por escrito',
      bullets: ['¿Qué tareas y horarios están incluidos?', '¿Se cobran aparte desplazamientos o citas adicionales?', '¿Cómo se regulan cambios, cancelaciones y necesidades adicionales?', '¿Qué cualificación se prevé?', '¿Cómo se documenta la atención y quién tiene acceso?'],
      note: 'Compare el alcance y las responsabilidades, no solo un precio aislado.',
    },
    {
      eyebrow: 'Seguros y cajas de cuidados',
      title: 'No dé por supuesto el reembolso',
      paragraphs: [
        'El posible reembolso depende del contrato personal, de la cobertura y del caso concreto.',
        'Geis Care no trabaja directamente con aseguradoras ni cajas de cuidados. Aclare usted mismo cualquier reembolso antes de contratar y solicite confirmación escrita cuando sea posible.',
      ],
    },
    {
      eyebrow: 'Siguiente paso',
      title: 'Preparar la primera consulta',
      paragraphs: [
        'Anote el lugar, los días y horarios deseados y las tareas para las que necesita ayuda. También es útil indicar quién ayuda actualmente y qué citas son periódicas.',
        'La primera consulta ofrece una orientación inicial. Si procede, se realiza una valoración personal en casa antes de preparar el presupuesto concreto.',
      ],
    },
  ],
  servicesLabel: 'Para la planificación',
  servicesTitle: 'Seis datos para un presupuesto transparente',
  services: [
    { title: 'Lugar', desc: 'Dirección o región de Mallorca y accesibilidad del domicilio.' },
    { title: 'Periodo', desc: 'Frecuencia, duración, horarios y fecha de inicio deseada.' },
    { title: 'Tareas', desc: 'Apoyo concreto en la vida diaria, en casa o en citas.' },
    { title: 'Necesidades de cuidado', desc: 'Valorar por separado los requisitos médicos y de enfermería.' },
    { title: 'Contactos', desc: 'Indicar familiares implicados y vías de comunicación.' },
    { title: 'Presupuesto escrito', desc: 'Recoger claramente el alcance y los posibles costes adicionales.' },
  ],
  serviceAreaText: 'Geis Care prepara presupuestos individuales para cuidados en toda Mallorca. El precio, los desplazamientos, los cambios y las cancelaciones se acuerdan para el servicio concreto.',
  faq: [
    { q: '¿Por qué Geis Care no publica un precio estándar?', a: 'Porque el alcance, la duración, el lugar y los requisitos profesionales varían. El precio se calcula cuando se conocen las necesidades reales.' },
    { q: '¿La primera consulta es gratuita?', a: 'Sí. Es gratuita y sin compromiso. El presupuesto concreto se prepara después de valorar las necesidades.' },
    { q: '¿Trabaja Geis Care con aseguradoras o cajas de cuidados?', a: 'No. Geis Care no factura directamente a estos organismos. Aclare usted mismo cualquier posible reembolso.' },
    { q: '¿Cómo se calculan los desplazamientos y las cancelaciones?', a: 'El precio, los desplazamientos, los cambios y las cancelaciones se calculan individualmente y constan en el presupuesto.' },
    { q: '¿Puede cambiar el precio más adelante?', a: 'Si cambian las tareas, los horarios o el alcance, puede ser necesario recalcular. Los cambios deben comentarse y documentarse previamente.' },
  ],
  internalLinks: [
    { label: 'Atención a mayores Mallorca' }, { label: 'Cuidados a domicilio Mallorca' }, { label: 'Cómo funciona la primera consulta' },
    { label: 'Organizar la atención desde el extranjero' }, { label: 'Atención vacacional y de respiro' },
  ],
  breadcrumbs: [{ label: 'Inicio' }, { label: 'Guías' }, { label: 'Costes de la atención a mayores' }],
  contactTitle: 'Preparar un presupuesto individual',
  contactText: 'Indique el lugar, el periodo y las tareas principales. Geis Care explicará durante la consulta gratuita qué otros datos son necesarios.',
});

mergeSeoTranslation('eltern-auf-mallorca-betreuung-aus-deutschland', 'en', {
  h1: 'Organising care for parents in Mallorca from abroad',
  metaTitle: 'Organise care for parents in Mallorca from abroad | Geis Care',
  metaDescription: 'Plan care for parents in Mallorca from abroad with clear contacts, authorisations, emergency procedures and transparent updates.',
  metaKeywords: 'parents Mallorca care from abroad, organise senior care Mallorca, relatives Mallorca care',
  intro: 'When parents live in Mallorca and relatives live abroad, care requires clear responsibilities and reliable information. Geis Care can support the family locally. The person receiving care must be involved and communication, consent and emergency contacts must be clearly agreed.',
  contentSections: [
    {
      eyebrow: 'A joint decision',
      title: 'Speak with your parents, not only about them',
      paragraphs: [
        'Start with an open discussion about which support is wanted and accepted. As long as the person can decide for themselves, they determine who is involved and which information may be shared.',
        'Describe the need precisely: companionship, shopping, personal care, medical appointments, medication organisation or support after hospital discharge.',
      ],
    },
    {
      eyebrow: 'Locally and from abroad',
      title: 'Assign responsibilities clearly',
      bullets: ['Name one principal family contact and a substitute', 'Record the local doctor, pharmacy and other professionals', 'Agree emergency contacts and communication channels', 'Have powers of attorney and consent reviewed where necessary', 'Set regular updates and escalation thresholds'],
      note: 'Geis Care shares health information only with authorised people and within the agreed framework.',
    },
    {
      eyebrow: 'Emergency planning',
      title: 'A simple plan prevents searching in a crisis',
      paragraphs: [
        'Identification, insurance details, medication, allergies, medical contacts and reachable relatives should be available in a known but protected place.',
        'Call 112 for an acute life-threatening emergency. Regular care and the Geis Care on-call service do not replace emergency medical services.',
      ],
    },
    {
      eyebrow: 'Ongoing cooperation',
      title: 'Transparency without permanent surveillance',
      paragraphs: [
        'Digital documentation can record agreed measures and appointments. Access is agreed with the person receiving care.',
        'Short, scheduled updates are often more useful than many spontaneous messages and make it easier to discuss changes in needs.',
      ],
    },
  ],
  servicesLabel: 'Organisation',
  servicesTitle: 'What families should clarify before the start',
  services: [
    { title: 'Parents’ wishes', desc: 'Record consent, priorities and boundaries.' },
    { title: 'Contacts', desc: 'Name a reliable principal contact and a substitute.' },
    { title: 'Documents', desc: 'Keep insurance, medication and relevant medical information current.' },
    { title: 'Emergency plan', desc: 'Define 112, local contacts and the information chain.' },
    { title: 'Documentation', desc: 'Agree what is recorded and who may see it.' },
    { title: 'Regular updates', desc: 'Set fixed times for care and family coordination.' },
  ],
  serviceAreaText: 'Geis Care offers care for parents and relatives throughout Mallorca. Location, schedule and staffing are planned personally.',
  faq: [
    { q: 'Can I commission care without involving my parents?', a: 'The person receiving care should generally be involved and consent to care and information sharing. Legal representation must be documented and respected.' },
    { q: 'How do I know whether agreed visits took place?', a: 'Digital documentation and additional updates can be agreed. Access rights are defined before care begins.' },
    { q: 'Are night care and an on-call service available?', a: 'Yes. Geis Care offers night and around-the-clock models as well as an on-call service. Exact contact times, response procedures and staffing are agreed individually; call 112 in an acute emergency.' },
    { q: 'Which powers of attorney are useful?', a: 'This depends on the family and legal situation. Obtain professional advice where necessary; Geis Care does not provide legal advice.' },
    { q: 'Can care be adjusted at short notice?', a: 'Changes can be discussed but depend on the new need and availability. They should be documented and confirmed.' },
  ],
  internalLinks: [
    { label: 'Senior Care Mallorca' }, { label: 'Dementia Care Mallorca' }, { label: 'Cost of senior care' },
    { label: 'How the first consultation works' }, { label: 'Emergency in Mallorca' },
  ],
  breadcrumbs: [{ label: 'Home' }, { label: 'Guides' }, { label: 'Organising care from abroad' }],
  contactTitle: 'Plan care from abroad together',
  contactText: 'Describe the location, current situation and the family’s desired role. Geis Care will explain how a consultation with everyone involved can be organised.',
});

mergeSeoTranslation('eltern-auf-mallorca-betreuung-aus-deutschland', 'es', {
  h1: 'Organizar desde el extranjero la atención de los padres en Mallorca',
  metaTitle: 'Organizar la atención de los padres en Mallorca desde el extranjero | Geis Care',
  metaDescription: 'Planifique la atención de sus padres en Mallorca con contactos claros, autorizaciones, procedimientos de urgencia y actualizaciones transparentes.',
  metaKeywords: 'padres Mallorca atención extranjero, organizar atención mayores Mallorca, familiares cuidados Mallorca',
  intro: 'Cuando los padres viven en Mallorca y la familia en el extranjero, la atención necesita responsabilidades claras e información fiable. Geis Care puede apoyar localmente. La persona atendida debe participar y deben acordarse la comunicación, los consentimientos y los contactos de urgencia.',
  contentSections: [
    {
      eyebrow: 'Decisión conjunta',
      title: 'Hablar con los padres, no solo sobre ellos',
      paragraphs: [
        'Empiece con una conversación abierta sobre el apoyo deseado y aceptado. Mientras la persona pueda decidir, determina quién participa y qué información puede compartirse.',
        'Describa la necesidad con precisión: compañía, compras, higiene, citas médicas, medicación o apoyo después de una hospitalización.',
      ],
    },
    {
      eyebrow: 'Localmente y desde el extranjero',
      title: 'Repartir claramente las responsabilidades',
      bullets: ['Nombrar un contacto familiar principal y un sustituto', 'Anotar médico, farmacia y otros profesionales locales', 'Acordar contactos de urgencia y vías de comunicación', 'Revisar poderes y consentimientos cuando sea necesario', 'Fijar actualizaciones periódicas y límites de escalada'],
      note: 'Geis Care comparte información médica solo con personas autorizadas y dentro del marco acordado.',
    },
    {
      eyebrow: 'Plan de urgencia',
      title: 'Un plan sencillo evita búsquedas en una crisis',
      paragraphs: [
        'Identificación, seguro, medicación, alergias, contactos médicos y familiares localizables deben estar disponibles en un lugar conocido y protegido.',
        'Ante una urgencia vital llame al 112. La atención regular y el servicio de guardia de Geis Care no sustituyen los servicios médicos de urgencia.',
      ],
    },
    {
      eyebrow: 'Colaboración continua',
      title: 'Transparencia sin vigilancia permanente',
      paragraphs: [
        'La documentación digital puede registrar medidas y citas acordadas. El acceso se acuerda con la persona atendida.',
        'Las actualizaciones breves y programadas suelen ser más útiles que muchos mensajes espontáneos y facilitan hablar de cambios.',
      ],
    },
  ],
  servicesLabel: 'Organización',
  servicesTitle: 'Qué debe aclarar la familia antes de empezar',
  services: [
    { title: 'Deseos de los padres', desc: 'Registrar consentimiento, prioridades y límites.' },
    { title: 'Contactos', desc: 'Nombrar un contacto principal y un sustituto.' },
    { title: 'Documentos', desc: 'Mantener al día seguro, medicación e información médica.' },
    { title: 'Plan de urgencia', desc: 'Definir 112, contactos locales y cadena de información.' },
    { title: 'Documentación', desc: 'Acordar qué se registra y quién puede consultarlo.' },
    { title: 'Actualizaciones periódicas', desc: 'Fijar horarios para la atención y coordinación familiar.' },
  ],
  serviceAreaText: 'Geis Care ofrece atención para padres y familiares en toda Mallorca. El lugar, los horarios y el personal se planifican personalmente.',
  faq: [
    { q: '¿Puedo contratar la atención sin implicar a mis padres?', a: 'La persona atendida debe participar y consentir la atención y la transmisión de información. La representación legal debe documentarse y respetarse.' },
    { q: '¿Cómo sé si se realizaron las visitas acordadas?', a: 'Pueden acordarse documentación digital y actualizaciones adicionales. Los derechos de acceso se definen antes de empezar.' },
    { q: '¿Hay atención nocturna y servicio de guardia?', a: 'Sí. Geis Care ofrece modelos nocturnos y de 24 horas, además de servicio de guardia. Los horarios, la respuesta y el personal se acuerdan; ante una urgencia aguda llame al 112.' },
    { q: '¿Qué poderes son útiles?', a: 'Depende de la situación familiar y jurídica. Solicite asesoramiento profesional cuando sea necesario; Geis Care no ofrece asesoramiento legal.' },
    { q: '¿Puede adaptarse la atención con poca antelación?', a: 'Los cambios pueden hablarse, pero dependen de la nueva necesidad y disponibilidad. Deben documentarse y confirmarse.' },
  ],
  internalLinks: [
    { label: 'Atención a mayores Mallorca' }, { label: 'Atención a la demencia Mallorca' }, { label: 'Costes de la atención a mayores' },
    { label: 'Cómo funciona la primera consulta' }, { label: 'Urgencias en Mallorca' },
  ],
  breadcrumbs: [{ label: 'Inicio' }, { label: 'Guías' }, { label: 'Organizar la atención desde el extranjero' }],
  contactTitle: 'Planificar juntos la atención desde el extranjero',
  contactText: 'Describa el lugar, la situación actual y el papel deseado de la familia. Geis Care explicará cómo organizar una consulta con todas las personas implicadas.',
});

mergeSeoTranslation('betreuung-nach-krankenhaus-mallorca', 'en', {
  h1: 'Support after hospital discharge in Mallorca',
  metaTitle: 'Support after hospital discharge in Mallorca | Geis Care',
  metaDescription: 'Prepare a safe discharge in Mallorca: documents, medication, equipment, appointments and support at home.',
  metaKeywords: 'after hospital care Mallorca, discharge support Mallorca, post operative home care Mallorca',
  intro: 'The first days after hospital discharge often determine whether recovery at home proceeds safely. Medication, mobility, wound care, food, follow-up appointments and support must be coordinated before discharge. Geis Care can assess and provide suitable support throughout Mallorca.',
  contentSections: [
    {
      eyebrow: 'Before discharge',
      title: 'Do not leave the clinic without a clear plan',
      bullets: ['Discharge report and current diagnoses', 'Medication plan with changes clearly marked', 'Wound-care or therapy instructions', 'Required equipment and safe transport home', 'Follow-up appointments and reachable medical contacts'],
      note: 'Clarify unclear instructions with the treating team before leaving the hospital.',
    },
    {
      eyebrow: 'At home',
      title: 'Prepare the first days realistically',
      paragraphs: [
        'Check access, stairs, bathroom, bed, food and available assistance. Someone should know who to contact if symptoms change.',
        'Tasks must be divided clearly between the client, relatives, Geis Care and other professionals.',
      ],
    },
    {
      eyebrow: 'Professional assessment',
      title: 'Medical measures require clear instructions',
      paragraphs: [
        'Wound care, medication administration and other nursing measures must be assessed professionally and, where required, prescribed by a doctor.',
        'For severe pain, shortness of breath, heavy bleeding, loss of consciousness or acute deterioration, call 112 immediately.',
      ],
    },
    {
      eyebrow: 'Ongoing plan',
      title: 'Review needs after the first visits',
      paragraphs: [
        'Recovery and independence can change quickly after discharge. The plan should therefore be reviewed after the first visits.',
        'Any adjustment to tasks, times or staffing is discussed and recorded rather than assumed.',
      ],
    },
  ],
  servicesLabel: 'Discharge planning',
  servicesTitle: 'What should be ready at home',
  services: [
    { title: 'Discharge documents', desc: 'Keep reports, instructions and contacts together.' },
    { title: 'Medication', desc: 'Record the current plan and all changes clearly.' },
    { title: 'Home safety', desc: 'Check access, bathroom, bed, lighting and fall risks.' },
    { title: 'Nursing tasks', desc: 'Clarify professional and medical requirements before the start.' },
    { title: 'Appointments', desc: 'Plan follow-up visits, transport and necessary accompaniment.' },
    { title: 'Family contacts', desc: 'Define reachable contacts and authorised recipients of information.' },
  ],
  serviceAreaText: 'Geis Care offers support after hospital discharge throughout Mallorca. Location, start date and scope are agreed individually before care begins.',
  faq: [
    { q: 'When should support be requested?', a: 'Ideally before discharge, as soon as the expected date and likely needs are known.' },
    { q: 'Can Geis Care organise medication and wound care?', a: 'Medication administration and wound care are confirmed after professional assessment and, where required, medical instructions.' },
    { q: 'Is night or around-the-clock support possible?', a: 'Yes. These models are available and their staffing is planned for the individual situation.' },
    { q: 'What if the person’s condition suddenly worsens?', a: 'Call 112 for acute or life-threatening symptoms. The agreed on-call and family contacts should then be informed.' },
    { q: 'Can relatives abroad be updated?', a: 'Yes, with the client’s consent or appropriate authorisation. Channels and frequency are agreed before care begins.' },
  ],
  internalLinks: [
    { label: 'Intensive Care Mallorca' }, { label: 'Home Care Mallorca' }, { label: 'Hospital admission in Mallorca' },
    { label: 'Doctors in Mallorca' }, { label: 'How the first consultation works' },
  ],
  breadcrumbs: [{ label: 'Home' }, { label: 'Guides' }, { label: 'Support after hospital discharge' }],
  contactTitle: 'Prepare support before discharge',
  contactText: 'Tell us the expected discharge date, location and anticipated support. Geis Care will explain which documents are needed for assessment.',
});

mergeSeoTranslation('betreuung-nach-krankenhaus-mallorca', 'es', {
  h1: 'Apoyo después del alta hospitalaria en Mallorca',
  metaTitle: 'Apoyo después del alta hospitalaria en Mallorca | Geis Care',
  metaDescription: 'Prepare un alta segura en Mallorca: documentos, medicación, ayudas, citas y atención en casa.',
  metaKeywords: 'cuidados después hospital Mallorca, apoyo alta hospitalaria Mallorca, cuidados posoperatorios domicilio Mallorca',
  intro: 'Los primeros días tras el alta suelen ser decisivos para una recuperación segura en casa. Deben coordinarse medicación, movilidad, heridas, alimentación, citas de seguimiento y apoyo. Geis Care puede valorar y prestar la ayuda adecuada en toda Mallorca.',
  contentSections: [
    {
      eyebrow: 'Antes del alta',
      title: 'No abandonar la clínica sin un plan claro',
      bullets: ['Informe de alta y diagnósticos actuales', 'Plan de medicación con cambios claramente marcados', 'Indicaciones para heridas o terapias', 'Ayudas necesarias y transporte seguro', 'Citas de seguimiento y contactos médicos localizables'],
      note: 'Aclare cualquier indicación dudosa con el equipo tratante antes de salir del hospital.',
    },
    {
      eyebrow: 'En casa',
      title: 'Preparar de forma realista los primeros días',
      paragraphs: [
        'Revise acceso, escaleras, baño, cama, alimentación y ayuda disponible. Alguien debe saber a quién contactar si cambian los síntomas.',
        'Las tareas deben repartirse claramente entre la persona, la familia, Geis Care y otros profesionales.',
      ],
    },
    {
      eyebrow: 'Valoración profesional',
      title: 'Las medidas médicas necesitan instrucciones claras',
      paragraphs: [
        'El cuidado de heridas, la administración de medicación y otras medidas requieren valoración profesional y, cuando corresponda, indicación médica.',
        'Ante dolor intenso, falta de aire, hemorragia, pérdida de conciencia o empeoramiento agudo, llame inmediatamente al 112.',
      ],
    },
    {
      eyebrow: 'Plan continuo',
      title: 'Revisar las necesidades tras las primeras visitas',
      paragraphs: [
        'La recuperación y la autonomía pueden cambiar rápidamente. Por ello, el plan debe revisarse después de las primeras visitas.',
        'Cualquier cambio de tareas, horarios o personal se comenta y documenta.',
      ],
    },
  ],
  servicesLabel: 'Planificación del alta',
  servicesTitle: 'Qué debe estar preparado en casa',
  services: [
    { title: 'Documentos de alta', desc: 'Mantener juntos informes, indicaciones y contactos.' },
    { title: 'Medicación', desc: 'Registrar claramente el plan actual y todos los cambios.' },
    { title: 'Seguridad en casa', desc: 'Revisar accesos, baño, cama, iluminación y riesgos de caída.' },
    { title: 'Tareas de enfermería', desc: 'Aclarar requisitos profesionales y médicos antes del inicio.' },
    { title: 'Citas', desc: 'Planificar seguimientos, transporte y acompañamiento.' },
    { title: 'Contactos familiares', desc: 'Definir personas localizables y destinatarios autorizados.' },
  ],
  serviceAreaText: 'Geis Care ofrece apoyo después del alta hospitalaria en toda Mallorca. El lugar, la fecha de inicio y el alcance se acuerdan individualmente.',
  faq: [
    { q: '¿Cuándo debe solicitarse el apoyo?', a: 'Preferiblemente antes del alta, cuando se conozcan la fecha prevista y las necesidades probables.' },
    { q: '¿Puede Geis Care organizar la medicación y el cuidado de heridas?', a: 'Estas medidas se confirman tras una valoración profesional y, cuando sea necesario, indicación médica.' },
    { q: '¿Es posible la atención nocturna o de 24 horas?', a: 'Sí. Estos modelos están disponibles y el personal se planifica para la situación concreta.' },
    { q: '¿Qué ocurre si el estado empeora de repente?', a: 'Ante síntomas agudos o vitales llame al 112. Después deben informarse los contactos de guardia y familiares acordados.' },
    { q: '¿Puede informarse a familiares en el extranjero?', a: 'Sí, con el consentimiento o la autorización correspondiente. Las vías y la frecuencia se acuerdan antes.' },
  ],
  internalLinks: [
    { label: 'Cuidados intensivos Mallorca' }, { label: 'Cuidados a domicilio Mallorca' }, { label: 'Ingreso hospitalario en Mallorca' },
    { label: 'Médicos en Mallorca' }, { label: 'Cómo funciona la primera consulta' },
  ],
  breadcrumbs: [{ label: 'Inicio' }, { label: 'Guías' }, { label: 'Apoyo después del alta' }],
  contactTitle: 'Preparar el apoyo antes del alta',
  contactText: 'Indique la fecha prevista, el lugar y la ayuda esperada. Geis Care explicará qué documentos necesita para la valoración.',
});

mergeSeoTranslation('urlaubs-und-verhinderungspflege-mallorca', 'en', {
  h1: 'Planning holiday and respite care in Mallorca',
  metaTitle: 'Holiday and respite care in Mallorca | Geis Care',
  metaDescription: 'Plan reliable care in Mallorca when family carers are unavailable or need a break: handover, medication, emergency contacts and costs.',
  metaKeywords: 'respite care Mallorca, holiday care Mallorca, temporary care Mallorca',
  intro: 'Family carers are allowed to be unavailable, travel or simply take a break. To keep the person in Mallorca reliably supported, temporary cover should be planned early with a complete handover. Geis Care offers holiday support and flexible care models after an individual assessment.',
  contentSections: [
    {
      eyebrow: 'Reasons',
      title: 'When temporary cover is useful',
      paragraphs: [
        'Cover may be required for holidays, illness, work commitments or regular breaks. Long-stay visitors and winter residents can also request support for a limited period.',
        'Tasks, responsibilities, routines, risks and reachable contacts should be documented rather than handed over only verbally.',
      ],
    },
    {
      eyebrow: 'Handover',
      title: 'Information that belongs in the care plan',
      bullets: ['Daily routine, preferences and important habits', 'Current medication plan and responsible medical practice', 'Mobility, equipment and personal-care needs', 'Shopping, meals, appointments and household tasks', 'Emergency contacts, consent and permitted information sharing', 'Start, end and requested care times'],
    },
    {
      eyebrow: 'Scope',
      title: 'Temporary cover is not automatically around-the-clock care',
      paragraphs: [
        'Hourly support, daytime assistance, night care and around-the-clock care are different models. Geis Care offers these models and plans the required coverage and staffing individually.',
        'An on-call service with agreed contact procedures can also be arranged. Call 112 for an acute or life-threatening emergency. Medical measures require separate professional and, where necessary, medical assessment.',
      ],
    },
    {
      eyebrow: 'Financing',
      title: 'Confirm possible reimbursement individually',
      paragraphs: [
        'Eligibility for reimbursement depends on the insurance relationship, personal entitlement and the payer’s rules.',
        'Geis Care does not work directly with care funds or insurers. Clarify possible reimbursement yourself in advance and request written confirmation.',
      ],
    },
  ],
  servicesLabel: 'Before the start',
  servicesTitle: 'Prepare temporary cover in six steps',
  services: [
    { title: 'Set the period', desc: 'State start, end, times and frequency precisely.' },
    { title: 'Assess needs', desc: 'Describe everyday tasks and nursing requirements separately.' },
    { title: 'Meet beforehand', desc: 'If possible, introduce the person receiving care and the caregiver.' },
    { title: 'Document the handover', desc: 'Record routines, medication, appointments and responsibilities.' },
    { title: 'Emergency contacts', desc: 'Agree reachable people and procedures for changes.' },
    { title: 'Plan updates', desc: 'Define who is informed, when and by which channel.' },
  ],
  serviceAreaText: 'Temporary care is offered throughout Mallorca. Planning time, location and staffing are agreed individually before the start.',
  faq: [
    { q: 'How early should I request holiday cover?', a: 'As early as possible, especially for longer or fixed periods. Early planning supports assessment, introductions and a reliable handover.' },
    { q: 'Is hourly relief also possible?', a: 'Yes. Geis Care offers hourly and daily models. The requested times are confirmed individually.' },
    { q: 'Will a care fund cover the costs?', a: 'This cannot be answered generally. Geis Care does not work directly with care funds or insurers. Please clarify reimbursement yourself in writing.' },
    { q: 'Can care be provided at night or around the clock?', a: 'Yes. Geis Care offers night and around-the-clock models. Staffing is planned professionally and individually.' },
    { q: 'What happens in an emergency?', a: 'Geis Care offers an individually agreed on-call service. For an acute or life-threatening emergency, call 112 immediately.' },
  ],
  internalLinks: [
    { label: 'Senior Care Mallorca' }, { label: 'Dementia Care Mallorca' }, { label: 'Cost of senior care' },
    { label: 'Organising care from abroad' }, { label: 'How the first consultation works' },
  ],
  breadcrumbs: [{ label: 'Home' }, { label: 'Guides' }, { label: 'Holiday and respite care' }],
  contactTitle: 'Request temporary cover early',
  contactText: 'Provide the period, location and daily support required. Geis Care will assess which model can be offered reliably.',
});

mergeSeoTranslation('urlaubs-und-verhinderungspflege-mallorca', 'es', {
  h1: 'Planificar la atención vacacional y de respiro en Mallorca',
  metaTitle: 'Atención vacacional y de respiro en Mallorca | Geis Care',
  metaDescription: 'Planifique cuidados fiables cuando los familiares no están disponibles o necesitan un descanso: traspaso, medicación, urgencias y costes.',
  metaKeywords: 'atención respiro Mallorca, cuidados vacaciones Mallorca, cuidados temporales Mallorca',
  intro: 'Los familiares cuidadores pueden ausentarse, viajar o simplemente descansar. Para que la persona en Mallorca siga bien atendida, la sustitución debe planificarse con tiempo y con un traspaso completo. Geis Care ofrece apoyo vacacional y modelos flexibles tras una valoración individual.',
  contentSections: [
    {
      eyebrow: 'Motivos',
      title: 'Cuándo es útil una sustitución temporal',
      paragraphs: [
        'Puede necesitarse por vacaciones, enfermedad, compromisos laborales o descansos periódicos. También quienes pasan largas temporadas o el invierno pueden solicitar apoyo por un periodo limitado.',
        'Las tareas, responsabilidades, rutinas, riesgos y contactos deben documentarse y no transmitirse solo verbalmente.',
      ],
    },
    {
      eyebrow: 'Traspaso',
      title: 'Información que debe incluir el plan',
      bullets: ['Rutina diaria, preferencias y hábitos importantes', 'Plan de medicación y consulta médica responsable', 'Movilidad, ayudas y necesidades de higiene', 'Compras, comidas, citas y tareas domésticas', 'Contactos de urgencia, consentimientos y transmisión permitida', 'Inicio, fin y horarios deseados'],
    },
    {
      eyebrow: 'Alcance',
      title: 'La sustitución no es automáticamente atención de 24 horas',
      paragraphs: [
        'El apoyo por horas, de día, nocturno y de 24 horas son modelos distintos. Geis Care los ofrece y planifica individualmente la cobertura y el personal.',
        'También puede acordarse un servicio de guardia. Ante una urgencia grave o vital llame al 112. Las medidas médicas requieren valoración profesional y, cuando corresponda, médica.',
      ],
    },
    {
      eyebrow: 'Financiación',
      title: 'Confirmar individualmente un posible reembolso',
      paragraphs: [
        'El posible reembolso depende de la relación de seguro, del derecho personal y de las reglas del organismo pagador.',
        'Geis Care no trabaja directamente con cajas de cuidados ni aseguradoras. Aclare usted mismo el reembolso y solicite confirmación escrita.',
      ],
    },
  ],
  servicesLabel: 'Antes de empezar',
  servicesTitle: 'Preparar la sustitución en seis pasos',
  services: [
    { title: 'Definir el periodo', desc: 'Indicar con precisión inicio, fin, horarios y frecuencia.' },
    { title: 'Valorar necesidades', desc: 'Describir por separado las tareas diarias y la enfermería.' },
    { title: 'Conocerse previamente', desc: 'Si es posible, presentar a la persona atendida y al cuidador.' },
    { title: 'Documentar el traspaso', desc: 'Registrar rutinas, medicación, citas y responsabilidades.' },
    { title: 'Contactos de urgencia', desc: 'Acordar personas localizables y procedimiento ante cambios.' },
    { title: 'Planificar actualizaciones', desc: 'Definir a quién se informa, cuándo y por qué vía.' },
  ],
  serviceAreaText: 'La atención temporal se ofrece en toda Mallorca. El plazo de planificación, el lugar y el personal se acuerdan individualmente.',
  faq: [
    { q: '¿Con cuánta antelación debo solicitar la sustitución?', a: 'Lo antes posible, especialmente para periodos largos o fechas fijas. La planificación temprana facilita valoración, presentación y traspaso.' },
    { q: '¿También es posible el apoyo por horas?', a: 'Sí. Geis Care ofrece modelos por horas y por días. Los horarios se confirman individualmente.' },
    { q: '¿La caja de cuidados cubre los costes?', a: 'No puede responderse de forma general. Geis Care no trabaja directamente con cajas ni aseguradoras. Aclare usted mismo el reembolso por escrito.' },
    { q: '¿Puede prestarse atención nocturna o de 24 horas?', a: 'Sí. Geis Care ofrece ambos modelos. El personal se planifica profesionalmente para cada caso.' },
    { q: '¿Qué ocurre en una urgencia?', a: 'Geis Care ofrece un servicio de guardia acordado individualmente. Ante una urgencia aguda o vital llame al 112.' },
  ],
  internalLinks: [
    { label: 'Atención a mayores Mallorca' }, { label: 'Atención a la demencia Mallorca' }, { label: 'Costes de la atención a mayores' },
    { label: 'Organizar la atención desde el extranjero' }, { label: 'Cómo funciona la primera consulta' },
  ],
  breadcrumbs: [{ label: 'Inicio' }, { label: 'Guías' }, { label: 'Atención vacacional y de respiro' }],
  contactTitle: 'Solicitar la sustitución con antelación',
  contactText: 'Indique el periodo, el lugar y el apoyo diario. Geis Care valorará qué modelo puede ofrecerse de forma fiable.',
});

mergeSeoTranslation('pflege-zuhause-oder-pflegeheim-mallorca', 'en', {
  h1: 'Care at home or a care home in Mallorca?',
  metaTitle: 'Care at home or a care home in Mallorca? | Geis Care',
  metaDescription: 'A structured decision guide for families comparing safety, care needs, housing, social contact, family involvement and costs.',
  metaKeywords: 'home care or care home Mallorca, nursing home Mallorca decision, home care Mallorca',
  intro: 'There is no single solution that is right for every family. The decision depends on care needs, safety, the home, social contacts, pressure on relatives and finances. This guide helps organise the key questions but does not replace personal medical, nursing or legal advice.',
  contentSections: [
    {
      eyebrow: 'At home',
      title: 'When familiar surroundings can work well',
      paragraphs: [
        'Remaining at home can preserve orientation, privacy and familiar routines if the home suits the person’s mobility and reliable support can be organised.',
        'Support can be increased gradually. Gaps must still be assessed honestly, including long periods alone, stairs, night needs or a lack of local contacts.',
      ],
    },
    {
      eyebrow: 'Residential care',
      title: 'When a care home can provide relief',
      paragraphs: [
        'Residential care may be suitable when extensive support, continuous presence or an adapted environment is permanently required and cannot be arranged safely at home.',
        'Families should review the care concept, communication, languages, visiting rules, medical links, costs and their personal impression, not only the facilities.',
      ],
    },
    {
      eyebrow: 'Decision matrix',
      title: 'Six questions for a family discussion',
      bullets: ['What does the person concerned want?', 'What help is actually needed by day and night?', 'Can the home be adapted safely at reasonable effort?', 'Who is reliably local and who can help only from abroad?', 'Which social contacts exist in each option?', 'What are the total costs including care, travel and housing?'],
    },
    {
      eyebrow: 'Intermediate solutions',
      title: 'The decision does not have to be final immediately',
      paragraphs: [
        'Temporary support after hospital discharge or during a family break can help reveal the actual needs. A combination of home care, family help, daily structure and other services may also be considered.',
        'Geis Care can explain the possible scope of its home support. The overall decision should involve the person concerned and independent medical or nursing professionals.',
      ],
    },
  ],
  servicesLabel: 'Decision',
  servicesTitle: 'Areas you should compare',
  services: [
    { title: 'Personal preference', desc: 'Take quality of life, autonomy and familiar routines seriously.' },
    { title: 'Care needs', desc: 'Assess daytime, night-time and medical support realistically.' },
    { title: 'Home safety', desc: 'Review access, bathroom, stairs, equipment and time alone.' },
    { title: 'Social environment', desc: 'Include relatives, neighbours, activities and reachable contacts.' },
    { title: 'Emergency organisation', desc: 'Clarify response procedures and responsibilities for both options.' },
    { title: 'Total costs', desc: 'Compare all ongoing and additional costs, not isolated prices.' },
  ],
  serviceAreaText: 'Geis Care advises on its own home-care services throughout Mallorca. Availability and details of residential facilities must be checked directly with the respective provider.',
  faq: [
    { q: 'Is care at home always better?', a: 'No. It can be suitable when preferences, housing, needs and available help fit together. For high or permanent needs, another model may be safer.' },
    { q: 'How do I know home care is no longer enough?', a: 'Frequent crises, long unprovided periods, repeated falls, family exhaustion or medical requirements that cannot be met safely are warning signs.' },
    { q: 'Can home care be tried for a limited period?', a: 'Yes. A defined trial followed by a review can help. Scope, goals and criteria for adjustment should be agreed first.' },
    { q: 'Does Geis Care provide independent care-home advice?', a: 'Geis Care provides home care. Independent medical, nursing and, where necessary, legal advice should also be included in the overall decision.' },
    { q: 'What should be checked at a care home?', a: 'Care concept, licence, staffing, languages, medical links, costs, visiting rules, contract terms and the personal impression.' },
  ],
  internalLinks: [
    { label: 'Home Care Mallorca' }, { label: 'Senior Care Mallorca' }, { label: 'Dementia Care Mallorca' },
    { label: 'Cost of senior care' }, { label: 'Support after hospital discharge' },
  ],
  breadcrumbs: [{ label: 'Home' }, { label: 'Guides' }, { label: 'At home or a care home' }],
  contactTitle: 'Assess the possibilities at home realistically',
  contactText: 'Describe the home, support needs and available help. Geis Care can explain which home services are possible and where independent advice is useful.',
});

mergeSeoTranslation('pflege-zuhause-oder-pflegeheim-mallorca', 'es', {
  h1: '¿Cuidados en casa o residencia en Mallorca?',
  metaTitle: '¿Cuidados en casa o residencia en Mallorca? | Geis Care',
  metaDescription: 'Guía estructurada para comparar seguridad, necesidades, vivienda, contactos sociales, participación familiar y costes.',
  metaKeywords: 'cuidados casa o residencia Mallorca, residencia mayores Mallorca decisión, cuidados domicilio Mallorca',
  intro: 'No existe una solución correcta para todas las familias. La decisión depende de las necesidades, la seguridad, la vivienda, los contactos sociales, la carga familiar y las finanzas. Esta guía ordena las preguntas principales, pero no sustituye el asesoramiento médico, asistencial o jurídico.',
  contentSections: [
    {
      eyebrow: 'En casa',
      title: 'Cuándo puede funcionar bien el entorno conocido',
      paragraphs: [
        'Permanecer en casa puede conservar orientación, privacidad y rutinas si la vivienda se adapta a la movilidad y puede organizarse apoyo fiable.',
        'La ayuda puede ampliarse gradualmente. Aun así, deben valorarse periodos largos a solas, escaleras, necesidades nocturnas o falta de contactos locales.',
      ],
    },
    {
      eyebrow: 'Atención residencial',
      title: 'Cuándo puede aliviar una residencia',
      paragraphs: [
        'Puede ser adecuada cuando se necesita mucha ayuda, presencia continua o un entorno adaptado y no puede organizarse de forma segura en casa.',
        'La familia debe revisar el concepto asistencial, comunicación, idiomas, visitas, conexión médica, costes y su impresión personal, no solo las instalaciones.',
      ],
    },
    {
      eyebrow: 'Matriz de decisión',
      title: 'Seis preguntas para hablar en familia',
      bullets: ['¿Qué desea la persona afectada?', '¿Qué ayuda necesita realmente de día y de noche?', '¿Puede adaptarse la vivienda de forma segura?', '¿Quién está presente localmente y quién ayuda desde lejos?', '¿Qué contactos sociales existen en cada opción?', '¿Cuáles son los costes totales, incluidos cuidados, viajes y vivienda?'],
    },
    {
      eyebrow: 'Soluciones intermedias',
      title: 'La decisión no tiene que ser definitiva de inmediato',
      paragraphs: [
        'El apoyo temporal después del hospital o durante un descanso familiar puede mostrar las necesidades reales. También puede valorarse una combinación de atención domiciliaria, familia y otros servicios.',
        'Geis Care puede explicar el alcance de su apoyo en casa. La decisión global debe incluir a la persona y a profesionales médicos o asistenciales independientes.',
      ],
    },
  ],
  servicesLabel: 'Decisión',
  servicesTitle: 'Aspectos que debe comparar',
  services: [
    { title: 'Deseo personal', desc: 'Tomar en serio la calidad de vida, la autonomía y las rutinas.' },
    { title: 'Necesidades de cuidado', desc: 'Valorar de forma realista la ayuda diurna, nocturna y médica.' },
    { title: 'Seguridad del domicilio', desc: 'Revisar accesos, baño, escaleras, ayudas y tiempo a solas.' },
    { title: 'Entorno social', desc: 'Incluir familiares, vecinos, actividades y contactos.' },
    { title: 'Organización de urgencias', desc: 'Aclarar vías de respuesta y responsabilidades en ambas opciones.' },
    { title: 'Costes totales', desc: 'Comparar todos los costes continuos y adicionales.' },
  ],
  serviceAreaText: 'Geis Care asesora sobre sus propios servicios domiciliarios en toda Mallorca. La disponibilidad y los detalles de residencias deben consultarse con cada proveedor.',
  faq: [
    { q: '¿Los cuidados en casa son siempre mejores?', a: 'No. Pueden ser adecuados si deseos, vivienda, necesidades y ayuda disponible encajan. Con necesidades elevadas o permanentes puede ser más seguro otro modelo.' },
    { q: '¿Cómo sé que la solución en casa ya no es suficiente?', a: 'Crisis frecuentes, periodos largos sin ayuda, caídas repetidas, agotamiento familiar o requisitos médicos que no pueden cumplirse son señales de alerta.' },
    { q: '¿Puede probarse la atención domiciliaria durante un periodo?', a: 'Sí. Un inicio definido con revisión posterior puede ayudar. Deben acordarse previamente alcance, objetivos y criterios de adaptación.' },
    { q: '¿Geis Care asesora de forma independiente sobre residencias?', a: 'Geis Care ofrece cuidados domiciliarios. Para la decisión global deben añadirse profesionales médicos, asistenciales y, si es necesario, jurídicos independientes.' },
    { q: '¿Qué debe revisarse en una residencia?', a: 'Concepto asistencial, autorización, personal, idiomas, conexión médica, costes, visitas, contrato e impresión personal.' },
  ],
  internalLinks: [
    { label: 'Cuidados a domicilio Mallorca' }, { label: 'Atención a mayores Mallorca' }, { label: 'Atención a la demencia Mallorca' },
    { label: 'Costes de la atención a mayores' }, { label: 'Apoyo después del hospital' },
  ],
  breadcrumbs: [{ label: 'Inicio' }, { label: 'Guías' }, { label: 'En casa o residencia' }],
  contactTitle: 'Valorar de forma realista las posibilidades en casa',
  contactText: 'Describa la vivienda, las necesidades y la ayuda disponible. Geis Care puede explicar sus servicios a domicilio y dónde conviene asesoramiento independiente.',
});

mergeSeoTranslation('erstgespraech-betreuungsbeginn-geiscare', 'en', {
  h1: 'From the first consultation to the start of care with Geis Care',
  metaTitle: 'First consultation and start of care with Geis Care',
  metaDescription: 'How care with Geis Care begins: free consultation, needs assessment, quote, introduction and documented start.',
  metaKeywords: 'Geis Care consultation, start care Mallorca, request home care Mallorca',
  intro: 'Good care begins with listening and a realistic needs assessment. Geis Care uses three stages: a free initial consultation, a personal needs assessment and the start of the agreed care. This page explains how to prepare and when a binding commitment is possible.',
  contentSections: [
    {
      eyebrow: 'Step 1',
      title: 'Free initial consultation',
      paragraphs: [
        'You describe the situation, the location in Mallorca and the support required. Geis Care provides initial orientation and checks whether the request generally fits the service.',
        'The consultation lasts approximately 20 to 30 minutes, by phone or in person. It is free and is not yet a commitment to a particular scope or start date.',
      ],
    },
    {
      eyebrow: 'Step 2',
      title: 'Personal needs and preferences assessment',
      paragraphs: [
        'If cooperation appears suitable, everyday life, mobility, personal care, medication, housing, appointments, relatives and communication are reviewed in more detail.',
        'Geis Care estimates around one to two hours at the home. Nursing or medical requirements may require additional documents and medical coordination.',
      ],
    },
    {
      eyebrow: 'Step 3',
      title: 'Quote, agreement and introduction',
      paragraphs: [
        'Tasks, times, costs, responsibilities and documentation can be agreed on the basis of the assessment. Read the scope carefully and ask about anything unclear.',
        'Before the start, clarify access to the home, authorised contacts and how short-notice changes are handled.',
      ],
    },
    {
      eyebrow: 'Start of care',
      title: 'Begin with documentation and review regularly',
      paragraphs: [
        'The agreed tasks are implemented and documented. Authorised relatives can be given access to digital records of measures and appointments.',
        'After the first visits, the schedule and tasks should be reviewed together. Changes in need are agreed rather than assumed.',
      ],
    },
  ],
  servicesLabel: 'Preparation',
  servicesTitle: 'What to have ready for the consultation',
  services: [
    { title: 'Location', desc: 'Address or region in Mallorca and access information.' },
    { title: 'Timing', desc: 'Preferred start, days, times and expected duration.' },
    { title: 'Support required', desc: 'List specific tasks at home, in daily life or at appointments.' },
    { title: 'Health information', desc: 'Provide relevant documents only through an agreed secure channel.' },
    { title: 'Contacts', desc: 'Name the person concerned, relatives and local professionals.' },
    { title: 'Communication', desc: 'Define updates and authorised recipients.' },
  ],
  serviceAreaText: 'The free consultation is available for care throughout Mallorca. The home visit, start date, scope and staffing are agreed according to the individual need.',
  faq: [
    { q: 'Is the first consultation really free?', a: 'Yes. The initial consultation is free and without obligation.' },
    { q: 'How long does it take?', a: 'Approximately 20 to 30 minutes. Complex situations may require additional clarification.' },
    { q: 'Must I send medical records immediately?', a: 'No. First describe the request. Sensitive health information should be sent only after an appropriate secure channel has been agreed.' },
    { q: 'When will I receive a concrete quote?', a: 'Once needs, tasks, times and location are sufficiently clear. Complex requests may require a home visit first.' },
    { q: 'How quickly can care begin?', a: 'This depends on needs, location and availability. A binding start date is confirmed only after assessment and agreement.' },
    { q: 'Can a relative abroad participate?', a: 'Yes, with the person’s consent or appropriate authorisation. Geis Care specifically supports families in the DACH region.' },
  ],
  internalLinks: [
    { label: 'Care Mallorca' }, { label: 'Senior Care Mallorca' }, { label: 'Cost of senior care' },
    { label: 'Organising care from abroad' }, { label: 'Support after hospital discharge' },
  ],
  breadcrumbs: [{ label: 'Home' }, { label: 'Guides' }, { label: 'First consultation and start of care' }],
  contactTitle: 'Request a first consultation',
  contactText: 'State the location, preferred time frame and the most important type of support. Confidential health details can then be discussed through an agreed channel.',
});

mergeSeoTranslation('erstgespraech-betreuungsbeginn-geiscare', 'es', {
  h1: 'Desde la primera consulta hasta el inicio de la atención con Geis Care',
  metaTitle: 'Primera consulta e inicio de la atención con Geis Care',
  metaDescription: 'Cómo comienza la atención con Geis Care: consulta gratuita, valoración, presupuesto, presentación e inicio documentado.',
  metaKeywords: 'consulta Geis Care, inicio cuidados Mallorca, solicitar atención domicilio Mallorca',
  intro: 'Una buena atención empieza escuchando y valorando de forma realista las necesidades. Geis Care utiliza tres etapas: consulta inicial gratuita, valoración personal e inicio de la atención acordada. Esta página explica cómo prepararse y cuándo es posible una confirmación vinculante.',
  contentSections: [
    {
      eyebrow: 'Paso 1',
      title: 'Consulta inicial gratuita',
      paragraphs: [
        'Usted describe la situación, el lugar en Mallorca y el apoyo deseado. Geis Care ofrece una primera orientación y comprueba si la solicitud encaja en general.',
        'La consulta dura unos 20 a 30 minutos, por teléfono o en persona. Es gratuita y todavía no supone confirmar un alcance o una fecha de inicio.',
      ],
    },
    {
      eyebrow: 'Paso 2',
      title: 'Valoración personal de necesidades y deseos',
      paragraphs: [
        'Si la colaboración parece adecuada, se revisan con más detalle vida diaria, movilidad, higiene, medicación, vivienda, citas, familiares y comunicación.',
        'Geis Care calcula aproximadamente una o dos horas en el domicilio. Las necesidades médicas pueden requerir documentos adicionales y coordinación con médicos.',
      ],
    },
    {
      eyebrow: 'Paso 3',
      title: 'Presupuesto, acuerdo y presentación',
      paragraphs: [
        'Sobre la base de la valoración se acuerdan tareas, horarios, costes, responsabilidades y documentación. Lea atentamente el alcance y pregunte cualquier duda.',
        'Antes de empezar deben aclararse el acceso al domicilio, los contactos autorizados y cómo se gestionan cambios con poca antelación.',
      ],
    },
    {
      eyebrow: 'Inicio de la atención',
      title: 'Empezar documentando y revisar periódicamente',
      paragraphs: [
        'Se realizan y documentan las tareas acordadas. Los familiares autorizados pueden acceder a registros digitales de medidas y citas.',
        'Después de las primeras visitas deben revisarse horarios y tareas. Los cambios de necesidad se acuerdan y no se dan por supuestos.',
      ],
    },
  ],
  servicesLabel: 'Preparación',
  servicesTitle: 'Qué puede tener preparado para la consulta',
  services: [
    { title: 'Lugar', desc: 'Dirección o región de Mallorca e información de acceso.' },
    { title: 'Horarios', desc: 'Inicio deseado, días, horas y duración prevista.' },
    { title: 'Apoyo necesario', desc: 'Anotar tareas concretas en casa, en la vida diaria o en citas.' },
    { title: 'Información médica', desc: 'Facilitar documentos solo por una vía segura acordada.' },
    { title: 'Contactos', desc: 'Indicar persona afectada, familiares y profesionales locales.' },
    { title: 'Comunicación', desc: 'Definir actualizaciones y destinatarios autorizados.' },
  ],
  serviceAreaText: 'La consulta gratuita está disponible para cuidados en toda Mallorca. La visita, el inicio, el alcance y el personal se acuerdan según la necesidad.',
  faq: [
    { q: '¿La primera consulta es realmente gratuita?', a: 'Sí. Es gratuita y sin compromiso.' },
    { q: '¿Cuánto dura?', a: 'Unos 20 a 30 minutos. Las situaciones complejas pueden requerir aclaraciones adicionales.' },
    { q: '¿Debo enviar inmediatamente documentos médicos?', a: 'No. Primero describa la solicitud. Los datos sensibles deben enviarse solo tras acordar una vía segura.' },
    { q: '¿Cuándo recibiré un presupuesto concreto?', a: 'Cuando necesidades, tareas, horarios y lugar estén suficientemente claros. Puede ser necesaria una visita previa.' },
    { q: '¿Cuándo puede empezar la atención?', a: 'Depende de las necesidades, el lugar y la disponibilidad. La fecha vinculante se confirma tras la valoración y el acuerdo.' },
    { q: '¿Puede participar un familiar desde el extranjero?', a: 'Sí, con el consentimiento o la autorización correspondiente. Geis Care se dirige especialmente a familias de la región DACH.' },
  ],
  internalLinks: [
    { label: 'Cuidados Mallorca' }, { label: 'Atención a mayores Mallorca' }, { label: 'Costes de la atención a mayores' },
    { label: 'Organizar la atención desde el extranjero' }, { label: 'Apoyo después del hospital' },
  ],
  breadcrumbs: [{ label: 'Inicio' }, { label: 'Guías' }, { label: 'Primera consulta e inicio de la atención' }],
  contactTitle: 'Solicitar una primera consulta',
  contactText: 'Indique el lugar, el periodo deseado y la forma principal de apoyo. Los datos médicos confidenciales pueden tratarse después por una vía acordada.',
});

// Per-page EN/ES translations for all SEO pages.
seoPageTranslations = { ...{

  'pflege-mallorca': {
    en: {
      h1: 'Professional Care in Mallorca',
      metaTitle: 'Care in Mallorca – German-speaking Nurses & Support | Geis Care',
      metaDescription: 'Professional German-speaking care in Mallorca: personal care, medication, wound treatment, 24h support. Qualified nurse with 15+ years experience.',
      metaKeywords: 'care Mallorca, German-speaking care Mallorca, home care Mallorca, nursing service Mallorca',
      intro: 'Geis Care provides professional, German-speaking care in Mallorca – from daily personal care to 24-hour support. Qualified nurse Tutku Geis brings 15 years of ICU and surgical nursing experience, caring for you with calm, dignity, and competence. Whether short-term or long-term care: we are your partner on the island.',
      services: [
        { title: 'Personal Care', desc: 'Bathing, showering – with dignity and respect.' },
        { title: 'Medication Management', desc: 'Safe administration and monitoring.' },
        { title: 'Wound Care', desc: 'Professional wound treatment and aftercare.' },
        { title: '24-Hour Care', desc: 'Around-the-clock nursing and safety.' },
        { title: 'Medical Accompaniment', desc: 'Reliable accompaniment to medical appointments.' },
        { title: 'Care Documentation', desc: 'Transparent digital documentation for families.' },
      ],
      serviceAreaText: 'Our care services are available throughout Mallorca – from Palma to Manacor, Cala Millor, Alcudia, Andratx, and Sant Llorenc.',
      faq: [
        { q: 'What does care in Mallorca cost?', a: 'Costs vary depending on scope and duration. In a free initial consultation, we create a personalised quote with no hidden costs.' },
        { q: 'Are your carers German-speaking?', a: 'Yes, all our carers speak fluent German. Communication in your native language is very important to us.' },
        { q: 'How quickly can you start?', a: 'In many cases, we can start within 24-48 hours. Contact us for a quick solution.' },
      ],
      internalLinks: [
        { label: 'Intensive Care Mallorca' },
        { label: 'Senior Care Mallorca' },
        { label: 'Home Care Mallorca' },
        { label: 'Dementia Care Mallorca' },
        { label: 'Care in Palma' },
        { label: 'Care in Manacor' },
      ],
      breadcrumbs: [{ label: 'Home' }, { label: 'Care Mallorca' }],
    },
    es: {
      h1: 'Cuidado Profesional en Mallorca',
      metaTitle: 'Cuidado en Mallorca – Enfermeras Germanoparlantes | Geis Care',
      metaDescription: 'Cuidado profesional germanoparlante en Mallorca: higiene personal, medicacion, cuidado de heridas, atencion 24h. Enfermera diplomada con 15+ anos de experiencia.',
      metaKeywords: 'cuidado Mallorca, cuidado germanoparlante Mallorca, cuidado domiciliario Mallorca',
      intro: 'Geis Care ofrece cuidado profesional germanoparlante en Mallorca – desde la higiene personal diaria hasta la atencion 24 horas. La enfermera diplomada Tutku Geis aporta 15 anos de experiencia en UCI y enfermeria quirurgica, cuidandole con calma, dignidad y competencia.',
      services: [
        { title: 'Higiene Personal', desc: 'Bano, ducha – con dignidad y respeto.' },
        { title: 'Gestion de Medicamentos', desc: 'Administracion y control seguros.' },
        { title: 'Cuidado de Heridas', desc: 'Tratamiento profesional y seguimiento.' },
        { title: 'Atencion 24 Horas', desc: 'Enfermeria y seguridad las 24 horas.' },
        { title: 'Acompanamiento Medico', desc: 'Acompanamiento fiable a citas medicas.' },
        { title: 'Documentacion', desc: 'Documentacion digital transparente para familias.' },
      ],
      serviceAreaText: 'Nuestros servicios estan disponibles en toda Mallorca – desde Palma hasta Manacor, Cala Millor, Alcudia, Andratx y Sant Llorenc.',
      faq: [
        { q: 'Cuanto cuesta el cuidado en Mallorca?', a: 'Los costes varian segun el alcance y la duracion. En una consulta inicial gratuita, creamos un presupuesto personalizado sin costes ocultos.' },
        { q: 'Hablan aleman sus cuidadores?', a: 'Si, todos nuestros cuidadores hablan aleman con fluidez.' },
        { q: 'Con que rapidez pueden empezar?', a: 'En muchos casos, podemos empezar en 24-48 horas. Contactenos para una solucion rapida.' },
      ],
      internalLinks: [
        { label: 'Cuidados Intensivos Mallorca' },
        { label: 'Atencion a Mayores Mallorca' },
        { label: 'Cuidado Domiciliario Mallorca' },
        { label: 'Atencion a la Demencia Mallorca' },
        { label: 'Cuidado en Palma' },
        { label: 'Cuidado en Manacor' },
      ],
      breadcrumbs: [{ label: 'Inicio' }, { label: 'Cuidado Mallorca' }],
    },
  },

  'intensivpflege-mallorca': {
    en: {
      h1: 'Intensive Care in Mallorca',
      metaTitle: 'Intensive Care Mallorca – 24h Support & Specialist Nursing | Geis Care',
      metaDescription: 'Intensive care in Mallorca: 24-hour support, post-operative care, vital sign monitoring. Qualified specialist with ICU experience.',
      metaKeywords: 'intensive care Mallorca, 24h care Mallorca, specialist nursing Mallorca',
      intro: 'Our intensive care is designed for patients requiring close monitoring. With over 15 years of ICU and surgical experience, Tutku Geis provides highly qualified care – from post-operative aftercare to permanent 24-hour support at your home in Mallorca.',
      services: [
        { title: '24h Intensive Care', desc: 'Around-the-clock monitoring and nursing.' },
        { title: 'Post-operative Care', desc: 'Professional aftercare following surgery.' },
        { title: 'Vital Sign Monitoring', desc: 'Continuous monitoring of all relevant parameters.' },
        { title: 'Specialist Nursing', desc: 'Infusion therapy, wound management, pain therapy.' },
        { title: 'Medical Coordination', desc: 'Liaison with treating doctors and clinics.' },
        { title: 'Family Support', desc: 'Comprehensive information and emotional support.' },
      ],
      serviceAreaText: 'Intensive care available in all regions of Mallorca – Palma, Manacor, Cala Millor, Alcudia, Andratx and surroundings.',
      faq: [
        { q: 'What distinguishes intensive care from standard care?', a: 'Intensive care involves closer medical monitoring, specialised nursing measures, and often 24-hour presence.' },
        { q: 'Do your carers have intensive care experience?', a: 'Yes, Tutku Geis has 15 years of ICU and surgical nursing experience at German hospitals.' },
        { q: 'Can you support after a hospital stay?', a: 'Yes, post-operative care and rehabilitation support are among our core services.' },
      ],
      internalLinks: [
        { label: 'Care Mallorca' },
        { label: 'Senior Care Mallorca' },
        { label: 'Home Care Mallorca' },
        { label: 'Doctors in Mallorca' },
        { label: 'Clinics in Mallorca' },
      ],
      breadcrumbs: [{ label: 'Home' }, { label: 'Intensive Care Mallorca' }],
    },
    es: {
      h1: 'Cuidados Intensivos en Mallorca',
      metaTitle: 'Cuidados Intensivos Mallorca – Atencion 24h y Enfermeria Especializada | Geis Care',
      metaDescription: 'Cuidados intensivos en Mallorca: atencion 24 horas, cuidado postoperatorio, monitorizacion de signos vitales. Especialista con experiencia en UCI.',
      metaKeywords: 'cuidados intensivos Mallorca, atencion 24h Mallorca, enfermeria especializada Mallorca',
      intro: 'Nuestros cuidados intensivos estan disenados para pacientes que necesitan monitorizacion estrecha. Con mas de 15 anos de experiencia en UCI, Tutku Geis ofrece atencion altamente cualificada en su hogar en Mallorca.',
      services: [
        { title: 'Cuidado Intensivo 24h', desc: 'Monitorizacion y enfermeria las 24 horas.' },
        { title: 'Cuidado Postoperatorio', desc: 'Seguimiento profesional tras cirugias.' },
        { title: 'Monitorizacion Vital', desc: 'Control continuo de todos los parametros relevantes.' },
        { title: 'Enfermeria Especializada', desc: 'Terapia de infusion, manejo de heridas, terapia del dolor.' },
        { title: 'Coordinacion Medica', desc: 'Coordinacion con medicos y clinicas tratantes.' },
        { title: 'Apoyo Familiar', desc: 'Informacion completa y apoyo emocional.' },
      ],
      serviceAreaText: 'Cuidados intensivos disponibles en todas las regiones de Mallorca.',
      faq: [
        { q: 'Que diferencia los cuidados intensivos del cuidado estandar?', a: 'Los cuidados intensivos implican monitorizacion medica mas estrecha, medidas de enfermeria especializadas y a menudo presencia 24 horas.' },
        { q: 'Tienen sus cuidadores experiencia en cuidados intensivos?', a: 'Si, Tutku Geis tiene 15 anos de experiencia en UCI y enfermeria quirurgica.' },
        { q: 'Pueden apoyar despues de una estancia hospitalaria?', a: 'Si, el cuidado postoperatorio es una de nuestras competencias principales.' },
      ],
      internalLinks: [
        { label: 'Cuidado Mallorca' },
        { label: 'Atencion a Mayores Mallorca' },
        { label: 'Cuidado Domiciliario Mallorca' },
        { label: 'Medicos en Mallorca' },
        { label: 'Clinicas en Mallorca' },
      ],
      breadcrumbs: [{ label: 'Inicio' }, { label: 'Cuidados Intensivos Mallorca' }],
    },
  },

  'seniorenbetreuung-mallorca': {
    en: {
      h1: 'Senior Care in Mallorca',
      metaTitle: 'Senior Care Mallorca – German-speaking Daily Assistance | Geis Care',
      metaDescription: 'Senior care in Mallorca: daily assistance, medical accompaniment, companionship, shopping support. German-speaking, caring, professional.',
      metaKeywords: 'senior care Mallorca, elderly care Mallorca, daily assistance Mallorca',
      intro: 'Our senior care enables older people to live independently in familiar surroundings. Whether daily assistance, medical accompaniment, companionship, or shopping support – our German-speaking team supports you with heart and professionalism.',
      services: [
        { title: 'Daily Companionship', desc: 'Support with daily routines and tasks.' },
        { title: 'Medical Accompaniment', desc: 'Reliable accompaniment to medical appointments.' },
        { title: 'Medication Management', desc: 'Structured administration and monitoring.' },
        { title: 'Shopping Assistance', desc: 'Joint errands and shopping trips.' },
        { title: 'Social Companionship', desc: 'Conversations, shared activities, social contacts.' },
        { title: 'Walks', desc: 'Accompanied outings in the fresh air.' },
      ],
      serviceAreaText: 'Senior care throughout Mallorca: Palma, Manacor, Cala Millor, Inca, Alcudia, Andratx, Sant Llorenc and more.',
      faq: [
        { q: 'From what age do you offer senior care?', a: 'There is no fixed age limit. We care for anyone who needs support in daily life.' },
        { q: 'Can care be booked by the hour?', a: 'Yes, we offer flexible models – from individual hours to day care to 24-hour support.' },
        { q: 'Do your carers also speak Spanish?', a: 'Yes, our team is multilingual and can translate at medical appointments.' },
      ],
      internalLinks: [
        { label: 'Care Mallorca' },
        { label: 'Home Care Mallorca' },
        { label: 'Dementia Care Mallorca' },
        { label: 'Care in Palma' },
        { label: 'Care in Cala Millor' },
        { label: 'Care in Alcudia' },
      ],
      breadcrumbs: [{ label: 'Home' }, { label: 'Senior Care Mallorca' }],
    },
    es: {
      h1: 'Atencion a Mayores en Mallorca',
      metaTitle: 'Atencion a Mayores Mallorca – Ayuda Diaria Germanoparlante | Geis Care',
      metaDescription: 'Atencion a mayores en Mallorca: ayuda diaria, acompanamiento medico, compania, acompanamiento de compras. Germanoparlante y profesional.',
      metaKeywords: 'atencion mayores Mallorca, cuidado ancianos Mallorca, ayuda diaria Mallorca',
      intro: 'Nuestra atencion a mayores permite a las personas mayores vivir de forma independiente en un entorno familiar. Nuestro equipo germanoparlante le apoya con corazon y profesionalismo.',
      services: [
        { title: 'Acompanamiento Diario', desc: 'Apoyo en rutinas y tareas diarias.' },
        { title: 'Acompanamiento Medico', desc: 'Acompanamiento fiable a citas medicas.' },
        { title: 'Gestion de Medicamentos', desc: 'Administracion estructurada y control.' },
        { title: 'Acompanamiento de Compras', desc: 'Recados y compras conjuntas.' },
        { title: 'Compania Social', desc: 'Conversaciones, actividades compartidas, contactos sociales.' },
        { title: 'Paseos', desc: 'Salidas acompanadas al aire libre.' },
      ],
      serviceAreaText: 'Atencion a mayores en toda Mallorca: Palma, Manacor, Cala Millor, Inca, Alcudia, Andratx y mas.',
      faq: [
        { q: 'A partir de que edad ofrecen atencion a mayores?', a: 'No hay limite de edad fijo. Atendemos a todas las personas que necesitan apoyo en la vida diaria.' },
        { q: 'Se puede reservar por horas?', a: 'Si, ofrecemos modelos flexibles – desde horas individuales hasta atencion 24 horas.' },
        { q: 'Hablan tambien espanol sus cuidadores?', a: 'Si, nuestro equipo es multilingue y puede traducir en citas medicas.' },
      ],
      internalLinks: [
        { label: 'Cuidado Mallorca' },
        { label: 'Cuidado Domiciliario Mallorca' },
        { label: 'Atencion a la Demencia Mallorca' },
        { label: 'Cuidado en Palma' },
        { label: 'Cuidado en Cala Millor' },
        { label: 'Cuidado en Alcudia' },
      ],
      breadcrumbs: [{ label: 'Inicio' }, { label: 'Atencion a Mayores Mallorca' }],
    },
  },

  'hauspflege-mallorca': {
    en: {
      h1: 'Home Care in Mallorca',
      metaTitle: 'Home Care Mallorca – Nursing & Support at Your Home | Geis Care',
      metaDescription: 'Home care in Mallorca: personal care, medical nursing, household help directly at your home. German-speaking and professional.',
      metaKeywords: 'home care Mallorca, home nursing Mallorca, care at home Mallorca',
      intro: 'With Geis Care home nursing, you receive professional care directly in your home. Our qualified nurse comes to you – whether for daily personal care, medical nursing, or household support. Discreet, reliable, and tailored to your needs.',
      services: [
        { title: 'Care at Home', desc: 'Professional care in your familiar surroundings.' },
        { title: 'Personal Care', desc: 'Hygiene, dressing, mobilisation.' },
        { title: 'Medical Nursing', desc: 'Medical measures as prescribed by doctors.' },
        { title: 'Household Help', desc: 'Support with cooking, cleaning, laundry.' },
        { title: 'Emergency Availability', desc: 'Reachable outside regular hours.' },
        { title: 'Digital Documentation', desc: 'Real-time insight for family members.' },
      ],
      serviceAreaText: 'Home care in all regions of Mallorca: Palma, Manacor, Inca, Cala Millor, Alcudia, Andratx, Sant Llorenc.',
      faq: [
        { q: 'How often does the carer come to my home?', a: 'Frequency depends on your needs – from daily visits to 24-hour care.' },
        { q: 'Can I adjust care times flexibly?', a: 'Yes, we adapt schedules to your needs and daily rhythm at any time.' },
        { q: 'What is the difference between personal and medical care?', a: 'Personal care includes daily help like hygiene. Medical care includes measures like wound treatment.' },
      ],
      internalLinks: [
        { label: 'Care Mallorca' },
        { label: 'Senior Care Mallorca' },
        { label: 'Intensive Care Mallorca' },
        { label: 'Care in Manacor' },
        { label: 'Care in Inca' },
      ],
      breadcrumbs: [{ label: 'Home' }, { label: 'Home Care Mallorca' }],
    },
    es: {
      h1: 'Cuidado Domiciliario en Mallorca',
      metaTitle: 'Cuidado Domiciliario Mallorca – Enfermeria y Atencion en Casa | Geis Care',
      metaDescription: 'Cuidado domiciliario en Mallorca: higiene personal, enfermeria medica, ayuda domestica directamente en su hogar. Germanoparlante y profesional.',
      metaKeywords: 'cuidado domiciliario Mallorca, enfermeria domiciliaria Mallorca, cuidado en casa Mallorca',
      intro: 'Con el cuidado domiciliario de Geis Care, recibe atencion profesional directamente en su hogar. Nuestra enfermera diplomada viene a usted – discreto, fiable y adaptado a sus necesidades.',
      services: [
        { title: 'Cuidado en Casa', desc: 'Atencion profesional en su entorno familiar.' },
        { title: 'Higiene Personal', desc: 'Aseo, vestido, movilizacion.' },
        { title: 'Enfermeria Medica', desc: 'Medidas medicas segun prescripcion.' },
        { title: 'Ayuda Domestica', desc: 'Apoyo con cocina, limpieza, lavanderia.' },
        { title: 'Disponibilidad de Emergencia', desc: 'Accesible fuera del horario regular.' },
        { title: 'Documentacion Digital', desc: 'Informacion en tiempo real para familiares.' },
      ],
      serviceAreaText: 'Cuidado domiciliario en todas las regiones de Mallorca.',
      faq: [
        { q: 'Con que frecuencia viene el cuidador a casa?', a: 'La frecuencia depende de sus necesidades – desde visitas diarias hasta atencion 24 horas.' },
        { q: 'Puedo ajustar los horarios de forma flexible?', a: 'Si, adaptamos los horarios a sus necesidades en cualquier momento.' },
        { q: 'Cual es la diferencia entre cuidado personal y medico?', a: 'El cuidado personal incluye ayuda diaria como la higiene. El cuidado medico incluye medidas como el tratamiento de heridas.' },
      ],
      internalLinks: [
        { label: 'Cuidado Mallorca' },
        { label: 'Atencion a Mayores Mallorca' },
        { label: 'Cuidados Intensivos Mallorca' },
        { label: 'Cuidado en Manacor' },
        { label: 'Cuidado en Inca' },
      ],
      breadcrumbs: [{ label: 'Inicio' }, { label: 'Cuidado Domiciliario Mallorca' }],
    },
  },

  'demenzbetreuung-mallorca': {
    en: {
      h1: 'Dementia Care in Mallorca',
      metaTitle: 'Dementia Care Mallorca – Compassionate Support | Geis Care',
      metaDescription: 'Dementia care in Mallorca: compassionate support, activation, safety, and family relief. German-speaking and experienced.',
      metaKeywords: 'dementia care Mallorca, Alzheimer care Mallorca, dementia support Mallorca',
      intro: 'Geis Care offers compassionate, professional support for people with dementia and Alzheimer\'s in Mallorca. Our trained carers provide safety, activation, and dignity in daily life – while relieving family members.',
      services: [
        { title: 'Cognitive Activation', desc: 'Memory training and mental stimulation.' },
        { title: 'Daily Safety', desc: 'Fall prevention and orientation support.' },
        { title: 'Activity Therapy', desc: 'Creative activities and music therapy.' },
        { title: 'Compassionate Support', desc: 'Patience, understanding, and emotional closeness.' },
        { title: 'Family Relief', desc: 'Regular breaks for caring family members.' },
        { title: 'Behaviour Management', desc: 'Professional handling of challenging behaviour.' },
      ],
      serviceAreaText: 'Dementia care available throughout Mallorca: Palma, Manacor, Cala Millor, Inca, Alcudia, Andratx.',
      faq: [
        { q: 'From which stage do you offer dementia care?', a: 'We support people at all stages – from mild cognitive impairment to severe dementia.' },
        { q: 'How do your carers handle challenging behaviour?', a: 'Our carers are specially trained. We use validation and empathetic communication.' },
        { q: 'Can you also provide night care?', a: 'Yes, we offer night care and 24-hour support for dementia patients.' },
      ],
      internalLinks: [
        { label: 'Care Mallorca' },
        { label: 'Senior Care Mallorca' },
        { label: 'Intensive Care Mallorca' },
        { label: 'Doctors in Mallorca' },
        { label: 'Care in Palma' },
      ],
      breadcrumbs: [{ label: 'Home' }, { label: 'Dementia Care Mallorca' }],
    },
    es: {
      h1: 'Atencion a la Demencia en Mallorca',
      metaTitle: 'Atencion a la Demencia Mallorca – Apoyo Compasivo | Geis Care',
      metaDescription: 'Atencion a la demencia en Mallorca: apoyo compasivo, activacion, seguridad y alivio familiar. Germanoparlante y con experiencia.',
      metaKeywords: 'atencion demencia Mallorca, cuidado Alzheimer Mallorca',
      intro: 'Geis Care ofrece apoyo compasivo y profesional para personas con demencia y Alzheimer en Mallorca. Nuestros cuidadores formados proporcionan seguridad, activacion y dignidad en la vida diaria.',
      services: [
        { title: 'Activacion Cognitiva', desc: 'Entrenamiento de memoria y estimulacion mental.' },
        { title: 'Seguridad Diaria', desc: 'Prevencion de caidas y apoyo de orientacion.' },
        { title: 'Terapia de Actividades', desc: 'Actividades creativas y musicoterapia.' },
        { title: 'Apoyo Compasivo', desc: 'Paciencia, comprension y cercania emocional.' },
        { title: 'Alivio Familiar', desc: 'Descansos regulares para familiares cuidadores.' },
        { title: 'Manejo del Comportamiento', desc: 'Manejo profesional de comportamiento desafiante.' },
      ],
      serviceAreaText: 'Atencion a la demencia disponible en toda Mallorca.',
      faq: [
        { q: 'A partir de que etapa ofrecen atencion a la demencia?', a: 'Apoyamos a personas en todas las etapas – desde deterioro cognitivo leve hasta demencia severa.' },
        { q: 'Como manejan sus cuidadores el comportamiento desafiante?', a: 'Nuestros cuidadores estan especialmente formados. Usamos validacion y comunicacion empatica.' },
        { q: 'Pueden proporcionar atencion nocturna?', a: 'Si, ofrecemos atencion nocturna y apoyo 24 horas para pacientes con demencia.' },
      ],
      internalLinks: [
        { label: 'Cuidado Mallorca' },
        { label: 'Atencion a Mayores Mallorca' },
        { label: 'Cuidados Intensivos Mallorca' },
        { label: 'Medicos en Mallorca' },
        { label: 'Cuidado en Palma' },
      ],
      breadcrumbs: [{ label: 'Inicio' }, { label: 'Atencion a la Demencia Mallorca' }],
    },
  },

  'aerzte-mallorca': {
    en: {
      h1: 'German-speaking Doctors in Mallorca',
      metaTitle: 'Doctors in Mallorca – German-speaking Doctors & Specialists | Geis Care',
      metaDescription: 'German-speaking doctors in Mallorca: specialists, GPs, clinics. Overview and accompaniment to appointments by Geis Care.',
      metaKeywords: 'doctors Mallorca, German-speaking doctors Mallorca, specialist Mallorca',
      intro: 'Mallorca offers an excellent network of German-speaking doctors and specialists. Geis Care works closely with these experts and reliably accompanies you to all appointments – whether general medicine, cardiology, orthopaedics, or neurology.',
      services: [
        { title: 'Medical Accompaniment', desc: 'Reliable accompaniment to all medical appointments.' },
        { title: 'Translation Support', desc: 'Language support with Spanish-speaking doctors.' },
        { title: 'Report Documentation', desc: 'Written summary of all doctor visits.' },
        { title: 'Prescription Management', desc: 'Organisation and collection of prescriptions.' },
        { title: 'Appointment Coordination', desc: 'Scheduling and managing all medical appointments.' },
        { title: 'Doctor Network', desc: 'Referral to specialised physicians.' },
      ],
      serviceAreaText: 'Medical accompaniment throughout Mallorca: Palma, Manacor, Inca, Alcudia, Andratx and all other regions.',
      faq: [
        { q: 'Are there many German-speaking doctors in Mallorca?', a: 'Yes, especially in Palma and tourist regions there are numerous German-speaking GPs and specialists.' },
        { q: 'Can you accompany me to the doctor?', a: 'Yes, medical accompaniment is one of our core services. We accompany, translate, and document all findings.' },
        { q: 'How do I find the right specialist?', a: 'We have an extensive network and recommend the right specialist for you.' },
      ],
      internalLinks: [
        { label: 'Clinics in Mallorca' },
        { label: 'Emergency Numbers Mallorca' },
        { label: 'Healthcare System Mallorca' },
        { label: 'Care Mallorca' },
        { label: 'Care in Palma' },
      ],
      breadcrumbs: [{ label: 'Home' }, { label: 'Doctors Mallorca' }],
    },
    es: {
      h1: 'Medicos Germanoparlantes en Mallorca',
      metaTitle: 'Medicos en Mallorca – Medicos y Especialistas Germanoparlantes | Geis Care',
      metaDescription: 'Medicos germanoparlantes en Mallorca: especialistas, medicos generales, clinicas. Resumen y acompanamiento a citas por Geis Care.',
      metaKeywords: 'medicos Mallorca, medicos germanoparlantes Mallorca, especialista Mallorca',
      intro: 'Mallorca ofrece una excelente red de medicos y especialistas germanoparlantes. Geis Care trabaja estrechamente con estos expertos y le acompana de forma fiable a todas las citas.',
      services: [
        { title: 'Acompanamiento Medico', desc: 'Acompanamiento fiable a todas las citas medicas.' },
        { title: 'Apoyo de Traduccion', desc: 'Apoyo linguistico con medicos hispanoparlantes.' },
        { title: 'Documentacion de Informes', desc: 'Resumen escrito de todas las visitas medicas.' },
        { title: 'Gestion de Recetas', desc: 'Organizacion y recogida de recetas.' },
        { title: 'Coordinacion de Citas', desc: 'Programacion y gestion de todas las citas medicas.' },
        { title: 'Red de Medicos', desc: 'Derivacion a medicos especializados.' },
      ],
      serviceAreaText: 'Acompanamiento medico en toda Mallorca.',
      faq: [
        { q: 'Hay muchos medicos germanoparlantes en Mallorca?', a: 'Si, especialmente en Palma y las regiones turisticas hay numerosos medicos germanoparlantes.' },
        { q: 'Pueden acompanarme al medico?', a: 'Si, el acompanamiento medico es uno de nuestros servicios principales.' },
        { q: 'Como encuentro al especialista adecuado?', a: 'Tenemos una amplia red y le recomendamos el especialista adecuado.' },
      ],
      internalLinks: [
        { label: 'Clinicas en Mallorca' },
        { label: 'Numeros de Emergencia Mallorca' },
        { label: 'Sistema Sanitario Mallorca' },
        { label: 'Cuidado Mallorca' },
        { label: 'Cuidado en Palma' },
      ],
      breadcrumbs: [{ label: 'Inicio' }, { label: 'Medicos Mallorca' }],
    },
  },

  'kliniken-mallorca': {
    en: {
      h1: 'Clinics and Hospitals in Mallorca',
      metaTitle: 'Clinics in Mallorca – Hospitals & Private Clinics | Geis Care',
      metaDescription: 'Overview of the most important clinics and hospitals in Mallorca. Geis Care accompanies you before, during, and after hospital stays.',
      metaKeywords: 'clinics Mallorca, hospital Mallorca, private clinic Mallorca',
      intro: 'Mallorca has first-class clinics and hospitals. Geis Care knows the most important facilities and competently accompanies you before, during, and after a hospital stay – from appointment scheduling to language support to aftercare at home.',
      services: [
        { title: 'Hospital Accompaniment', desc: 'Accompaniment during inpatient stays.' },
        { title: 'Admission Support', desc: 'Help with forms and formalities.' },
        { title: 'Language Support', desc: 'Translation and communication with hospital staff.' },
        { title: 'Home Aftercare', desc: 'Professional care after hospital discharge.' },
        { title: 'Discharge Management', desc: 'Organisation of transition from hospital to home.' },
        { title: 'Medication Transition', desc: 'Safe implementation of hospital medication at home.' },
      ],
      serviceAreaText: 'Hospital accompaniment throughout Mallorca: we accompany you to all hospitals and clinics on the island.',
      faq: [
        { q: 'Which clinics in Mallorca do you recommend?', a: 'Depending on the specialty, we recommend various facilities – from Son Espases to Quironsalud to Policlinica Miramar.' },
        { q: 'Can you visit me in hospital?', a: 'Yes, we offer accompaniment and care services during inpatient stays.' },
        { q: 'Do you also help with aftercare?', a: 'Yes, post-operative aftercare at home is one of our core competencies.' },
      ],
      internalLinks: [
        { label: 'Doctors in Mallorca' },
        { label: 'Emergency Numbers Mallorca' },
        { label: 'Healthcare System Mallorca' },
        { label: 'Intensive Care Mallorca' },
        { label: 'Care Mallorca' },
      ],
      breadcrumbs: [{ label: 'Home' }, { label: 'Clinics Mallorca' }],
    },
    es: {
      h1: 'Clinicas y Hospitales en Mallorca',
      metaTitle: 'Clinicas en Mallorca – Hospitales y Clinicas Privadas | Geis Care',
      metaDescription: 'Resumen de las clinicas y hospitales mas importantes de Mallorca. Geis Care le acompana antes, durante y despues de estancias hospitalarias.',
      metaKeywords: 'clinicas Mallorca, hospital Mallorca, clinica privada Mallorca',
      intro: 'Mallorca cuenta con clinicas y hospitales de primera clase. Geis Care conoce las instalaciones mas importantes y le acompana competentemente antes, durante y despues de una estancia hospitalaria.',
      services: [
        { title: 'Acompanamiento Hospitalario', desc: 'Acompanamiento durante estancias hospitalarias.' },
        { title: 'Apoyo en Admision', desc: 'Ayuda con formularios y formalidades.' },
        { title: 'Apoyo Linguistico', desc: 'Traduccion y comunicacion con el personal hospitalario.' },
        { title: 'Cuidado Post-alta', desc: 'Atencion profesional tras el alta hospitalaria.' },
        { title: 'Gestion del Alta', desc: 'Organizacion de la transicion del hospital al hogar.' },
        { title: 'Transicion de Medicacion', desc: 'Implementacion segura de la medicacion hospitalaria en casa.' },
      ],
      serviceAreaText: 'Acompanamiento hospitalario en toda Mallorca.',
      faq: [
        { q: 'Que clinicas en Mallorca recomiendan?', a: 'Segun la especialidad, recomendamos diferentes centros – desde Son Espases hasta Quironsalud y Policlinica Miramar.' },
        { q: 'Pueden visitarme en el hospital?', a: 'Si, ofrecemos servicios de acompanamiento y atencion durante estancias hospitalarias.' },
        { q: 'Ayudan tambien con el cuidado posterior?', a: 'Si, el cuidado postoperatorio en casa es una de nuestras competencias principales.' },
      ],
      internalLinks: [
        { label: 'Medicos en Mallorca' },
        { label: 'Numeros de Emergencia Mallorca' },
        { label: 'Sistema Sanitario Mallorca' },
        { label: 'Cuidados Intensivos Mallorca' },
        { label: 'Cuidado Mallorca' },
      ],
      breadcrumbs: [{ label: 'Inicio' }, { label: 'Clinicas Mallorca' }],
    },
  },

  'notfallnummern-mallorca': {
    en: {
      h1: 'Important Emergency Numbers in Mallorca',
      metaTitle: 'Emergency Numbers Mallorca – Important Numbers for Emergencies | Geis Care',
      metaDescription: 'All important emergency numbers for Mallorca: emergency 112, ambulance 061, police, fire brigade, poison control.',
      metaKeywords: 'emergency numbers Mallorca, emergency call Mallorca, ambulance Mallorca',
      intro: 'In an emergency, every second counts. Here you will find all important emergency numbers for Mallorca at a glance. Geis Care recommends: save these numbers in your phone. As your care partner, we are also available in emergencies.',
      services: [
        { title: 'General Emergency: 112', desc: 'Europe-wide emergency number – police, fire, rescue.' },
        { title: 'Ambulance: 061', desc: 'Balearic medical emergency service (SAMU).' },
        { title: 'Police: 091 / 092', desc: 'National Police (091) and Local Police (092).' },
        { title: 'Fire Brigade: 080', desc: 'Bomberos de Mallorca.' },
        { title: 'Poison Control: +34 91 562 04 20', desc: 'Instituto Nacional de Toxicologia.' },
        { title: 'Geis Care: +34 601 889 566', desc: 'Your personal care contact in Mallorca.' },
      ],
      serviceAreaText: 'These emergency numbers apply throughout Mallorca and the Balearic Islands.',
      faq: [
        { q: 'Which number do I call in an emergency in Mallorca?', a: '112 is the general emergency number and works throughout Europe. For medical emergencies, you can also call 061.' },
        { q: 'Is there German-speaking emergency help?', a: 'The 112 control centre often offers multilingual support. Geis Care is also available as a personal contact.' },
        { q: 'What do I do in a medical emergency?', a: 'Call 112 or 061, stay calm, and follow instructions. Then inform Geis Care.' },
      ],
      internalLinks: [
        { label: 'Doctors in Mallorca' },
        { label: 'Clinics in Mallorca' },
        { label: 'Healthcare System Mallorca' },
        { label: 'Care Mallorca' },
        { label: 'Intensive Care Mallorca' },
      ],
      breadcrumbs: [{ label: 'Home' }, { label: 'Emergency Numbers Mallorca' }],
    },
    es: {
      h1: 'Numeros de Emergencia Importantes en Mallorca',
      metaTitle: 'Numeros de Emergencia Mallorca – Numeros Importantes | Geis Care',
      metaDescription: 'Todos los numeros de emergencia importantes para Mallorca: emergencias 112, ambulancia 061, policia, bomberos.',
      metaKeywords: 'numeros emergencia Mallorca, emergencias Mallorca, ambulancia Mallorca',
      intro: 'En una emergencia, cada segundo cuenta. Aqui encontrara todos los numeros de emergencia importantes para Mallorca de un vistazo.',
      services: [
        { title: 'Emergencias General: 112', desc: 'Numero de emergencia europeo – policia, bomberos, rescate.' },
        { title: 'Ambulancia: 061', desc: 'Servicio de emergencias medicas de Baleares (SAMU).' },
        { title: 'Policia: 091 / 092', desc: 'Policia Nacional (091) y Policia Local (092).' },
        { title: 'Bomberos: 080', desc: 'Bomberos de Mallorca.' },
        { title: 'Control de Venenos: +34 91 562 04 20', desc: 'Instituto Nacional de Toxicologia.' },
        { title: 'Geis Care: +34 601 889 566', desc: 'Su contacto personal de cuidado en Mallorca.' },
      ],
      serviceAreaText: 'Estos numeros de emergencia son validos en toda Mallorca y las Islas Baleares.',
      faq: [
        { q: 'Que numero llamo en una emergencia en Mallorca?', a: 'El 112 es el numero de emergencia general y funciona en toda Europa. Para emergencias medicas, tambien puede llamar al 061.' },
        { q: 'Hay ayuda de emergencia en aleman?', a: 'La central del 112 a menudo ofrece soporte multilingue. Geis Care tambien esta disponible como contacto personal.' },
        { q: 'Que hago en una emergencia medica?', a: 'Llame al 112 o 061, mantenga la calma y siga las instrucciones. Luego informe a Geis Care.' },
      ],
      internalLinks: [
        { label: 'Medicos en Mallorca' },
        { label: 'Clinicas en Mallorca' },
        { label: 'Sistema Sanitario Mallorca' },
        { label: 'Cuidado Mallorca' },
        { label: 'Cuidados Intensivos Mallorca' },
      ],
      breadcrumbs: [{ label: 'Inicio' }, { label: 'Numeros de Emergencia Mallorca' }],
    },
  },

  'gesundheitssystem-mallorca': {
    en: {
      h1: 'The Healthcare System in Mallorca',
      metaTitle: 'Healthcare System Mallorca – Guide for German Residents | Geis Care',
      metaDescription: 'The healthcare system in Mallorca: public vs. private, Tarjeta Sanitaria, EHIC. Your guide to medical care as a resident.',
      metaKeywords: 'healthcare system Mallorca, health insurance Mallorca, Tarjeta Sanitaria, medical care Mallorca',
      intro: 'The Spanish healthcare system offers public and private care at a high level. For German residents, orientation can initially be challenging. Geis Care explains the key differences, helps with the Tarjeta Sanitaria, and guides you through the system.',
      services: [
        { title: 'Public System (IB-Salut)', desc: 'Access via Tarjeta Sanitaria for all insured persons.' },
        { title: 'Private System', desc: 'Faster appointments, more comfort, free choice of doctor.' },
        { title: 'Tarjeta Sanitaria', desc: 'Application and use of the health card.' },
        { title: 'EHIC / European Card', desc: 'Use of the European Health Insurance Card.' },
        { title: 'Insurance Advice', desc: 'Guidance on private supplementary insurance.' },
        { title: 'Language Support', desc: 'Accompaniment to authorities and applications.' },
      ],
      serviceAreaText: 'Our healthcare system advice and accompaniment applies throughout Mallorca.',
      faq: [
        { q: 'Do I need private health insurance in Mallorca?', a: 'As an EU citizen, you have access to the public system. Private supplementary insurance offers faster appointments and more comfort.' },
        { q: 'What is the Tarjeta Sanitaria?', a: 'The Spanish health card that gives access to the public healthcare system. We help with the application.' },
        { q: 'Can I see a doctor with my German insurance?', a: 'With the EHIC, you are entitled to care in the public system. For private doctors, you need international health insurance.' },
      ],
      internalLinks: [
        { label: 'Doctors in Mallorca' },
        { label: 'Clinics in Mallorca' },
        { label: 'Emergency Numbers Mallorca' },
        { label: 'Care Mallorca' },
        { label: 'Senior Care Mallorca' },
      ],
      breadcrumbs: [{ label: 'Home' }, { label: 'Healthcare System Mallorca' }],
    },
    es: {
      h1: 'El Sistema Sanitario en Mallorca',
      metaTitle: 'Sistema Sanitario Mallorca – Guia para Residentes Alemanes | Geis Care',
      metaDescription: 'El sistema sanitario en Mallorca: publico vs. privado, Tarjeta Sanitaria, TSE. Su guia para la atencion medica como residente.',
      metaKeywords: 'sistema sanitario Mallorca, seguro medico Mallorca, Tarjeta Sanitaria',
      intro: 'El sistema sanitario espanol ofrece atencion publica y privada de alto nivel. Para residentes alemanes, la orientacion puede ser un reto al principio. Geis Care explica las diferencias clave y le guia a traves del sistema.',
      services: [
        { title: 'Sistema Publico (IB-Salut)', desc: 'Acceso mediante Tarjeta Sanitaria para todos los asegurados.' },
        { title: 'Sistema Privado', desc: 'Citas mas rapidas, mas comodidad, libre eleccion de medico.' },
        { title: 'Tarjeta Sanitaria', desc: 'Solicitud y uso de la tarjeta sanitaria.' },
        { title: 'TSE / Tarjeta Europea', desc: 'Uso de la Tarjeta Sanitaria Europea.' },
        { title: 'Asesoramiento de Seguros', desc: 'Orientacion sobre seguros complementarios privados.' },
        { title: 'Apoyo Linguistico', desc: 'Acompanamiento a gestiones y solicitudes.' },
      ],
      serviceAreaText: 'Nuestro asesoramiento sobre el sistema sanitario es valido para toda Mallorca.',
      faq: [
        { q: 'Necesito un seguro medico privado en Mallorca?', a: 'Como ciudadano de la UE, tiene acceso al sistema publico. Un seguro complementario privado ofrece citas mas rapidas y mas comodidad.' },
        { q: 'Que es la Tarjeta Sanitaria?', a: 'La tarjeta sanitaria espanola que da acceso al sistema sanitario publico. Le ayudamos con la solicitud.' },
        { q: 'Puedo ir al medico con mi seguro aleman?', a: 'Con la TSE, tiene derecho a atencion en el sistema publico. Para medicos privados, necesita un seguro de salud internacional.' },
      ],
      internalLinks: [
        { label: 'Medicos en Mallorca' },
        { label: 'Clinicas en Mallorca' },
        { label: 'Numeros de Emergencia Mallorca' },
        { label: 'Cuidado Mallorca' },
        { label: 'Atencion a Mayores Mallorca' },
      ],
      breadcrumbs: [{ label: 'Inicio' }, { label: 'Sistema Sanitario Mallorca' }],
    },
  },

  // ===== LOCATION PAGES =====

  'pflege-palma': {
    en: {
      h1: 'Care in Palma de Mallorca',
      metaTitle: 'Care in Palma de Mallorca – German-speaking Support | Geis Care',
      metaDescription: 'Professional German-speaking care in Palma de Mallorca: personal care, senior support, medical accompaniment, 24h care.',
      metaKeywords: 'care Palma, care Palma de Mallorca, senior care Palma, home care Palma',
      intro: 'In the island capital Palma, Geis Care offers professional, German-speaking care and support. Whether old town, Son Vida, Portixol, or suburbs – our qualified nurse comes directly to you. Fast and reliable.',
      services: [
        { title: 'Home Care', desc: 'Professional care at home throughout Palma.' },
        { title: 'Medical Accompaniment', desc: 'Accompaniment to doctors and clinics in Palma.' },
        { title: 'Medication Management', desc: 'Reliable medication administration.' },
        { title: 'Daily Accompaniment', desc: 'Shopping, errands, social activities.' },
        { title: '24h Care', desc: 'Around-the-clock care in Palma.' },
        { title: 'Senior Care', desc: 'Caring companionship for older people.' },
      ],
      serviceAreaText: 'Service area in Palma: old town, Son Vida, Portixol, Santa Catalina, El Terreno, Genova, Son Espanyolet and all districts.',
      faq: [
        { q: 'In which districts of Palma are you active?', a: 'Throughout Palma – from the old town to Son Vida and Portixol to the suburbs.' },
        { q: 'How quickly can you be on site in Palma?', a: 'In Palma, we can usually be on site within a few hours.' },
        { q: 'Do you work with clinics in Palma?', a: 'Yes, we work closely with Son Espases, Quironsalud, Policlinica Miramar, and other clinics.' },
      ],
      internalLinks: [
        { label: 'Care Mallorca' },
        { label: 'Care in Manacor' },
        { label: 'Care in Andratx' },
        { label: 'Doctors in Mallorca' },
        { label: 'Clinics in Mallorca' },
        { label: 'Senior Care Mallorca' },
      ],
      breadcrumbs: [{ label: 'Home' }, { label: 'Care Mallorca' }, { label: 'Care Palma' }],
    },
    es: {
      h1: 'Cuidado en Palma de Mallorca',
      metaTitle: 'Cuidado en Palma de Mallorca – Atencion Germanoparlante | Geis Care',
      metaDescription: 'Cuidado profesional germanoparlante en Palma de Mallorca: higiene personal, atencion a mayores, acompanamiento medico, atencion 24h.',
      metaKeywords: 'cuidado Palma, cuidado Palma de Mallorca, atencion mayores Palma',
      intro: 'En la capital de la isla, Palma, Geis Care ofrece cuidado profesional germanoparlante. Ya sea casco antiguo, Son Vida, Portixol o suburbios – nuestra enfermera diplomada viene directamente a usted.',
      services: [
        { title: 'Cuidado Domiciliario', desc: 'Atencion profesional en casa en todo Palma.' },
        { title: 'Acompanamiento Medico', desc: 'Acompanamiento a medicos y clinicas en Palma.' },
        { title: 'Gestion de Medicamentos', desc: 'Administracion fiable de medicamentos.' },
        { title: 'Acompanamiento Diario', desc: 'Compras, gestiones, actividades sociales.' },
        { title: 'Atencion 24h', desc: 'Cuidado las 24 horas en Palma.' },
        { title: 'Atencion a Mayores', desc: 'Acompanamiento carinoso para personas mayores.' },
      ],
      serviceAreaText: 'Area de servicio en Palma: casco antiguo, Son Vida, Portixol, Santa Catalina, El Terreno, Genova y todos los barrios.',
      faq: [
        { q: 'En que barrios de Palma estan activos?', a: 'En todo Palma – desde el casco antiguo hasta Son Vida, Portixol y los suburbios.' },
        { q: 'Con que rapidez pueden estar en Palma?', a: 'En Palma, normalmente podemos estar en el lugar en pocas horas.' },
        { q: 'Trabajan con clinicas en Palma?', a: 'Si, trabajamos estrechamente con Son Espases, Quironsalud, Policlinica Miramar y otras clinicas.' },
      ],
      internalLinks: [
        { label: 'Cuidado Mallorca' },
        { label: 'Cuidado en Manacor' },
        { label: 'Cuidado en Andratx' },
        { label: 'Medicos en Mallorca' },
        { label: 'Clinicas en Mallorca' },
        { label: 'Atencion a Mayores Mallorca' },
      ],
      breadcrumbs: [{ label: 'Inicio' }, { label: 'Cuidado Mallorca' }, { label: 'Cuidado Palma' }],
    },
  },

  'pflege-manacor': {
    en: {
      h1: 'Care in Manacor and Surroundings',
      metaTitle: 'Care in Manacor – German-speaking Support East Mallorca | Geis Care',
      metaDescription: 'Professional care in Manacor and surroundings: senior care, home nursing, medical accompaniment. German-speaking and reliable.',
      metaKeywords: 'care Manacor, senior care Manacor, home care Manacor, care East Mallorca',
      intro: 'In Manacor, Mallorca\'s second-largest city, Geis Care offers comprehensive care services. The region with Porto Cristo, Son Servera, and surrounding fincas is one of our core areas. Short travel times and personal care guaranteed.',
      services: [
        { title: 'Home Care', desc: 'Care at home in Manacor and surroundings.' },
        { title: 'Medical Accompaniment', desc: 'Accompaniment to doctors in Manacor and Palma.' },
        { title: 'Medication Management', desc: 'Reliable medication administration.' },
        { title: 'Daily Accompaniment', desc: 'Shopping, errands, leisure activities.' },
        { title: '24h Care', desc: 'Around-the-clock care in the region.' },
        { title: 'Senior Care', desc: 'Caring companionship in daily life.' },
      ],
      serviceAreaText: 'Service area: Manacor, Porto Cristo, Son Servera, Arta, Capdepera, Son Macia, Felanitx and surrounding fincas.',
      faq: [
        { q: 'Do you also care for fincas outside Manacor?', a: 'Yes, we also care for remote fincas in the Manacor, Porto Cristo, and Son Servera area.' },
        { q: 'Are there German-speaking doctors in Manacor?', a: 'Yes, there are German-speaking doctors in Manacor. For specialists, we accompany you to Palma.' },
        { q: 'How far does your service area reach?', a: 'We cover the entire East Mallorca region: from Arta in the north to Felanitx in the south.' },
      ],
      internalLinks: [
        { label: 'Care Mallorca' },
        { label: 'Care in Cala Millor' },
        { label: 'Care in Sant Llorenc' },
        { label: 'Care in Palma' },
        { label: 'Senior Care Mallorca' },
      ],
      breadcrumbs: [{ label: 'Home' }, { label: 'Care Mallorca' }, { label: 'Care Manacor' }],
    },
    es: {
      h1: 'Cuidado en Manacor y Alrededores',
      metaTitle: 'Cuidado en Manacor – Atencion Germanoparlante Este de Mallorca | Geis Care',
      metaDescription: 'Cuidado profesional en Manacor y alrededores: atencion a mayores, enfermeria domiciliaria, acompanamiento medico. Germanoparlante y fiable.',
      metaKeywords: 'cuidado Manacor, atencion mayores Manacor, cuidado domiciliario Manacor',
      intro: 'En Manacor, la segunda ciudad mas grande de Mallorca, Geis Care ofrece servicios de cuidado integrales. La region con Porto Cristo, Son Servera y las fincas circundantes es una de nuestras areas principales.',
      services: [
        { title: 'Cuidado Domiciliario', desc: 'Atencion en casa en Manacor y alrededores.' },
        { title: 'Acompanamiento Medico', desc: 'Acompanamiento a medicos en Manacor y Palma.' },
        { title: 'Gestion de Medicamentos', desc: 'Administracion fiable de medicamentos.' },
        { title: 'Acompanamiento Diario', desc: 'Compras, gestiones, actividades de ocio.' },
        { title: 'Atencion 24h', desc: 'Cuidado las 24 horas en la region.' },
        { title: 'Atencion a Mayores', desc: 'Acompanamiento carinoso en el dia a dia.' },
      ],
      serviceAreaText: 'Area de servicio: Manacor, Porto Cristo, Son Servera, Arta, Capdepera, Son Macia, Felanitx y fincas circundantes.',
      faq: [
        { q: 'Atienden tambien fincas fuera de Manacor?', a: 'Si, tambien atendemos fincas remotas en los alrededores de Manacor, Porto Cristo y Son Servera.' },
        { q: 'Hay medicos germanoparlantes en Manacor?', a: 'Si, hay medicos germanoparlantes en Manacor. Para especialistas, le acompanamos a Palma.' },
        { q: 'Hasta donde llega su area de servicio?', a: 'Cubrimos toda la region del este de Mallorca: desde Arta en el norte hasta Felanitx en el sur.' },
      ],
      internalLinks: [
        { label: 'Cuidado Mallorca' },
        { label: 'Cuidado en Cala Millor' },
        { label: 'Cuidado en Sant Llorenc' },
        { label: 'Cuidado en Palma' },
        { label: 'Atencion a Mayores Mallorca' },
      ],
      breadcrumbs: [{ label: 'Inicio' }, { label: 'Cuidado Mallorca' }, { label: 'Cuidado Manacor' }],
    },
  },

  'pflege-sant-llorenc': {
    en: {
      h1: 'Care in Sant Llorenc des Cardassar',
      metaTitle: 'Care in Sant Llorenc des Cardassar – Support East Mallorca | Geis Care',
      metaDescription: 'Care in Sant Llorenc des Cardassar and Sa Coma: senior care, home nursing, medical accompaniment. German-speaking and personal.',
      metaKeywords: 'care Sant Llorenc, care Sa Coma, senior care Sant Llorenc',
      intro: 'Sant Llorenc and the coastal towns Sa Coma and S\'Illot are popular residential areas for German residents. Geis Care offers professional care right at your doorstep here.',
      services: [
        { title: 'Home Care', desc: 'Care at home in Sant Llorenc and surroundings.' },
        { title: 'Medical Accompaniment', desc: 'Accompaniment to doctors in the region and Palma.' },
        { title: 'Medication Management', desc: 'Reliable medication administration.' },
        { title: 'Walks', desc: 'Accompanied outings along the coast.' },
        { title: 'Shopping Assistance', desc: 'Joint errands in the village.' },
        { title: 'Companionship', desc: 'Social contacts and shared activities.' },
      ],
      serviceAreaText: 'Service area: Sant Llorenc, Sa Coma, S\'Illot, Son Carrio, Cala Millor, Son Servera.',
      faq: [
        { q: 'Are you regularly in Sant Llorenc?', a: 'Yes, Sant Llorenc and the coastal towns are part of our core area with regular visits.' },
        { q: 'Can you come at short notice?', a: 'Yes, due to our proximity to the region, we can often be on site within a few hours.' },
        { q: 'Are there doctors nearby?', a: 'There is a health centre in Sant Llorenc. For specialists, we accompany you to Manacor or Palma.' },
      ],
      internalLinks: [
        { label: 'Care in Cala Millor' },
        { label: 'Care in Manacor' },
        { label: 'Care Mallorca' },
        { label: 'Senior Care Mallorca' },
        { label: 'Home Care Mallorca' },
      ],
      breadcrumbs: [{ label: 'Home' }, { label: 'Care Mallorca' }, { label: 'Care Sant Llorenc' }],
    },
    es: {
      h1: 'Cuidado en Sant Llorenc des Cardassar',
      metaTitle: 'Cuidado en Sant Llorenc des Cardassar – Atencion Este de Mallorca | Geis Care',
      metaDescription: 'Cuidado en Sant Llorenc des Cardassar y Sa Coma: atencion a mayores, enfermeria domiciliaria, acompanamiento medico. Germanoparlante y personal.',
      metaKeywords: 'cuidado Sant Llorenc, cuidado Sa Coma, atencion mayores Sant Llorenc',
      intro: 'Sant Llorenc y los pueblos costeros Sa Coma y S\'Illot son zonas residenciales populares para residentes alemanes. Geis Care ofrece aqui cuidado profesional directamente a su puerta.',
      services: [
        { title: 'Cuidado Domiciliario', desc: 'Atencion en casa en Sant Llorenc y alrededores.' },
        { title: 'Acompanamiento Medico', desc: 'Acompanamiento a medicos en la region y Palma.' },
        { title: 'Gestion de Medicamentos', desc: 'Administracion fiable de medicamentos.' },
        { title: 'Paseos', desc: 'Salidas acompanadas por la costa.' },
        { title: 'Acompanamiento de Compras', desc: 'Recados conjuntos en el pueblo.' },
        { title: 'Compania', desc: 'Contactos sociales y actividades compartidas.' },
      ],
      serviceAreaText: 'Area de servicio: Sant Llorenc, Sa Coma, S\'Illot, Son Carrio, Cala Millor, Son Servera.',
      faq: [
        { q: 'Estan regularmente en Sant Llorenc?', a: 'Si, Sant Llorenc y los pueblos costeros forman parte de nuestra area principal.' },
        { q: 'Pueden venir con poco aviso?', a: 'Si, debido a nuestra proximidad, a menudo podemos estar en el lugar en pocas horas.' },
        { q: 'Hay medicos cerca?', a: 'Hay un centro de salud en Sant Llorenc. Para especialistas, le acompanamos a Manacor o Palma.' },
      ],
      internalLinks: [
        { label: 'Cuidado en Cala Millor' },
        { label: 'Cuidado en Manacor' },
        { label: 'Cuidado Mallorca' },
        { label: 'Atencion a Mayores Mallorca' },
        { label: 'Cuidado Domiciliario Mallorca' },
      ],
      breadcrumbs: [{ label: 'Inicio' }, { label: 'Cuidado Mallorca' }, { label: 'Cuidado Sant Llorenc' }],
    },
  },

  'pflege-cala-millor': {
    en: {
      h1: 'Care in Cala Millor',
      metaTitle: 'Care in Cala Millor – German-speaking Support | Geis Care',
      metaDescription: 'Care in Cala Millor and Cala Bona: senior care, home nursing, medical accompaniment on the east coast of Mallorca.',
      metaKeywords: 'care Cala Millor, senior care Cala Millor, home care Cala Millor',
      intro: 'Cala Millor and Cala Bona on the east coast are home to many German residents. Geis Care offers tailored care services here – whether daily care, beach walk accompaniment, or medical support. Your trusted partner on the east coast.',
      services: [
        { title: 'Home Care', desc: 'Care at home in Cala Millor and Cala Bona.' },
        { title: 'Medical Accompaniment', desc: 'Accompaniment to doctors in the region.' },
        { title: 'Beach Walks', desc: 'Accompanied walks along the promenade.' },
        { title: 'Medication Management', desc: 'Safe medication administration.' },
        { title: 'Daily Accompaniment', desc: 'Shopping and errands in the area.' },
        { title: '24h Care', desc: 'Around-the-clock care available.' },
      ],
      serviceAreaText: 'Service area: Cala Millor, Cala Bona, Son Servera, Sa Coma, S\'Illot, Porto Cristo.',
      faq: [
        { q: 'Do you also care for holiday apartments in Cala Millor?', a: 'Yes, we also care for long-term holidaymakers in apartments and hotels.' },
        { q: 'How far is the nearest hospital?', a: 'Hospital de Manacor is about 20 minutes away. We are happy to accompany you.' },
        { q: 'Do you offer short-term care for tourists?', a: 'Yes, we offer flexible care also for shorter stays.' },
      ],
      internalLinks: [
        { label: 'Care in Sant Llorenc' },
        { label: 'Care in Manacor' },
        { label: 'Care Mallorca' },
        { label: 'Senior Care Mallorca' },
        { label: 'Care in Alcudia' },
      ],
      breadcrumbs: [{ label: 'Home' }, { label: 'Care Mallorca' }, { label: 'Care Cala Millor' }],
    },
    es: {
      h1: 'Cuidado en Cala Millor',
      metaTitle: 'Cuidado en Cala Millor – Atencion Germanoparlante | Geis Care',
      metaDescription: 'Cuidado en Cala Millor y Cala Bona: atencion a mayores, enfermeria domiciliaria, acompanamiento medico en la costa este de Mallorca.',
      metaKeywords: 'cuidado Cala Millor, atencion mayores Cala Millor, cuidado domiciliario Cala Millor',
      intro: 'Cala Millor y Cala Bona en la costa este son hogar de muchos residentes alemanes. Geis Care ofrece aqui servicios de cuidado a medida. Su socio de confianza en la costa este.',
      services: [
        { title: 'Cuidado Domiciliario', desc: 'Atencion en casa en Cala Millor y Cala Bona.' },
        { title: 'Acompanamiento Medico', desc: 'Acompanamiento a medicos en la region.' },
        { title: 'Paseos por la Playa', desc: 'Paseos acompanados por el paseo maritimo.' },
        { title: 'Gestion de Medicamentos', desc: 'Administracion segura de medicamentos.' },
        { title: 'Acompanamiento Diario', desc: 'Compras y recados en la zona.' },
        { title: 'Atencion 24h', desc: 'Cuidado las 24 horas disponible.' },
      ],
      serviceAreaText: 'Area de servicio: Cala Millor, Cala Bona, Son Servera, Sa Coma, S\'Illot, Porto Cristo.',
      faq: [
        { q: 'Atienden tambien apartamentos vacacionales en Cala Millor?', a: 'Si, tambien atendemos a vacacionistas de larga estancia en apartamentos y hoteles.' },
        { q: 'A que distancia esta el hospital mas cercano?', a: 'El Hospital de Manacor esta a unos 20 minutos. Le acompanamos con gusto.' },
        { q: 'Ofrecen cuidado temporal para turistas?', a: 'Si, ofrecemos cuidado flexible tambien para estancias cortas.' },
      ],
      internalLinks: [
        { label: 'Cuidado en Sant Llorenc' },
        { label: 'Cuidado en Manacor' },
        { label: 'Cuidado Mallorca' },
        { label: 'Atencion a Mayores Mallorca' },
        { label: 'Cuidado en Alcudia' },
      ],
      breadcrumbs: [{ label: 'Inicio' }, { label: 'Cuidado Mallorca' }, { label: 'Cuidado Cala Millor' }],
    },
  },

  'pflege-inca': {
    en: {
      h1: 'Care in Inca and Central Mallorca',
      metaTitle: 'Care in Inca – Support in the Heart of Mallorca | Geis Care',
      metaDescription: 'Professional care in Inca and Central Mallorca: senior care, home nursing, medical accompaniment. German-speaking and personal.',
      metaKeywords: 'care Inca, senior care Inca, home care Inca, care Central Mallorca',
      intro: 'Inca lies centrally in the heart of the island, surrounded by picturesque villages and fincas. Geis Care offers professional care for German-speaking residents here. Our central location enables fast response times.',
      services: [
        { title: 'Home Care', desc: 'Care at home in Inca and surroundings.' },
        { title: 'Medical Accompaniment', desc: 'Accompaniment to Hospital de Inca and specialists.' },
        { title: 'Medication Management', desc: 'Reliable medication administration.' },
        { title: 'Market Accompaniment', desc: 'Joint visit to the famous Dijous market.' },
        { title: 'Walks', desc: 'Accompanied outings in the surroundings.' },
        { title: 'Senior Care', desc: 'Caring companionship in daily life.' },
      ],
      serviceAreaText: 'Service area: Inca, Binissalem, Lloseta, Selva, Campanet, Sa Pobla and surrounding fincas.',
      faq: [
        { q: 'Is there a hospital in Inca?', a: 'Yes, Hospital de Inca offers good basic care. For specialists, we accompany you to Palma.' },
        { q: 'Do you also care for the villages around Inca?', a: 'Yes, we care for the entire region: Binissalem, Lloseta, Selva, Campanet, and Sa Pobla.' },
        { q: 'How can I reach you?', a: 'Contact us at +34 601 889 566 or via WhatsApp. We are quickly available for you.' },
      ],
      internalLinks: [
        { label: 'Care Mallorca' },
        { label: 'Care in Palma' },
        { label: 'Care in Alcudia' },
        { label: 'Home Care Mallorca' },
        { label: 'Senior Care Mallorca' },
      ],
      breadcrumbs: [{ label: 'Home' }, { label: 'Care Mallorca' }, { label: 'Care Inca' }],
    },
    es: {
      h1: 'Cuidado en Inca y Centro de Mallorca',
      metaTitle: 'Cuidado en Inca – Atencion en el Corazon de Mallorca | Geis Care',
      metaDescription: 'Cuidado profesional en Inca y Centro de Mallorca: atencion a mayores, enfermeria domiciliaria, acompanamiento medico. Germanoparlante y personal.',
      metaKeywords: 'cuidado Inca, atencion mayores Inca, cuidado domiciliario Inca',
      intro: 'Inca esta en el centro de la isla, rodeada de pueblos pintorescos y fincas. Geis Care ofrece aqui cuidado profesional para residentes germanoparlantes. Nuestra ubicacion central permite tiempos de respuesta rapidos.',
      services: [
        { title: 'Cuidado Domiciliario', desc: 'Atencion en casa en Inca y alrededores.' },
        { title: 'Acompanamiento Medico', desc: 'Acompanamiento al Hospital de Inca y especialistas.' },
        { title: 'Gestion de Medicamentos', desc: 'Administracion fiable de medicamentos.' },
        { title: 'Acompanamiento al Mercado', desc: 'Visita conjunta al famoso mercado del Dijous.' },
        { title: 'Paseos', desc: 'Salidas acompanadas por los alrededores.' },
        { title: 'Atencion a Mayores', desc: 'Acompanamiento carinoso en el dia a dia.' },
      ],
      serviceAreaText: 'Area de servicio: Inca, Binissalem, Lloseta, Selva, Campanet, Sa Pobla y fincas circundantes.',
      faq: [
        { q: 'Hay un hospital en Inca?', a: 'Si, el Hospital de Inca ofrece buena atencion basica. Para especialistas, le acompanamos a Palma.' },
        { q: 'Atienden tambien los pueblos alrededor de Inca?', a: 'Si, atendemos toda la region: Binissalem, Lloseta, Selva, Campanet y Sa Pobla.' },
        { q: 'Como puedo contactarles?', a: 'Contactenos al +34 601 889 566 o por WhatsApp. Estamos rapidamente disponibles.' },
      ],
      internalLinks: [
        { label: 'Cuidado Mallorca' },
        { label: 'Cuidado en Palma' },
        { label: 'Cuidado en Alcudia' },
        { label: 'Cuidado Domiciliario Mallorca' },
        { label: 'Atencion a Mayores Mallorca' },
      ],
      breadcrumbs: [{ label: 'Inicio' }, { label: 'Cuidado Mallorca' }, { label: 'Cuidado Inca' }],
    },
  },

  'pflege-andratx': {
    en: {
      h1: 'Care in Andratx and Port d\'Andratx',
      metaTitle: 'Care in Andratx & Port d\'Andratx – Premium Support | Geis Care',
      metaDescription: 'Exclusive care in Andratx and Port d\'Andratx: senior care, home nursing, medical accompaniment in Mallorca\'s premium region.',
      metaKeywords: 'care Andratx, care Port d\'Andratx, senior care Andratx, care West Mallorca',
      intro: 'Andratx and Port d\'Andratx are among Mallorca\'s most exclusive residential areas. Geis Care offers discreet, professional care at the highest level here. Whether harbour villa or mountain finca – individual care that matches your lifestyle.',
      services: [
        { title: 'Premium Home Care', desc: 'Exclusive care in your villa or finca.' },
        { title: 'Medical Accompaniment', desc: 'Accompaniment to doctors in Andratx and Palma.' },
        { title: 'Medication Management', desc: 'Discreet and reliable medication administration.' },
        { title: 'Harbour Promenade', desc: 'Accompanied walks at Port d\'Andratx harbour.' },
        { title: '24h Care', desc: 'Around-the-clock care in the region.' },
        { title: 'Discreet Support', desc: 'Highest discretion and privacy.' },
      ],
      serviceAreaText: 'Service area: Andratx, Port d\'Andratx, Camp de Mar, Peguera, Santa Ponsa.',
      faq: [
        { q: 'Do you offer particularly discreet care?', a: 'Yes, discretion is one of our core values. Especially in Andratx, we place great emphasis on privacy.' },
        { q: 'How far is it to Palma?', a: 'Andratx is about 30 minutes from Palma. We are happy to accompany you to specialists.' },
        { q: 'Do you also care for guests in holiday homes?', a: 'Yes, we also offer temporary care for holidaymakers and seasonal residents.' },
      ],
      internalLinks: [
        { label: 'Care Mallorca' },
        { label: 'Care in Palma' },
        { label: 'Senior Care Mallorca' },
        { label: 'Intensive Care Mallorca' },
        { label: 'Doctors in Mallorca' },
      ],
      breadcrumbs: [{ label: 'Home' }, { label: 'Care Mallorca' }, { label: 'Care Andratx' }],
    },
    es: {
      h1: 'Cuidado en Andratx y Port d\'Andratx',
      metaTitle: 'Cuidado en Andratx y Port d\'Andratx – Atencion Premium | Geis Care',
      metaDescription: 'Cuidado exclusivo en Andratx y Port d\'Andratx: atencion a mayores, enfermeria domiciliaria, acompanamiento medico en la region premium de Mallorca.',
      metaKeywords: 'cuidado Andratx, cuidado Port d\'Andratx, atencion mayores Andratx',
      intro: 'Andratx y Port d\'Andratx son de las zonas residenciales mas exclusivas de Mallorca. Geis Care ofrece aqui cuidado discreto y profesional al mas alto nivel. Cuidado individual que se adapta a su estilo de vida.',
      services: [
        { title: 'Cuidado Premium', desc: 'Atencion exclusiva en su villa o finca.' },
        { title: 'Acompanamiento Medico', desc: 'Acompanamiento a medicos en Andratx y Palma.' },
        { title: 'Gestion de Medicamentos', desc: 'Administracion discreta y fiable de medicamentos.' },
        { title: 'Paseo por el Puerto', desc: 'Paseos acompanados por el puerto de Port d\'Andratx.' },
        { title: 'Atencion 24h', desc: 'Cuidado las 24 horas en la region.' },
        { title: 'Atencion Discreta', desc: 'Maxima discrecion y privacidad.' },
      ],
      serviceAreaText: 'Area de servicio: Andratx, Port d\'Andratx, Camp de Mar, Peguera, Santa Ponsa.',
      faq: [
        { q: 'Ofrecen cuidado especialmente discreto?', a: 'Si, la discrecion es uno de nuestros valores fundamentales. Especialmente en Andratx, damos gran importancia a la privacidad.' },
        { q: 'A que distancia esta Palma?', a: 'Andratx esta a unos 30 minutos de Palma. Le acompanamos con gusto a especialistas.' },
        { q: 'Atienden tambien huespedes en casas vacacionales?', a: 'Si, tambien ofrecemos cuidado temporal para vacacionistas y residentes estacionales.' },
      ],
      internalLinks: [
        { label: 'Cuidado Mallorca' },
        { label: 'Cuidado en Palma' },
        { label: 'Atencion a Mayores Mallorca' },
        { label: 'Cuidados Intensivos Mallorca' },
        { label: 'Medicos en Mallorca' },
      ],
      breadcrumbs: [{ label: 'Inicio' }, { label: 'Cuidado Mallorca' }, { label: 'Cuidado Andratx' }],
    },
  },

  'pflege-alcudia': {
    en: {
      h1: 'Care in Alcudia and North Mallorca',
      metaTitle: 'Care in Alcudia & Port d\'Alcudia – Support North Mallorca | Geis Care',
      metaDescription: 'Care in Alcudia and Port d\'Alcudia: senior care, home nursing, medical accompaniment in north Mallorca. German-speaking and professional.',
      metaKeywords: 'care Alcudia, care Port d\'Alcudia, senior care Alcudia, care North Mallorca',
      intro: 'Alcudia and Port d\'Alcudia in the north are popular residential areas for German residents. Geis Care offers professional care here – from the historic old town to the beach apartments. Enjoy the mild climate while we take care of your nursing needs.',
      services: [
        { title: 'Home Care', desc: 'Care at home in Alcudia and surroundings.' },
        { title: 'Medical Accompaniment', desc: 'Accompaniment to doctors in Alcudia and Inca.' },
        { title: 'Beach Walks', desc: 'Accompanied walks along the Bay of Alcudia.' },
        { title: 'Medication Management', desc: 'Reliable medication administration.' },
        { title: 'Market Accompaniment', desc: 'Joint visits to the weekly markets.' },
        { title: 'Senior Care', desc: 'Caring companionship in daily life.' },
      ],
      serviceAreaText: 'Service area: Alcudia, Port d\'Alcudia, Can Picafort, Pollenca, Port de Pollenca, Sa Pobla.',
      faq: [
        { q: 'Do you also cover Port de Pollenca?', a: 'Yes, our service area includes Alcudia, Pollenca, Port de Pollenca, Can Picafort, and surrounding communities.' },
        { q: 'Are there doctors in Alcudia?', a: 'Yes, there is a health centre and private practices. For specialists, we accompany you to Inca or Palma.' },
        { q: 'Do you offer care for winter residents?', a: 'Yes, many of our clients are long-term holidaymakers who need seasonal care.' },
      ],
      internalLinks: [
        { label: 'Care Mallorca' },
        { label: 'Care in Inca' },
        { label: 'Care in Palma' },
        { label: 'Senior Care Mallorca' },
        { label: 'Home Care Mallorca' },
      ],
      breadcrumbs: [{ label: 'Home' }, { label: 'Care Mallorca' }, { label: 'Care Alcudia' }],
    },
    es: {
      h1: 'Cuidado en Alcudia y Norte de Mallorca',
      metaTitle: 'Cuidado en Alcudia y Port d\'Alcudia – Atencion Norte de Mallorca | Geis Care',
      metaDescription: 'Cuidado en Alcudia y Port d\'Alcudia: atencion a mayores, enfermeria domiciliaria, acompanamiento medico en el norte de Mallorca. Germanoparlante y profesional.',
      metaKeywords: 'cuidado Alcudia, cuidado Port d\'Alcudia, atencion mayores Alcudia',
      intro: 'Alcudia y Port d\'Alcudia en el norte son zonas residenciales populares para residentes alemanes. Geis Care ofrece aqui cuidado profesional. Disfrute del clima suave mientras nos encargamos de sus necesidades de atencion.',
      services: [
        { title: 'Cuidado Domiciliario', desc: 'Atencion en casa en Alcudia y alrededores.' },
        { title: 'Acompanamiento Medico', desc: 'Acompanamiento a medicos en Alcudia e Inca.' },
        { title: 'Paseos por la Playa', desc: 'Paseos acompanados por la Bahia de Alcudia.' },
        { title: 'Gestion de Medicamentos', desc: 'Administracion fiable de medicamentos.' },
        { title: 'Acompanamiento al Mercado', desc: 'Visitas conjuntas a los mercados semanales.' },
        { title: 'Atencion a Mayores', desc: 'Acompanamiento carinoso en el dia a dia.' },
      ],
      serviceAreaText: 'Area de servicio: Alcudia, Port d\'Alcudia, Can Picafort, Pollenca, Port de Pollenca, Sa Pobla.',
      faq: [
        { q: 'Cubren tambien Port de Pollenca?', a: 'Si, nuestra area de servicio incluye Alcudia, Pollenca, Port de Pollenca, Can Picafort y comunidades circundantes.' },
        { q: 'Hay medicos en Alcudia?', a: 'Si, hay un centro de salud y consultas privadas. Para especialistas, le acompanamos a Inca o Palma.' },
        { q: 'Ofrecen cuidado para residentes de invierno?', a: 'Si, muchos de nuestros clientes son vacacionistas de larga estancia que necesitan cuidado estacional.' },
      ],
      internalLinks: [
        { label: 'Cuidado Mallorca' },
        { label: 'Cuidado en Inca' },
        { label: 'Cuidado en Palma' },
        { label: 'Atencion a Mayores Mallorca' },
        { label: 'Cuidado Domiciliario Mallorca' },
      ],
      breadcrumbs: [{ label: 'Inicio' }, { label: 'Cuidado Mallorca' }, { label: 'Cuidado Alcudia' }],
    },
  },

  // ===== NEW LOCATION PAGES =====

  'pflege-santa-ponsa': {
    en: {
      h1: 'Care in Santa Ponsa',
      metaTitle: 'Care in Santa Ponsa – German-speaking Support Calvia | Geis Care',
      metaDescription: 'Professional care in Santa Ponsa: senior care, home nursing, medical accompaniment in southwest Mallorca. German-speaking and reliable.',
      metaKeywords: 'care Santa Ponsa, senior care Santa Ponsa, home care Santa Ponsa, care Calvia',
      intro: 'Santa Ponsa is one of the most popular residential areas for German residents in southwest Mallorca. Geis Care offers professional, German-speaking care here – from the beach promenade to the residential complexes. Short distances to Palma and Andratx guarantee excellent medical care.',
      services: [
        { title: 'Home Care', desc: 'Care at home in Santa Ponsa and surroundings.' },
        { title: 'Medical Accompaniment', desc: 'Accompaniment to doctors in the region and Palma.' },
        { title: 'Medication Management', desc: 'Reliable medication administration.' },
        { title: 'Beach Walks', desc: 'Accompanied outings along the promenade.' },
        { title: 'Shopping Assistance', desc: 'Joint errands in the village.' },
        { title: '24h Care', desc: 'Around-the-clock care available.' },
      ],
      serviceAreaText: 'Service area: Santa Ponsa, Paguera, Magaluf, Palmanova, Costa de la Calma.',
      faq: [
        { q: 'Do you also care for holiday apartments in Santa Ponsa?', a: 'Yes, we also care for long-term holidaymakers and seasonal residents in holiday apartments.' },
        { q: 'How far is it to Palma?', a: 'Santa Ponsa is about 20 minutes from Palma. We are happy to accompany you to specialists.' },
        { q: 'Are there German-speaking doctors locally?', a: 'Yes, there are several German-speaking medical practices in Santa Ponsa and the surrounding area.' },
      ],
      internalLinks: [
        { label: 'Care Mallorca' },
        { label: 'Care in Andratx' },
        { label: 'Care in Palma' },
        { label: 'Senior Care Mallorca' },
        { label: 'Home Care Mallorca' },
      ],
      breadcrumbs: [{ label: 'Home' }, { label: 'Care Mallorca' }, { label: 'Care Santa Ponsa' }],
    },
    es: {
      h1: 'Cuidado en Santa Ponsa',
      metaTitle: 'Cuidado en Santa Ponsa – Atencion Germanoparlante Calvia | Geis Care',
      metaDescription: 'Cuidado profesional en Santa Ponsa: atencion a mayores, enfermeria domiciliaria, acompanamiento medico en el suroeste de Mallorca.',
      metaKeywords: 'cuidado Santa Ponsa, atencion mayores Santa Ponsa, cuidado domiciliario Santa Ponsa',
      intro: 'Santa Ponsa es una de las zonas residenciales mas populares para residentes alemanes en el suroeste de Mallorca. Geis Care ofrece aqui cuidado profesional germanoparlante – desde el paseo maritimo hasta los complejos residenciales.',
      services: [
        { title: 'Cuidado Domiciliario', desc: 'Atencion en casa en Santa Ponsa y alrededores.' },
        { title: 'Acompanamiento Medico', desc: 'Acompanamiento a medicos en la region y Palma.' },
        { title: 'Gestion de Medicamentos', desc: 'Administracion fiable de medicamentos.' },
        { title: 'Paseos por la Playa', desc: 'Salidas acompanadas por el paseo maritimo.' },
        { title: 'Acompanamiento de Compras', desc: 'Recados conjuntos en el pueblo.' },
        { title: 'Atencion 24h', desc: 'Cuidado las 24 horas disponible.' },
      ],
      serviceAreaText: 'Area de servicio: Santa Ponsa, Paguera, Magaluf, Palmanova, Costa de la Calma.',
      faq: [
        { q: 'Tambien atienden apartamentos vacacionales en Santa Ponsa?', a: 'Si, tambien atendemos a vacacionistas de larga estancia y residentes estacionales.' },
        { q: 'A que distancia esta Palma?', a: 'Santa Ponsa esta a unos 20 minutos de Palma. Le acompanamos con gusto a especialistas.' },
        { q: 'Hay medicos germanoparlantes localmente?', a: 'Si, hay varias consultas medicas germanoparlantes en Santa Ponsa y alrededores.' },
      ],
      internalLinks: [
        { label: 'Cuidado Mallorca' },
        { label: 'Cuidado en Andratx' },
        { label: 'Cuidado en Palma' },
        { label: 'Atencion a Mayores Mallorca' },
        { label: 'Cuidado Domiciliario Mallorca' },
      ],
      breadcrumbs: [{ label: 'Inicio' }, { label: 'Cuidado Mallorca' }, { label: 'Cuidado Santa Ponsa' }],
    },
  },

  'pflege-porto-cristo': {
    en: {
      h1: 'Care in Porto Cristo',
      metaTitle: 'Care in Porto Cristo – Support Mallorca East Coast | Geis Care',
      metaDescription: 'Care in Porto Cristo and surroundings: senior care, home nursing, medical accompaniment on the east coast. German-speaking and personal.',
      metaKeywords: 'care Porto Cristo, senior care Porto Cristo, home care Porto Cristo',
      intro: 'Porto Cristo on the east coast is known for its picturesque harbour and the Cuevas del Drach. Geis Care offers professional care for German residents here – whether harbour-side or surrounding fincas. Hospital Parque Llevant guarantees first-class medical care on site.',
      services: [
        { title: 'Home Care', desc: 'Care at home in Porto Cristo and surroundings.' },
        { title: 'Medical Accompaniment', desc: 'Accompaniment to doctors in Porto Cristo and Manacor.' },
        { title: 'Medication Management', desc: 'Reliable medication administration.' },
        { title: 'Harbour Walks', desc: 'Accompanied outings at the harbour and beach.' },
        { title: 'Daily Companionship', desc: 'Shopping and errands in the village.' },
        { title: 'Senior Care', desc: 'Caring companionship in daily life.' },
      ],
      serviceAreaText: 'Service area: Porto Cristo, Cala Anguila, Cala Mandia, Cales de Mallorca, S\'Illot.',
      faq: [
        { q: 'Is there a hospital in Porto Cristo?', a: 'Yes, Hospital Parque Llevant offers 24h emergency department and all important specialities.' },
        { q: 'Do you also cover the coastal villages around Porto Cristo?', a: 'Yes, we cover the entire region: Cala Anguila, Cala Mandia, Cales de Mallorca, and S\'Illot.' },
        { q: 'How quickly can you be on site?', a: 'Porto Cristo is part of our core area. We can often be on site within a few hours.' },
      ],
      internalLinks: [
        { label: 'Care in Manacor' },
        { label: 'Care in Cala Millor' },
        { label: 'Care Mallorca' },
        { label: 'Senior Care Mallorca' },
        { label: 'Clinics in Mallorca' },
      ],
      breadcrumbs: [{ label: 'Home' }, { label: 'Care Mallorca' }, { label: 'Care Porto Cristo' }],
    },
    es: {
      h1: 'Cuidado en Porto Cristo',
      metaTitle: 'Cuidado en Porto Cristo – Atencion Costa Este de Mallorca | Geis Care',
      metaDescription: 'Cuidado en Porto Cristo y alrededores: atencion a mayores, enfermeria domiciliaria, acompanamiento medico en la costa este.',
      metaKeywords: 'cuidado Porto Cristo, atencion mayores Porto Cristo, cuidado domiciliario Porto Cristo',
      intro: 'Porto Cristo en la costa este es conocido por su pintoresco puerto y las Cuevas del Drach. Geis Care ofrece aqui cuidado profesional para residentes alemanes. El Hospital Parque Llevant garantiza atencion medica de primera clase.',
      services: [
        { title: 'Cuidado Domiciliario', desc: 'Atencion en casa en Porto Cristo y alrededores.' },
        { title: 'Acompanamiento Medico', desc: 'Acompanamiento a medicos en Porto Cristo y Manacor.' },
        { title: 'Gestion de Medicamentos', desc: 'Administracion fiable de medicamentos.' },
        { title: 'Paseos por el Puerto', desc: 'Salidas acompanadas por el puerto y la playa.' },
        { title: 'Acompanamiento Diario', desc: 'Compras y recados en el pueblo.' },
        { title: 'Atencion a Mayores', desc: 'Acompanamiento carinoso en el dia a dia.' },
      ],
      serviceAreaText: 'Area de servicio: Porto Cristo, Cala Anguila, Cala Mandia, Cales de Mallorca, S\'Illot.',
      faq: [
        { q: 'Hay un hospital en Porto Cristo?', a: 'Si, el Hospital Parque Llevant ofrece urgencias 24h y todas las especialidades importantes.' },
        { q: 'Cubren los pueblos costeros alrededor de Porto Cristo?', a: 'Si, cubrimos toda la region: Cala Anguila, Cala Mandia, Cales de Mallorca y S\'Illot.' },
        { q: 'Con que rapidez pueden estar en el lugar?', a: 'Porto Cristo es parte de nuestra area principal. A menudo podemos estar en pocas horas.' },
      ],
      internalLinks: [
        { label: 'Cuidado en Manacor' },
        { label: 'Cuidado en Cala Millor' },
        { label: 'Cuidado Mallorca' },
        { label: 'Atencion a Mayores Mallorca' },
        { label: 'Clinicas en Mallorca' },
      ],
      breadcrumbs: [{ label: 'Inicio' }, { label: 'Cuidado Mallorca' }, { label: 'Cuidado Porto Cristo' }],
    },
  },

  'pflege-campos': {
    en: {
      h1: 'Care in Campos and South Mallorca',
      metaTitle: 'Care in Campos – Support South Mallorca | Geis Care',
      metaDescription: 'Professional care in Campos and surroundings: senior care, home nursing, medical accompaniment in south Mallorca.',
      metaKeywords: 'care Campos, senior care Campos, home care Campos, care South Mallorca',
      intro: 'Campos in the south of Mallorca is known for its quiet, rural atmosphere and proximity to stunning beaches like Es Trenc. Geis Care offers professional care for German-speaking residents here – from the town centre to surrounding fincas.',
      services: [
        { title: 'Home Care', desc: 'Care at home in Campos and surroundings.' },
        { title: 'Medical Accompaniment', desc: 'Accompaniment to doctors in Campos, Santanyi, and Palma.' },
        { title: 'Medication Management', desc: 'Reliable medication administration.' },
        { title: 'Market Accompaniment', desc: 'Joint visits to the weekly market in Campos.' },
        { title: 'Beach Outings', desc: 'Accompanied trips to Es Trenc and Colonia Sant Jordi.' },
        { title: 'Senior Care', desc: 'Caring companionship in daily life.' },
      ],
      serviceAreaText: 'Service area: Campos, Colonia Sant Jordi, Es Trenc, Ses Salines, Sa Rapita.',
      faq: [
        { q: 'Do you also care for remote fincas near Campos?', a: 'Yes, we also care for rural fincas in the surroundings of Campos and Ses Salines.' },
        { q: 'Are there doctors in Campos?', a: 'Yes, there is a health centre in Campos. For specialists, we accompany you to Manacor or Palma.' },
        { q: 'How far is the nearest hospital?', a: 'Hospital de Manacor is about 30 minutes away. We are happy to accompany you.' },
      ],
      internalLinks: [
        { label: 'Care Mallorca' },
        { label: 'Care in Manacor' },
        { label: 'Care in Palma' },
        { label: 'Senior Care Mallorca' },
        { label: 'Home Care Mallorca' },
      ],
      breadcrumbs: [{ label: 'Home' }, { label: 'Care Mallorca' }, { label: 'Care Campos' }],
    },
    es: {
      h1: 'Cuidado en Campos y Sur de Mallorca',
      metaTitle: 'Cuidado en Campos – Atencion Sur de Mallorca | Geis Care',
      metaDescription: 'Cuidado profesional en Campos y alrededores: atencion a mayores, enfermeria domiciliaria, acompanamiento medico en el sur de Mallorca.',
      metaKeywords: 'cuidado Campos, atencion mayores Campos, cuidado domiciliario Campos',
      intro: 'Campos en el sur de Mallorca es conocido por su atmosfera tranquila y rural y la proximidad a playas impresionantes como Es Trenc. Geis Care ofrece aqui cuidado profesional para residentes germanoparlantes.',
      services: [
        { title: 'Cuidado Domiciliario', desc: 'Atencion en casa en Campos y alrededores.' },
        { title: 'Acompanamiento Medico', desc: 'Acompanamiento a medicos en Campos, Santanyi y Palma.' },
        { title: 'Gestion de Medicamentos', desc: 'Administracion fiable de medicamentos.' },
        { title: 'Acompanamiento al Mercado', desc: 'Visitas conjuntas al mercado semanal de Campos.' },
        { title: 'Salidas a la Playa', desc: 'Excursiones acompanadas a Es Trenc y Colonia Sant Jordi.' },
        { title: 'Atencion a Mayores', desc: 'Acompanamiento carinoso en el dia a dia.' },
      ],
      serviceAreaText: 'Area de servicio: Campos, Colonia Sant Jordi, Es Trenc, Ses Salines, Sa Rapita.',
      faq: [
        { q: 'Atienden fincas remotas cerca de Campos?', a: 'Si, tambien atendemos fincas rurales en los alrededores de Campos y Ses Salines.' },
        { q: 'Hay medicos en Campos?', a: 'Si, hay un centro de salud en Campos. Para especialistas, le acompanamos a Manacor o Palma.' },
        { q: 'A que distancia esta el hospital mas cercano?', a: 'El Hospital de Manacor esta a unos 30 minutos. Le acompanamos con gusto.' },
      ],
      internalLinks: [
        { label: 'Cuidado Mallorca' },
        { label: 'Cuidado en Manacor' },
        { label: 'Cuidado en Palma' },
        { label: 'Atencion a Mayores Mallorca' },
        { label: 'Cuidado Domiciliario Mallorca' },
      ],
      breadcrumbs: [{ label: 'Inicio' }, { label: 'Cuidado Mallorca' }, { label: 'Cuidado Campos' }],
    },
  },

  'pflege-soller': {
    en: {
      h1: 'Care in Soller and Port de Soller',
      metaTitle: 'Care in Soller & Port de Soller – Support Tramuntana Region | Geis Care',
      metaDescription: 'Care in Soller and Port de Soller: senior care, home nursing, medical accompaniment in the Tramuntana region.',
      metaKeywords: 'care Soller, care Port de Soller, senior care Soller, care Tramuntana',
      intro: 'Soller and Port de Soller in the heart of the Tramuntana mountains are among the most beautiful places in Mallorca. Geis Care offers professional care for German-speaking residents here – whether in the valley or at the harbour.',
      services: [
        { title: 'Home Care', desc: 'Care at home in Soller and Port de Soller.' },
        { title: 'Medical Accompaniment', desc: 'Accompaniment to local doctors and in Palma.' },
        { title: 'Medication Management', desc: 'Reliable medication administration.' },
        { title: 'Harbour Promenade', desc: 'Accompanied walks at the Port de Soller harbour.' },
        { title: 'Market Accompaniment', desc: 'Joint visits to the famous Soller market.' },
        { title: '24h Care', desc: 'Around-the-clock care available.' },
      ],
      serviceAreaText: 'Service area: Soller, Port de Soller, Fornalutx, Deia, Bunyola.',
      faq: [
        { q: 'Are there doctors in Soller?', a: 'Yes, there is a health centre and the Medcare clinic in Port de Soller with German-speaking doctors.' },
        { q: 'Do you also cover the mountain villages around Soller?', a: 'Yes, we also care for Fornalutx, Deia, and surrounding fincas in the Tramuntana.' },
        { q: 'What is the connection to Palma like?', a: 'Soller is about 25 minutes from Palma via the tunnel. We are happy to accompany you to specialists.' },
      ],
      internalLinks: [
        { label: 'Care Mallorca' },
        { label: 'Care in Palma' },
        { label: 'Senior Care Mallorca' },
        { label: 'Home Care Mallorca' },
        { label: 'Doctors in Mallorca' },
      ],
      breadcrumbs: [{ label: 'Home' }, { label: 'Care Mallorca' }, { label: 'Care Soller' }],
    },
    es: {
      h1: 'Cuidado en Soller y Port de Soller',
      metaTitle: 'Cuidado en Soller y Port de Soller – Atencion Region Tramuntana | Geis Care',
      metaDescription: 'Cuidado en Soller y Port de Soller: atencion a mayores, enfermeria domiciliaria, acompanamiento medico en la region de la Tramuntana.',
      metaKeywords: 'cuidado Soller, cuidado Port de Soller, atencion mayores Soller',
      intro: 'Soller y Port de Soller en el corazon de la Tramuntana son de los lugares mas hermosos de Mallorca. Geis Care ofrece aqui cuidado profesional para residentes germanoparlantes.',
      services: [
        { title: 'Cuidado Domiciliario', desc: 'Atencion en casa en Soller y Port de Soller.' },
        { title: 'Acompanamiento Medico', desc: 'Acompanamiento a medicos locales y en Palma.' },
        { title: 'Gestion de Medicamentos', desc: 'Administracion fiable de medicamentos.' },
        { title: 'Paseo por el Puerto', desc: 'Paseos acompanados por el puerto de Port de Soller.' },
        { title: 'Acompanamiento al Mercado', desc: 'Visitas conjuntas al famoso mercado de Soller.' },
        { title: 'Atencion 24h', desc: 'Cuidado las 24 horas disponible.' },
      ],
      serviceAreaText: 'Area de servicio: Soller, Port de Soller, Fornalutx, Deia, Bunyola.',
      faq: [
        { q: 'Hay medicos en Soller?', a: 'Si, hay un centro de salud y la clinica Medcare en Port de Soller con medicos germanoparlantes.' },
        { q: 'Cubren los pueblos de montana alrededor de Soller?', a: 'Si, tambien atendemos Fornalutx, Deia y fincas circundantes en la Tramuntana.' },
        { q: 'Como es la conexion con Palma?', a: 'Soller esta a unos 25 minutos de Palma por el tunel. Le acompanamos con gusto a especialistas.' },
      ],
      internalLinks: [
        { label: 'Cuidado Mallorca' },
        { label: 'Cuidado en Palma' },
        { label: 'Atencion a Mayores Mallorca' },
        { label: 'Cuidado Domiciliario Mallorca' },
        { label: 'Medicos en Mallorca' },
      ],
      breadcrumbs: [{ label: 'Inicio' }, { label: 'Cuidado Mallorca' }, { label: 'Cuidado Soller' }],
    },
  },

  'pflege-pollenca': {
    en: {
      h1: 'Care in Pollenca and Port de Pollenca',
      metaTitle: 'Care in Pollenca & Port de Pollenca – Support North Mallorca | Geis Care',
      metaDescription: 'Professional care in Pollenca and Port de Pollenca: senior care, home nursing, medical accompaniment in north Mallorca.',
      metaKeywords: 'care Pollenca, care Port de Pollenca, senior care Pollenca, care North Mallorca',
      intro: 'Pollenca and Port de Pollenca in north Mallorca combine culture, nature, and Mediterranean lifestyle. Geis Care offers professional, German-speaking care here – from the historic old town to the harbour promenade.',
      services: [
        { title: 'Home Care', desc: 'Care at home in Pollenca and Port de Pollenca.' },
        { title: 'Medical Accompaniment', desc: 'Accompaniment to doctors in the region and Inca.' },
        { title: 'Medication Management', desc: 'Reliable medication administration.' },
        { title: 'Harbour Walks', desc: 'Accompanied outings at the harbour and beach.' },
        { title: 'Market Accompaniment', desc: 'Joint visits to the Sunday market in Pollenca.' },
        { title: 'Senior Care', desc: 'Caring companionship in daily life.' },
      ],
      serviceAreaText: 'Service area: Pollenca, Port de Pollenca, Cala Sant Vicenc, Alcudia, Formentor.',
      faq: [
        { q: 'Are there doctors in Pollenca?', a: 'Yes, there is a health centre in Pollenca. For specialists, we accompany you to Inca or Palma.' },
        { q: 'Do you also cover Cala Sant Vicenc and Formentor?', a: 'Yes, our service area covers the entire Pollenca region up to the Formentor peninsula.' },
        { q: 'Do you offer care for seasonal residents?', a: 'Yes, many of our clients are long-term holidaymakers and seasonal residents in the region.' },
      ],
      internalLinks: [
        { label: 'Care in Alcudia' },
        { label: 'Care in Inca' },
        { label: 'Care Mallorca' },
        { label: 'Senior Care Mallorca' },
        { label: 'Home Care Mallorca' },
      ],
      breadcrumbs: [{ label: 'Home' }, { label: 'Care Mallorca' }, { label: 'Care Pollenca' }],
    },
    es: {
      h1: 'Cuidado en Pollenca y Port de Pollenca',
      metaTitle: 'Cuidado en Pollenca y Port de Pollenca – Atencion Norte de Mallorca | Geis Care',
      metaDescription: 'Cuidado profesional en Pollenca y Port de Pollenca: atencion a mayores, enfermeria domiciliaria, acompanamiento medico en el norte de Mallorca.',
      metaKeywords: 'cuidado Pollenca, cuidado Port de Pollenca, atencion mayores Pollenca',
      intro: 'Pollenca y Port de Pollenca en el norte de Mallorca combinan cultura, naturaleza y estilo de vida mediterraneo. Geis Care ofrece aqui cuidado profesional germanoparlante.',
      services: [
        { title: 'Cuidado Domiciliario', desc: 'Atencion en casa en Pollenca y Port de Pollenca.' },
        { title: 'Acompanamiento Medico', desc: 'Acompanamiento a medicos en la region e Inca.' },
        { title: 'Gestion de Medicamentos', desc: 'Administracion fiable de medicamentos.' },
        { title: 'Paseos por el Puerto', desc: 'Salidas acompanadas por el puerto y la playa.' },
        { title: 'Acompanamiento al Mercado', desc: 'Visitas conjuntas al mercado dominical de Pollenca.' },
        { title: 'Atencion a Mayores', desc: 'Acompanamiento carinoso en el dia a dia.' },
      ],
      serviceAreaText: 'Area de servicio: Pollenca, Port de Pollenca, Cala Sant Vicenc, Alcudia, Formentor.',
      faq: [
        { q: 'Hay medicos en Pollenca?', a: 'Si, hay un centro de salud en Pollenca. Para especialistas, le acompanamos a Inca o Palma.' },
        { q: 'Cubren Cala Sant Vicenc y Formentor?', a: 'Si, nuestra area de servicio cubre toda la region de Pollenca hasta la peninsula de Formentor.' },
        { q: 'Ofrecen cuidado para residentes estacionales?', a: 'Si, muchos de nuestros clientes son vacacionistas de larga estancia y residentes estacionales.' },
      ],
      internalLinks: [
        { label: 'Cuidado en Alcudia' },
        { label: 'Cuidado en Inca' },
        { label: 'Cuidado Mallorca' },
        { label: 'Atencion a Mayores Mallorca' },
        { label: 'Cuidado Domiciliario Mallorca' },
      ],
      breadcrumbs: [{ label: 'Inicio' }, { label: 'Cuidado Mallorca' }, { label: 'Cuidado Pollenca' }],
    },
  },

  'pflege-calvia': {
    en: {
      h1: 'Care in Calvia and Surroundings',
      metaTitle: 'Care in Calvia – Support Southwest Mallorca | Geis Care',
      metaDescription: 'Care in Calvia and surroundings: senior care, home nursing, medical accompaniment in the southwest. German-speaking and professional.',
      metaKeywords: 'care Calvia, senior care Calvia, home care Calvia, care Southwest Mallorca',
      intro: 'The municipality of Calvia in southwest Mallorca includes popular places like Santa Ponsa, Paguera, Palmanova, and Magaluf. Geis Care offers professional, German-speaking care here – from the coast to the hinterland.',
      services: [
        { title: 'Home Care', desc: 'Care at home throughout the Calvia municipality.' },
        { title: 'Medical Accompaniment', desc: 'Accompaniment to local doctors and in Palma.' },
        { title: 'Medication Management', desc: 'Reliable medication administration.' },
        { title: 'Beach Walks', desc: 'Accompanied outings along the coast.' },
        { title: 'Shopping Assistance', desc: 'Joint errands in the village.' },
        { title: '24h Care', desc: 'Around-the-clock care available.' },
      ],
      serviceAreaText: 'Service area: Calvia, Santa Ponsa, Paguera, Palmanova, Magaluf, Costa de la Calma, Sol de Mallorca.',
      faq: [
        { q: 'In which places of Calvia are you active?', a: 'We cover the entire municipality: Santa Ponsa, Paguera, Palmanova, Magaluf, and all other places.' },
        { q: 'Are there German-speaking doctors in Calvia?', a: 'Yes, there are several German-speaking medical practices and health centres in the region.' },
        { q: 'How far is it to Palma?', a: 'Calvia is between 15 and 25 minutes from Palma, depending on the place.' },
      ],
      internalLinks: [
        { label: 'Care in Santa Ponsa' },
        { label: 'Care in Andratx' },
        { label: 'Care in Palma' },
        { label: 'Care Mallorca' },
        { label: 'Senior Care Mallorca' },
      ],
      breadcrumbs: [{ label: 'Home' }, { label: 'Care Mallorca' }, { label: 'Care Calvia' }],
    },
    es: {
      h1: 'Cuidado en Calvia y Alrededores',
      metaTitle: 'Cuidado en Calvia – Atencion Suroeste de Mallorca | Geis Care',
      metaDescription: 'Cuidado en Calvia y alrededores: atencion a mayores, enfermeria domiciliaria, acompanamiento medico en el suroeste.',
      metaKeywords: 'cuidado Calvia, atencion mayores Calvia, cuidado domiciliario Calvia',
      intro: 'El municipio de Calvia en el suroeste de Mallorca incluye lugares populares como Santa Ponsa, Paguera, Palmanova y Magaluf. Geis Care ofrece aqui cuidado profesional germanoparlante.',
      services: [
        { title: 'Cuidado Domiciliario', desc: 'Atencion en casa en todo el municipio de Calvia.' },
        { title: 'Acompanamiento Medico', desc: 'Acompanamiento a medicos locales y en Palma.' },
        { title: 'Gestion de Medicamentos', desc: 'Administracion fiable de medicamentos.' },
        { title: 'Paseos por la Playa', desc: 'Salidas acompanadas por la costa.' },
        { title: 'Acompanamiento de Compras', desc: 'Recados conjuntos en el pueblo.' },
        { title: 'Atencion 24h', desc: 'Cuidado las 24 horas disponible.' },
      ],
      serviceAreaText: 'Area de servicio: Calvia, Santa Ponsa, Paguera, Palmanova, Magaluf, Costa de la Calma, Sol de Mallorca.',
      faq: [
        { q: 'En que lugares de Calvia estan activos?', a: 'Cubrimos todo el municipio: Santa Ponsa, Paguera, Palmanova, Magaluf y todos los demas lugares.' },
        { q: 'Hay medicos germanoparlantes en Calvia?', a: 'Si, hay varias consultas medicas germanoparlantes y centros de salud en la region.' },
        { q: 'A que distancia esta Palma?', a: 'Calvia esta entre 15 y 25 minutos de Palma, dependiendo del lugar.' },
      ],
      internalLinks: [
        { label: 'Cuidado en Santa Ponsa' },
        { label: 'Cuidado en Andratx' },
        { label: 'Cuidado en Palma' },
        { label: 'Cuidado Mallorca' },
        { label: 'Atencion a Mayores Mallorca' },
      ],
      breadcrumbs: [{ label: 'Inicio' }, { label: 'Cuidado Mallorca' }, { label: 'Cuidado Calvia' }],
    },
  },

  'gesundheit-mallorca': {
    en: {
      h1: 'Health in Mallorca – Your Health Hub',
      metaTitle: 'Health in Mallorca – Your Health Hub for Medical Care | Geis Care',
      metaDescription: 'Central hub for health in Mallorca: doctors, clinics, care, emergency care, medication. Your German-speaking health hub.',
      metaKeywords: 'health Mallorca, medical care Mallorca, healthcare Mallorca',
      intro: 'Welcome to your central health hub for Mallorca. Here you will find all important information about medical care on the island: from German-speaking doctors to clinics to home care. Geis Care is your partner for safe and professional healthcare in Mallorca.',
      services: [
        { title: 'Doctors & Specialists', desc: 'Network of German-speaking doctors and specialists throughout Mallorca.' },
        { title: 'Clinics & Hospitals', desc: 'Overview of first-class medical facilities.' },
        { title: 'Home Care', desc: 'Professional home nursing and support.' },
        { title: 'Emergency Care', desc: 'Important emergency numbers and quick help in emergencies.' },
        { title: 'Medication & Pharmacies', desc: 'Prescription management and medication organisation.' },
        { title: 'Health Insurance', desc: 'Orientation in the Spanish healthcare system.' },
      ],
      serviceAreaText: 'Our health hub covers all of Mallorca: Palma, Manacor, Cala Millor, Inca, Alcudia, Andratx, Santa Ponsa, Porto Cristo, Campos, Soller, Pollenca and Calvia.',
      faq: [
        { q: 'Where can I find German-speaking doctors in Mallorca?', a: 'On our "Doctors Mallorca" page you will find a comprehensive overview of German-speaking GPs and specialists.' },
        { q: 'Which clinics do you recommend?', a: 'Depending on the specialty, we recommend various facilities. Visit our "Clinics Mallorca" page for details.' },
        { q: 'Do you offer home care?', a: 'Yes, Geis Care offers professional home care, senior care, and intensive care throughout Mallorca.' },
        { q: 'What do I do in a medical emergency?', a: 'Call 112 (general emergency) or 061 (medical emergency service). All emergency numbers can be found on our "Emergency Numbers Mallorca" page.' },
        { q: 'How does the healthcare system work in Mallorca?', a: 'Mallorca offers public and private care. Detailed information can be found on our "Healthcare System Mallorca" page.' },
      ],
      internalLinks: [
        { label: 'Care Mallorca' },
        { label: 'Senior Care Mallorca' },
        { label: 'Home Care Mallorca' },
        { label: 'Doctors in Mallorca' },
        { label: 'Clinics in Mallorca' },
        { label: 'Emergency Numbers Mallorca' },
        { label: 'Healthcare System Mallorca' },
      ],
      breadcrumbs: [{ label: 'Home' }, { label: 'Health Mallorca' }],
    },
    es: {
      h1: 'Salud en Mallorca – Su Centro de Salud',
      metaTitle: 'Salud en Mallorca – Su Centro de Salud para Atencion Medica | Geis Care',
      metaDescription: 'Centro de salud para Mallorca: medicos, clinicas, cuidado, atencion de emergencia, medicamentos. Su centro de salud germanoparlante.',
      metaKeywords: 'salud Mallorca, atencion medica Mallorca, asistencia sanitaria Mallorca',
      intro: 'Bienvenido a su centro de salud central para Mallorca. Aqui encontrara toda la informacion importante sobre la atencion medica en la isla: desde medicos germanoparlantes hasta clinicas y cuidado domiciliario. Geis Care es su socio para una atencion sanitaria segura y profesional en Mallorca.',
      services: [
        { title: 'Medicos y Especialistas', desc: 'Red de medicos y especialistas germanoparlantes en toda Mallorca.' },
        { title: 'Clinicas y Hospitales', desc: 'Resumen de centros medicos de primera clase.' },
        { title: 'Cuidado Domiciliario', desc: 'Enfermeria y atencion profesional en casa.' },
        { title: 'Atencion de Emergencia', desc: 'Numeros de emergencia importantes y ayuda rapida en caso de urgencia.' },
        { title: 'Medicamentos y Farmacias', desc: 'Gestion de recetas y organizacion de medicamentos.' },
        { title: 'Seguro Medico', desc: 'Orientacion en el sistema sanitario espanol.' },
      ],
      serviceAreaText: 'Nuestro centro de salud cubre toda Mallorca: Palma, Manacor, Cala Millor, Inca, Alcudia, Andratx, Santa Ponsa, Porto Cristo, Campos, Soller, Pollenca y Calvia.',
      faq: [
        { q: 'Donde puedo encontrar medicos germanoparlantes en Mallorca?', a: 'En nuestra pagina "Medicos Mallorca" encontrara un resumen completo de medicos generales y especialistas germanoparlantes.' },
        { q: 'Que clinicas recomiendan?', a: 'Segun la especialidad, recomendamos diferentes centros. Visite nuestra pagina "Clinicas Mallorca" para mas detalles.' },
        { q: 'Ofrecen cuidado domiciliario?', a: 'Si, Geis Care ofrece cuidado domiciliario profesional, atencion a mayores y cuidados intensivos en toda Mallorca.' },
        { q: 'Que hago en una emergencia medica?', a: 'Llame al 112 (emergencia general) o 061 (servicio de emergencias medicas). Todos los numeros de emergencia estan en nuestra pagina "Numeros de Emergencia Mallorca".' },
        { q: 'Como funciona el sistema sanitario en Mallorca?', a: 'Mallorca ofrece atencion publica y privada. Informacion detallada en nuestra pagina "Sistema Sanitario Mallorca".' },
      ],
      internalLinks: [
        { label: 'Cuidado Mallorca' },
        { label: 'Atencion a Mayores Mallorca' },
        { label: 'Cuidado Domiciliario Mallorca' },
        { label: 'Medicos en Mallorca' },
        { label: 'Clinicas en Mallorca' },
        { label: 'Numeros de Emergencia Mallorca' },
        { label: 'Sistema Sanitario Mallorca' },
      ],
      breadcrumbs: [{ label: 'Inicio' }, { label: 'Salud Mallorca' }],
    },
  },

  // ===== GUIDE PAGES (8 lifestyle pages) =====
  // Placeholder translations - full content to be added later
  'golfplaetze-mallorca': { en: { h1: 'Golf Courses in Mallorca' }, es: { h1: 'Campos de Golf en Mallorca' } },
  'mandelbluete-mallorca': { en: { h1: 'Almond Blossom in Mallorca' }, es: { h1: 'Almendros en Flor Mallorca' } },
  'ostern-mallorca': { en: { h1: 'Easter in Mallorca' }, es: { h1: 'Semana Santa Mallorca' } },
  'silvester-mallorca': { en: { h1: 'New Year in Mallorca' }, es: { h1: 'Nochevieja Mallorca' } },
  'sant-antoni-mallorca': { en: { h1: 'Sant Antoni in Mallorca' }, es: { h1: 'Sant Antoni Mallorca' } },
  'karneval-mallorca': { en: { h1: 'Carnival in Mallorca' }, es: { h1: 'Carnaval Mallorca' } },
  'weihnachten-mallorca': { en: { h1: 'Christmas in Mallorca' }, es: { h1: 'Navidad Mallorca' } },
  'weihnachtsmaerkte-mallorca': { en: { h1: 'Christmas Markets Mallorca' }, es: { h1: 'Mercados Navidad Mallorca' } },

  // ===== RATGEBER PAGES (7 medical guide pages) =====
  // Placeholder translations - full content to be added later
  'pflegegrad-mallorca': { en: { h1: 'Apply for Care Level in Mallorca' }, es: { h1: 'Solicitar Grado de Cuidado Mallorca' } },
  'arzttermin-mallorca': { en: { h1: 'Doctor Appointment Mallorca' }, es: { h1: 'Cita Medica Mallorca' } },
  'medikamente-mallorca': { en: { h1: 'Medication in Mallorca' }, es: { h1: 'Medicamentos Mallorca' } },
  'krankenversicherung-mallorca': { en: { h1: 'Health Insurance Mallorca' }, es: { h1: 'Seguro Medico Mallorca' } },
  'seniorenhilfe-mallorca': { en: { h1: 'Senior Help Process Mallorca' }, es: { h1: 'Proceso Ayuda Mayores Mallorca' } },
  'notfall-mallorca': { en: { h1: 'Emergency in Mallorca' }, es: { h1: 'Emergencia Mallorca' } },
  'krankenhausaufnahme-mallorca': { en: { h1: 'Hospital Admission Mallorca' }, es: { h1: 'Ingreso Hospitalario Mallorca' } },

} };

Object.entries(seoTranslationOverrides).forEach(([slug, override]) => {
  const base = seoPageTranslations[slug] || {};
  seoPageTranslations[slug] = {
    ...base,
    en: { ...(base.en || {}), ...(override.en || {}) },
    es: { ...(base.es || {}), ...(override.es || {}) },
  };
});
